// WP-0.4: minimal Account/Transaction domains over the event store (M5/M6 skeleton).
// AR-3: this class IS the write path — api/web/bridge/AI call these methods and never
// touch storage directly. Ids and timestamps come from the caller (determinism, AR-7).
// Full account CRUD lands in WP-1.1; full transaction forms in WP-1.2.
import { rebuildProjections, type Projection } from '../events/projection';
import {
  InMemoryEventStore,
  type EventSource,
  type EventStore,
  type LedgerEvent,
} from '../events/store';
import { currencyInfo, MoneyError, type CurrencyCode } from '../money/currency';
import { fromMinor, sum } from '../money/money';
import {
  firstOccurrenceOnOrAfter,
  nextOccurrence,
  RECUR_FREQUENCIES,
  type RecurSchedule,
} from './schedule';
import { amountMatchScore, parseSearchAmount, textMatchScore } from './search';
import { parseImportRow, type ImportColumnMapping, type ImportRowType } from './csvImport';

// AC-1: account types.
export const ACCOUNT_TYPES = ['bank', 'psp_wallet', 'cash', 'mobile_wallet', 'custom'] as const;
export type AccountType = (typeof ACCOUNT_TYPES)[number];

// FE-1: typed fee legs (withholding/levy is a first-class type — Pakistani bank reality).
export const FEE_TYPES = [
  'platform',
  'transfer',
  'bank',
  'fx_spread',
  'withholding',
  'other',
] as const;
export type FeeType = (typeof FEE_TYPES)[number];

export interface FeeLegRecord {
  readonly type: FeeType;
  readonly amountMinor: number;
}

// FE-3: aggregate fee query result (total fees + fees-by-type).
export interface FeeSummary {
  totalMinor: number;
  byType: Partial<Record<FeeType, number>>;
}

export interface TransactionLineRecord {
  readonly amountMinor: number;
  readonly note?: string;
  readonly category?: string; // EX-1; a real CategoryPicker/tree lands in WP-1.7
  // PJ-6 groundwork: which entity this line belongs to. `'receivable'`/`'payable'`/`'project'`/
  // `'scheduleItem'` are all validated by `recordTransaction` — the real allocation PICKER
  // (multi-way splits in the Income form's own UI) is still WP-3.5's scope.
  readonly linkType?: string;
  readonly linkId?: string;
}

// EX-2: category tree is user-defined (category -> subcategory), archivable, re-orderable.
// Expense categories nest; §16.11's income list is flat, but nothing in the model forbids
// nesting income categories too — the seed loader just doesn't create any.
export const CATEGORY_KINDS = ['expense', 'income'] as const;
export type CategoryKind = (typeof CATEGORY_KINDS)[number];

export interface CategoryRecord {
  id: string;
  name: string;
  kind: CategoryKind;
  parentId?: string;
  archived: boolean;
  displayOrder: number; // deterministic position among siblings (same kind + parentId)
  // WP-6.1 (GL-7/GL-8): "average monthly ESSENTIAL expense" needs a real essential/
  // discretionary split — WP-5.2 explicitly deferred this dimension ("no backing field
  // anywhere in this schema yet... a separate feature"). Undefined/false means
  // discretionary; only 'expense'-kind categories are meaningful here.
  essential?: boolean;
}

// ST-2 "tag manager": a flat set of user-defined labels, attached to transactions by id
// (TransactionRecord.tagIds) — unlike `category`, which stays a free-text string (a
// transaction's category is a fact captured at record time, not a live reference).
export interface TagRecord {
  id: string;
  name: string;
  archived: boolean;
}

// WP-3.1 (M1 PT-1..6): one entity for every financially relevant person/organization.
// Unlike category (WP-1.7's deliberate "frozen fact, not a live reference" design —
// TransactionRecord.category stays a free-text string forever), a party genuinely needs to be
// a live reference: PT-4's own words are "duplicate parties MUST be mergeable (all links
// re-pointed, history preserved)," which only makes sense against a real id a merge can walk
// and rewrite. `TransactionRecord.party`/`RpRecord.party`/`TpfCaseRecord.party` (the free-text
// strings from WP-1.2/M11/M10) are UNCHANGED and kept — they remain "the name as typed/spoken
// at capture time," the same historical-fact role `category` plays; a new, separate,
// OPTIONAL `partyId` is what merge actually re-points, so an old record with no `partyId`
// (everything created before this WP) is simply not part of any party's live profile, rather
// than being silently backfilled with a guess.
export const PARTY_ROLES = [
  'client',
  'vendor',
  'friend',
  'family',
  'freelancer',
  'employee',
  'business_partner',
  'creditor',
  'debtor',
  'tpf_owner',
  'custom',
] as const;
export type PartyRole = (typeof PARTY_ROLES)[number];

export interface PartyRecord {
  id: string;
  name: string;
  roles: PartyRole[]; // PT-1: multiple simultaneous roles
  aliases: string[]; // PT-3: feeds AU-4 AI matching
  phone?: string; // PT-3
  archived: boolean; // PT-5: hidden from pickers, retained in history
  provisional: boolean; // PT-6: AI-created during capture, awaiting owner confirmation
  mergedIntoId?: string; // PT-4: set once this party has been merged away — never deleted
  createdAt: string;
}

/** PT-2's profile aggregate — currency-bucketed (never summed across currencies, the same
 * "caller picks a currency, the engine never auto-converts" discipline `inTransitTotal`/
 * `queryFees`/`queryConversionLoss` already use). PT-2's own list also names notes/documents/
 * financial-timeline — these are deliberately NOT duplicated into this struct: each is served
 * by its own dedicated per-entity query instead (`GET /api/v1/notes`, the attachments service,
 * `GET /api/v1/timeline?partyId=`), the same "one query per concern" shape `quotationIds`/
 * `projectIds`/`scheduleItemIds` below don't need to repeat for record LISTS that already have
 * a perfectly good list route of their own.
 */
export interface CurrencyAmount {
  currency: CurrencyCode;
  amountMinor: number;
}

export interface PartyProfileSnapshot {
  party: PartyRecord;
  receivedTotal: CurrencyAmount[];
  paidTotal: CurrencyAmount[];
  receivableMinor: CurrencyAmount[]; // open receivables, remaining balance
  payableMinor: CurrencyAmount[]; // open payables, remaining balance
  tpfHeldMinor: CurrencyAmount[]; // owed-back on open TPF cases
  transactionIds: string[];
  rpIds: string[];
  tpfCaseIds: string[];
  quotationIds: string[]; // WP-3.2 (PT-2's own "quotations" aggregate line)
  projectIds: string[]; // WP-3.3 (PT-2's own "active/completed projects" aggregate line)
  scheduleItemIds: string[]; // WP-3.4 (PT-2's own "schedules" aggregate line)
}

// WP-3.2 (M2 QT-1..6, S-21/22): a quotation is a single-price proposal to a party — QT-2's own
// field list, nothing more (no line items — not part of this WP's spec). QT-1's states are
// locked by the spec; the exact TRANSITION table is not spelled out anywhere in REQUIREMENTS.md
// beyond S-21's own flow arrow ("Draft→Sent→Accepted→Rejected/Expired→Converted") — this is
// read as a strict forward flow (no draft->rejected skip, no reverting sent->draft, every
// non-'converted' terminal state is truly terminal) and enforced exactly that way below; a
// genuine interpretation, surfaced rather than silently assumed.
export const QUOTATION_STATES = [
  'draft',
  'sent',
  'accepted',
  'rejected',
  'expired',
  'converted',
] as const;
export type QuotationState = (typeof QUOTATION_STATES)[number];

// QT-3 (LOCKED): "a quotation of $100-200 remains visible verbatim forever" — fixed vs range
// is a real distinction the record carries, not collapsed into a single amount. `minMinor`/
// `maxMinor` names match `computeValueLenses`'s own `quotedRangeMinor: {min, max}` shape
// (packages/engine/src/calc/lenses.ts) — WP-3.3's future wiring point for this WP's data.
export type QuotationPricing =
  { kind: 'fixed'; amountMinor: number } | { kind: 'range'; minMinor: number; maxMinor: number };

/** QT-5 "revisions... kept as versions rather than overwrites" — a snapshot of the PRIOR
 * pricing, taken at the moment it's superseded by a new one (draft/sent only; QT-3's "verbatim
 * forever" principle extends to closing off revision once a quotation is accepted). */
export interface QuotationVersionSnapshot {
  pricing: QuotationPricing;
  supersededAt: string;
}

export const QUOTATION_CONVERT_TARGETS = ['project', 'contract', 'receivable'] as const;
export type QuotationConvertTarget = (typeof QUOTATION_CONVERT_TARGETS)[number];

export interface QuotationRecord {
  id: string;
  party: string; // frozen name, same WP-3.1 precedent as TransactionRecord.party
  partyId?: string;
  title: string;
  description?: string; // QT-2 "work description"
  pricing: QuotationPricing;
  currency: CurrencyCode;
  expiryDate?: string;
  notes?: string;
  paymentTerms?: string;
  expectedStartDate?: string;
  status: QuotationState;
  versions: QuotationVersionSnapshot[]; // QT-5, oldest first
  /** QT-4 "zero re-entry" conversion. `convertedToType: 'project' | 'contract'` records the
   * INTENT only — P3.3's Project entity doesn't exist yet (WP-3.3 is the very next WP), so
   * nothing is fabricated; WP-3.3's own project-creation flow reads this quotation's fields
   * for its "from-quotation prefill" once it exists (REQUIREMENTS.md's own project form spec
   * names this exact flow). `convertedToType: 'receivable'` is fully real today — see
   * `convertQuotation`. */
  convertedToType?: QuotationConvertTarget;
  convertedToId?: string; // only ever set for 'receivable' today
  createdAt: string;
}

// WP-3.3 (M3 PJ-1..7, S-23/24): a project is a real engagement — quote -> agreement ->
// adjustments -> payments -> net. MI-3's "four lenses" (Agreed Value -> Final Project Value ->
// Gross Received -> Net Usable) are the whole point of this entity: `agreedMinor` (PJ-3
// "original agreement immutable" — no method here ever mutates it once set) plus
// `originalQuotedRange` (preserved verbatim if the project came from a range quotation, QT-3's
// own principle carried forward) are lens 1; `adjustments` sum onto `agreedMinor` for lens 2;
// linked transactions feed lenses 3/4 via `queryProjectValueLenses`.
export const PROJECT_EARNING_MODELS = [
  'fixed',
  'hourly',
  'milestone',
  'recurring',
  'range_estimate',
] as const;
export type ProjectEarningModel = (typeof PROJECT_EARNING_MODELS)[number];

export const PROJECT_STATUSES = ['active', 'paused', 'completed', 'cancelled', 'archived'] as const;
export type ProjectStatus = (typeof PROJECT_STATUSES)[number];

// PJ-3's own locked list. `hourly_accrual` is never created directly by the owner — only
// `logProjectHours` creates one, one per hours entry (PJ-5: "manual hours entries... generate
// value adjustments/accruals").
export const PROJECT_ADJUSTMENT_TYPES = [
  'extra_work',
  'discount',
  'compensation',
  'scope_reduction',
  'hourly_accrual',
  'correction',
] as const;
export type ProjectAdjustmentType = (typeof PROJECT_ADJUSTMENT_TYPES)[number];

export interface ProjectAdjustment {
  id: string;
  type: ProjectAdjustmentType;
  amountMinor: number; // signed — a discount is caller-supplied negative, not auto-negated
  date: string;
  note?: string; // S-24's own form spec: "required for discount/compensation"
}

export interface ProjectHoursEntry {
  id: string;
  date: string;
  hours: number;
  rateMinor: number; // per-hour rate, minor units
  note?: string;
  adjustmentId: string; // the hourly_accrual adjustment this entry generated
}

export interface ProjectRecord {
  id: string;
  party: string; // frozen name, same WP-3.1 precedent
  partyId?: string;
  title: string;
  serviceCategory?: string;
  productCategory?: string;
  currency: CurrencyCode;
  status: ProjectStatus;
  earningModel: ProjectEarningModel;
  originalQuotedRange?: { minMinor: number; maxMinor: number }; // QT-3: verbatim, if from a range quotation
  agreedMinor: number; // PJ-3 "original agreement" — immutable once created
  adjustments: ProjectAdjustment[]; // PJ-3, oldest first
  hoursEntries: ProjectHoursEntry[]; // PJ-5, hourly projects only, oldest first
  startDate?: string;
  endDate?: string;
  notes?: string;
  fromQuotationId?: string; // QT-4 zero-re-entry backlink
  createdAt: string;
}

/** MI-3's four lenses, computed fresh from the project's own adjustments + whatever
 * transactions link to it — never stored, always reconstructable (§14-3/PJ-3). */
export interface ProjectValueLenses {
  originalQuotedRange?: { minMinor: number; maxMinor: number };
  agreedMinor: number;
  finalValueMinor: number; // agreedMinor + Σ adjustments
  grossReceivedMinor: number; // Σ linked, non-reversed income transactions
  netUsableMinor: number; // grossReceived − Σ fee legs on those same transactions
}

// WP-3.6 (M14 PP-1..3, MI-3): profitability. PP-2 — "profit differs by lens; make it
// explicit" — so revenue is always computed under a caller-chosen basis, never a single
// hidden default; only three of the four MI-3 lenses are valid profit bases (PP-2's own
// list excludes Agreed Value — a pre-adjustment estimate, not a real revenue figure).
export const REVENUE_BASES = ['final', 'gross', 'net'] as const;
export type RevenueBasis = (typeof REVENUE_BASES)[number];

// WP-5.2 (M17 MI-1/2, S-25): the Money Intelligence dimension engine's own analyzable slices.
// MI-1/MI-2 name more dimensions than modeled here (payment channel, currency, personal/
// business, recurring/one-time, essential/discretionary) — "currency" is deliberately excluded
// since RE-1's own `reportCurrency` family already answers "income by currency" (no point
// building it twice); the personal/business, recurring/one-time, and essential/discretionary
// splits have NO backing field anywhere in this schema yet (they'd need a new categorization
// scheme, a separate feature) — a documented, surfaced scope trim, not a silent omission.
export const MI_INCOME_DIMENSIONS = [
  'client',
  'project',
  'serviceCategory',
  'productCategory',
  'account',
  'earningModel',
] as const;
export type MiIncomeDimension = (typeof MI_INCOME_DIMENSIONS)[number];
export const MI_EXPENSE_DIMENSIONS = ['category', 'account', 'project'] as const;
export type MiExpenseDimension = (typeof MI_EXPENSE_DIMENSIONS)[number];
export type MiDirection = 'income' | 'expense';
export type MiDimension = MiIncomeDimension | MiExpenseDimension;

export interface MiRow {
  key: string;
  label: string;
  amountMinor: number;
}

/** MI-4's own "top-N ... trend lines" needs actual dated cash flow, not the 4-lens ALL-TIME
 * project properties `queryProfitabilityRollups` computes (an "agreed value" doesn't have a
 * month) — so this is a period-scoped, transaction-level sum-by-dimension-key, always sorted
 * richest-first (`rows[0..N]` IS "top-N", no separate mechanism needed). Rows always sum to
 * `totalMinor` — an unresolved dimension value (no project link, unknown account) lands in a
 * named fallback bucket, never silently dropped (the SAME "Uncategorized" discipline
 * `reportExpenses` already uses). */
export interface MiBreakdown {
  direction: MiDirection;
  dimension: MiDimension;
  currency: CurrencyCode;
  period: { from: string; to: string };
  rows: MiRow[];
  totalMinor: number;
  tagId?: string; // WP-C1.8 (SR-2): echoed back only when the query was tag-scoped
}

// WP-5.3 (M18 MF-1/2, S-26): Flow Explorer's fixed level order [LOCKED §2.20] — "Clients →
// Service Categories → Payment Channels → Fees → Accounts → Transfers → Expense Categories /
// Savings / People Paid / Remaining." None of these last four is a literal per-rupee trace
// (untraceable once money is fungible in an account) — each is a documented mapping onto the
// existing schema: "Payment Channel" = the receiving account's `type` (AC-1), the nearest
// existing field to a literal channel; "Service Category" = the SAME project-linked
// resolution `miIncomeDimensionKey('serviceCategory')` already uses (reused verbatim, not
// re-derived); "Savings" = a transfer whose destination account is non-spendable (AC-4) — no
// goals/reserves table exists yet (WP-6.1 is still pending); "Transfers" = a transfer to a
// SPENDABLE destination (plain internal reallocation); "People Paid" = an expense whose
// `party` (TX-3's free-text counterparty) is set, mirroring the income side's own client
// dimension; "Expense Categories" = every other expense, via `miExpenseDimensionKey`
// verbatim; "Remaining" = an account's period net-income-in minus what left it this period,
// clamped at zero — spending funded by a PRIOR period's balance isn't shown as negative flow,
// since this is a period-scoped flow diagram, not a full balance-sheet reconciliation. Every
// node/link value is a literal sum of real records for this currency+period — nothing
// invented (AD-1) — so the diagram's own totals reconcile to the ledger exactly (§14-5).
export const FLOW_LEVELS = [
  'client',
  'serviceCategory',
  'paymentChannel',
  'fees',
  'account',
  'transfersOrSavings',
  'terminal',
] as const;
export type FlowLevel = (typeof FLOW_LEVELS)[number];

export interface FlowNode {
  id: string;
  level: number; // index into FLOW_LEVELS
  label: string;
  valueMinor: number;
}

export interface FlowLink {
  source: string; // FlowNode.id
  target: string; // FlowNode.id
  valueMinor: number;
}

// MF-2 ("every important total explainable by its source records"): the exact records that
// summed to a node's value, keyed by FlowNode.id — a transaction and a transfer are distinct
// record kinds, so the drill panel can tell them apart without re-querying.
export type FlowRecordRef =
  { kind: 'transaction'; record: TransactionRecord } | { kind: 'transfer'; record: TransferRecord };

export interface MoneyFlow {
  currency: CurrencyCode;
  period: { from: string; to: string };
  nodes: FlowNode[];
  links: FlowLink[];
  recordsByNode: Record<string, FlowRecordRef[]>;
}

export interface ProjectCostEntry {
  transactionId: string;
  amountMinor: number;
  at: string;
  category?: string;
}

/** PP-1: revenue, direct costs, profit, margin — computed fresh, never stored (same
 * "always reconstructable" discipline `queryProjectValueLenses` already uses). */
export interface ProjectProfitability {
  basis: RevenueBasis;
  revenueMinor: number;
  directCostsMinor: number;
  profitMinor: number;
  marginPercent: number | null; // null when revenueMinor <= 0 — never a divide-by-zero NaN
  costs: ProjectCostEntry[];
}

export interface ProfitabilityRollupRow {
  key: string;
  label: string;
  revenueMinor: number;
  directCostsMinor: number;
  profitMinor: number;
  marginPercent: number | null;
  projectCount: number;
}

/** PP-3: "which clients/services are actually profitable" — one currency at a time, the
 * same discipline every other multi-entity total in this file uses. */
export interface ProfitabilityRollups {
  currency: CurrencyCode;
  basis: RevenueBasis;
  byClient: ProfitabilityRollupRow[];
  byServiceCategory: ProfitabilityRollupRow[];
  byProductCategory: ProfitabilityRollupRow[];
}

// WP-3.7 (M19 TL-1/2, S-29): a read-only rendering layer over the SAME append-only event
// log every entity already writes to (AR-4) — no new state. TL-1's own example chain
// ("quotation accepted -> payment expected -> payment received -> fee -> transfer ->
// conversion -> bank arrival -> expense") is entirely financial, so the feed scopes to the
// Work/Money-layer entity types below — config-ish log entries (account/category/tag/fxRate/
// importSource/recurringTemplate edits) and reminders are deliberately excluded, a genuine
// "financial timeline" reading of M19's own section title, surfaced here rather than dumping
// the raw log. The engine never formats money for display (FX-5/consistent with every other
// query in this file) — `amountMinor`/`currency`, when present, are the caller's to render.
/** WP-H2 (TL-1/2, LG-3): every entity type the append-only log actually writes. It carried 18
 * from the start, but the Timeline surfaced only the first seven — so eleven kinds of change were
 * invisible in the one view whose whole job is "what changed, when". */
export const TIMELINE_ENTITY_TYPES = [
  // Substantive: the things the owner thinks of as their records.
  'transaction',
  'transfer',
  'rp',
  'tpfCase',
  'quotation',
  'project',
  'scheduleItem',
  'party',
  'goal',
  'reserve',
  'suggestion',
  // Config-ish: real history, but rename/rate/mapping noise that would drown the feed above.
  'account',
  'category',
  'tag',
  'fxRate',
  'importSource',
  'recurringTemplate',
  'reminder',
] as const;
export type TimelineEntityType = (typeof TIMELINE_ENTITY_TYPES)[number];

/** WP-H2: shown BEHIND A FILTER rather than dropped. These are genuine, auditable changes — a
 * renamed category or a corrected FX rate can change what a report says — but they are not what
 * an owner scanning "what happened" is looking for, and including them by default would bury the
 * entries that are. Reachable at any time via an explicit `entityTypes` filter. */
export const TIMELINE_CONFIG_ENTITY_TYPES = [
  'account',
  'category',
  'tag',
  'fxRate',
  'importSource',
  'recurringTemplate',
  'reminder',
] as const satisfies readonly TimelineEntityType[];

/** What `queryTimeline()` returns when the caller names no types. */
export const TIMELINE_DEFAULT_ENTITY_TYPES = TIMELINE_ENTITY_TYPES.filter(
  (t) => !(TIMELINE_CONFIG_ENTITY_TYPES as readonly string[]).includes(t),
);

export interface TimelineEntry {
  id: string;
  seq: number;
  at: string;
  entityType: TimelineEntityType;
  entityId: string;
  sentence: string;
  amountMinor?: number;
  currency?: CurrencyCode;
  /** WP-H1 (LG-3, §18.1): WHAT made this change — manual · ai · whatsapp · import · recurring ·
   * system. LG-3 requires the source in the audit trail and §18.1 defines its iconography, but
   * this type omitted it, which made "who touched this invoice?" unanswerable from the timeline
   * at all. The event log has carried it since WP-0.3; only this projection dropped it. */
  source: EventSource;
}

// WP-3.8 (M20 RE-1/3/4, S-27/28): report families. RE-1's own seven families [LOCKED §2.22];
// RE-2 (Position-over-time via Snapshots, aging, fee-%-of-gross) needs P5 entities that don't
// exist yet (WP-5.1/5.4) and is deliberately excluded here, same "groundwork now, consumer
// later" precedent this codebase always follows rather than faking it. RE-3/CX-6/D-8 (LOCKED):
// every time-scoped report offers Calendar (Jan-Dec) <-> PK Fiscal (Jul-Jun) — `queryReport`
// is the FIRST engine query in this whole codebase to consume a year basis at all (WP-3.4's
// own CHANGELOG flagged this exact gap: "nothing in the engine consumes yearBasis yet").
// The engine returns RAW numbers only (FX-5) — same "caller builds the display/export shape"
// split `exportTable.ts`'s own header comment already documents for `TabularData`; the web
// layer maps `ReportResult.rows` into columns/labels per family.
// WP-5.4 (SC-7/RE-2 "aging", FE-3/RE-2 "fee-%-of-gross") adds the last two RE-2 families —
// both PURE and DB-free like every family above, so they join this SAME dispatcher rather
// than needing a bespoke page/route the way Snapshots/MI/Flow Explorer's Postgres-backed
// data did; the pre-existing dynamic Reports gallery (S-27) picks them up with zero web
// changes beyond a column mapping (`REPORT_COLUMNS`/`REPORT_PRIMARY_COLUMN`).
export const REPORT_FAMILIES = [
  'income',
  'expenses',
  'fees',
  'cashflow',
  'outstanding',
  'currency',
  'thirdParty',
  'aging',
  'feesByChannel',
] as const;
export type ReportFamily = (typeof REPORT_FAMILIES)[number];

export const YEAR_BASES = ['calendar', 'fiscal'] as const;
export type YearBasis = (typeof YEAR_BASES)[number];

export type ReportRow = Record<string, string | number>;

export interface ReportResult {
  family: ReportFamily;
  currency: CurrencyCode;
  yearBasis: YearBasis;
  period: { from: string; to: string };
  rows: ReportRow[];
}

/** The private per-event describers' own return shape — deliberately widened to allow an
 * explicit `undefined` VALUE (unlike the public `TimelineEntry`, under
 * `exactOptionalPropertyTypes` an optional key still can't be assigned `undefined` directly);
 * `queryTimeline` strips real `undefined`s via a conditional spread before it ever reaches
 * the public, narrow `TimelineEntry` shape. */
interface TimelineDescription {
  sentence: string;
  amountMinor?: number | undefined;
  currency?: CurrencyCode | undefined;
}

// WP-3.4 (M4 SC-1..8, S-14): a schedule item is one expected payment, within a project or
// standalone (the glossary's "project/contract" is never a hard requirement — same "a link is
// a fact, never mandatory" discipline every other Work-layer entity uses). SC-2's own
// Overdue/Due are TIME-relative facts and are never written to `status` by any event (AR-7:
// the engine never reads the clock) — only money/lifecycle-driven transitions are stored here
// (upcoming -> partially_paid -> paid, or an explicit cancel); `deriveScheduleItemStatus`
// computes the full SC-2 enum (adding due/overdue) at query time from a caller-supplied
// `asOf`, mirroring `queryOverdueRps`'s own "asOf is always a parameter" precedent.
export const CONFIDENCE_LEVELS = ['confirmed', 'likely', 'tentative'] as const;
export type ConfidenceLevel = (typeof CONFIDENCE_LEVELS)[number];

// SC-2 (LOCKED)'s full list. Only the first four are ever written directly by an event —
// 'due'/'overdue' exist purely as a query-time view (see `deriveScheduleItemStatus`).
export const SCHEDULE_ITEM_STATUSES = [
  'upcoming',
  'due',
  'partially_paid',
  'paid',
  'overdue',
  'cancelled',
] as const;
export type ScheduleItemStatus = (typeof SCHEDULE_ITEM_STATUSES)[number];
export type ScheduleItemLifecycleStatus = Exclude<ScheduleItemStatus, 'due' | 'overdue'>;

export interface ScheduleItemReceipt {
  amountMinor: number;
  transactionId: string;
  at: string;
}

// SC-6: rescheduling preserves the ORIGINAL expected date in history — every reschedule
// (including the first move off "undated") appends one of these, oldest first.
export interface ScheduleReschedule {
  previousDate?: string;
  newDate?: string;
  at: string;
}

export interface ScheduleItemRecord {
  id: string;
  party: string; // frozen name, same WP-3.1 precedent
  partyId?: string;
  projectId?: string; // Glossary: "one expected payment within a project/contract"
  currency: CurrencyCode;
  expectedAmountMinor: number;
  expectedDate?: string; // SC-1 "optional" — undated forms its own SC-4 rollup bucket
  milestone?: string; // SC-1 "related milestone" / S-14's own "milestone note"
  confidence: ConfidenceLevel;
  status: ScheduleItemLifecycleStatus;
  receipts: ScheduleItemReceipt[]; // SC-1 "actual amount(s) received, actual date(s)", oldest first
  receivedMinor: number;
  rescheduleHistory: ScheduleReschedule[]; // SC-6, oldest first
  cancelReason?: string; // S-14's own form spec: "Cancel requires reason"
  createdAt: string;
}

/** S-14 list rows / SC-2: the full six-value status plus the outstanding balance, computed
 * fresh at query time — never stored (see the header comment above). */
export interface ScheduleItemView extends ScheduleItemRecord {
  effectiveStatus: ScheduleItemStatus;
  remainingMinor: number;
}

export interface ConfidenceSplitAmount {
  confirmedMinor: number;
  likelyMinor: number;
  tentativeMinor: number;
}

/** SC-4: next 7 days / next 30 days / this month / this year / overdue / undated, each split
 * by confidence (SC-3) — §14-6 "Tentative never inflates Confirmed" is enforced structurally
 * by keeping the three confidence totals separate in every bucket, never summed into one
 * number. The date-window buckets deliberately OVERLAP (an item due in 3 days counts toward
 * next7Days, next30Days, thisMonth, AND thisYear simultaneously) — these are separate
 * time-horizon lenses on the same data, not a partition; only overdue/undated are mutually
 * exclusive with the forward-looking buckets. */
export interface ScheduleRollups {
  currency: CurrencyCode;
  next7Days: ConfidenceSplitAmount;
  next30Days: ConfidenceSplitAmount;
  thisMonth: ConfidenceSplitAmount;
  thisYear: ConfidenceSplitAmount;
  overdue: ConfidenceSplitAmount;
  undated: ConfidenceSplitAmount;
}

export interface AgingBucket {
  amountMinor: number;
  count: number;
}

/** SC-7: 1-30/31-60/61-90/90+ days overdue, unifying schedule items AND standalone
 * receivables (D-5: the two stay separate entities, unified only at this rollup layer). */
export interface AgingBuckets {
  currency: CurrencyCode;
  days1to30: AgingBucket;
  days31to60: AgingBucket;
  days61to90: AgingBucket;
  days90plus: AgingBucket;
}

export interface AccountRecord {
  id: string;
  name: string;
  type: AccountType; // AC-1
  currency: CurrencyCode;
  openingMinor: number;
  openingAsOf: string;
  spendable: boolean; // AC-4: feeds Spendable Now (DB-1)
  archived: boolean; // S-12: archive-only-at-zero-balance
  displayOrder: number; // AC-2: deterministic — creation order
  platformGroup?: string;
  icon?: string;
  color?: string;
  balanceMinor: number;
}

export interface TransactionRecord {
  id: string;
  type: 'expense' | 'income';
  accountId: string;
  currency: CurrencyCode;
  amountMinor: number;
  at: string; // TX-3 datetime — the create event's own instant (AR-7: caller-supplied)
  lines?: TransactionLineRecord[];
  feeLegs?: FeeLegRecord[];
  category?: string; // EX-1; full category tree/picker lands in WP-1.7
  party?: string; // TX-3 "optional counterparty" — the name as typed, a frozen historical fact
  partyId?: string; // WP-3.1: the live link a Party profile/merge actually reads and re-points
  // WP-3.3 (PJ-6/PJ-7): the simple direct link — one whole payment to one project. A payment
  // SPLIT across multiple projects goes through `lines[].linkType === 'project'` instead (the
  // same mechanism receivables/payables already use); PJ-7's "unallocated then relink" is why
  // this is a `correct`-amendable field, not just a create-time value.
  projectId?: string;
  // WP-3.4 (SC-1/SC-5): the simple direct link — one whole payment against one schedule item,
  // mirroring `projectId` exactly. A payment SPLIT across multiple schedule items (or mixed
  // with a project/RP) goes through `lines[].linkType === 'scheduleItem'` instead; the full
  // "against" picker experience is WP-3.5's own scope (DEV_PLAN's own "receive-payment
  // allocation" line) — this WP only lays the write-path groundwork.
  scheduleItemId?: string;
  note?: string;
  tagIds?: string[]; // ST-2 TagInput (§18.5 Detailed Expense "tags")
  reversed: boolean;
  source: EventSource;
  // OB-3: entering old history via CSV import suppresses notifications/advisor reactions —
  // that consumer (WP-1.17) doesn't exist yet, so this field just records the fact faithfully
  // today, the same "groundwork ahead of its consumer" precedent as TransactionLineRecord's
  // linkType/linkId (added for Project/ScheduleItem years before those entities exist).
  backfill?: boolean;
  // TX-1: Adjustment is a LOCKED flow type, "reconciliation-only, always reason-tagged" — a
  // marker on an ordinary income/expense record (direction from the sign the owner entered)
  // rather than a parallel entity, so it reuses every existing balance/invariant/search path;
  // `queryDashboard` excludes it from real income/expense (see recordAdjustment, WP-1.14).
  adjustment?: boolean;
  // TX-6: the id of an earlier same-account/same-type/same-amount transaction within ±1 day,
  // computed and stamped at entry time (deterministic — depends only on prior events). A soft
  // flag: never blocks recording (WP-1.16); surfaced in Data Health and as a UI badge.
  duplicateOf?: string;
}

// M9 (TR-1/TR-2): a transfer is a paired out/in between owned accounts, modeled as its
// own entity — never a TransactionRecord — so it structurally can never leak into
// income/expense totals (TR-1 "never income or expense"). While in_transit, the sent
// amount is deducted from the source but NOT yet credited to the destination — it lives
// in this record only (TR-2's own dashboard "in-transit" line), never double-counted.
export interface TransferRecord {
  id: string;
  fromAccountId: string;
  toAccountId: string;
  sentMinor: number; // in the source account's currency
  sentCurrency: CurrencyCode;
  feeLegs?: FeeLegRecord[]; // in the source account's currency, deducted from sent
  status: 'in_transit' | 'settled';
  initiatedAt: string;
  note?: string;
  receivedMinor?: number; // in the destination account's currency; set on settle
  settledAt?: string;
  // FX-1: cross-currency only — the indicative estimate captured AT INITIATION from
  // whatever rate was cached then (never recomputed later — FX-2), in the destination
  // account's currency.
  estimatedReceivedMinor?: number;
  // FX-4: (indicative estimate − actual received), set on settle when an estimate exists.
  // Positive = lost value vs the estimate; negative = did better than the estimate.
  conversionLossMinor?: number;
}

// M7 (FX-3): one indicative rate per currency pair, "from 1 unit of `from` to `to`".
// A rate is not money (FX-5's integer-minor-units rule governs stored amounts, not
// ratios) — ordinary floats are fine here; any amount computed FROM a rate is rounded to
// an integer minor unit before it's ever stored.
export interface FxRateRecord {
  from: CurrencyCode;
  to: CurrencyCode;
  rate: number;
  source: 'manual' | 'fetched';
  asOf: string;
}

// M11 (RP-1..4): a receivable and a payable are the same shape — an amount owed between
// the owner and a party, in one direction or the other — so they're one entity distinguished
// by `kind`, rather than two near-duplicate types.
export const RP_KINDS = ['receivable', 'payable'] as const;
export type RpKind = (typeof RP_KINDS)[number];

// RP-2: "each settlement links to the real transaction that paid it."
export interface RpSettlementRecord {
  amountMinor: number;
  transactionId: string;
  at: string;
}

export interface RpRecord {
  id: string;
  kind: RpKind;
  party: string; // the name as typed — a frozen historical fact (same as TransactionRecord.party)
  partyId?: string; // WP-3.1: the live link
  amountMinor: number;
  currency: CurrencyCode;
  reason: string; // RP-1 locked field
  createdAt: string;
  dueDate?: string;
  notes?: string;
  settlements: RpSettlementRecord[];
  settledMinor: number;
  status: 'open' | 'settled' | 'closed'; // RP-3 "closed" = written off (receivable) / waived (payable)
  closedReason?: string;
  closedAt?: string;
}

// M10 (TP-1..6, FE-4): third-party funds — money passing through the owner's accounts that
// belongs to someone else. Modeled as its own entity, never a TransactionRecord, so it
// structurally can never leak into income/expense totals (TP-2) — the same reasoning as
// WP-1.5's TransferRecord. Receive/return directly adjust the touched account's balance
// (real cash truly moved, so the ledger must match the bank) via `accountAmountMinor`, kept
// deliberately separate from `caseAmountMinor` (how much of the DEBT this movement covers,
// in the case's own currency) — the same "declared, not derived" split RP-2 settlements use,
// since a return can be paid from a different account in a different currency (A.3: a USD
// case settled with a PKR transfer) with no FX math the engine needs to perform.
export const TPF_FEE_BEARERS = ['owner', 'tpf_owner', 'split'] as const;
export type TpfFeeBearer = (typeof TPF_FEE_BEARERS)[number];

export interface TpfFeeRecord {
  amountMinor: number; // in the case's currency
  bearer: TpfFeeBearer;
}

export type TpfMovementKind = 'receive' | 'return' | 'convert';

export interface TpfMovementRecord {
  kind: TpfMovementKind;
  caseAmountMinor: number;
  accountId?: string; // absent for 'convert' — no cash moves, nothing to reclassify
  accountAmountMinor?: number;
  fee?: TpfFeeRecord;
  reason?: string; // TP-5: required for 'convert' ("explicit action")
  note?: string;
  at: string;
}

export interface TpfCaseRecord {
  id: string;
  party: string; // the name as typed — a frozen historical fact
  partyId?: string; // WP-3.1: the live link
  currency: CurrencyCode;
  movements: TpfMovementRecord[];
  receivedMinor: number;
  chargedFeesMinor: number; // fees NOT borne by the owner — these reduce owed-back (TP-3)
  returnedMinor: number;
  convertedMinor: number;
  owedBackMinor: number;
  status: 'open' | 'closed';
  closedAt?: string;
}

// M13 (RC-1..4): a recurring template posts the same expense/income/transfer/payable on a
// schedule. Occurrences are never pre-generated/stored — `nextRunAt` is the only computed
// pointer, advanced one step per post/skip — so RC-3 "edit-future-occurrences" is just an
// ordinary field edit: every future computation reads the template's current fields, and
// every already-posted entity is already its own independent record, untouched by the edit.
export const RECUR_KINDS = ['expense', 'income', 'transfer', 'payable'] as const;
export type RecurKind = (typeof RECUR_KINDS)[number];

// RC-2: variable-amount bills (electricity) must remind-to-confirm, never auto-post, since
// auto-posting a guessed amount corrupts data.
export const RECUR_BEHAVIORS = ['auto_post', 'confirm'] as const;
export type RecurBehavior = (typeof RECUR_BEHAVIORS)[number];

// §18.5 "base form embedded" — the same fields each underlying write API already takes,
// minus the pieces that only exist at post time (id, the actual amount for variable bills).
export type RecurBase =
  | { kind: 'expense'; accountId: string; amountMinor?: number; category?: string; note?: string }
  | { kind: 'income'; accountId: string; amountMinor?: number; category?: string; note?: string }
  | {
      kind: 'transfer';
      fromAccountId: string;
      toAccountId: string;
      amountMinor?: number;
      note?: string;
    }
  | {
      kind: 'payable';
      party: string;
      amountMinor?: number;
      currency: CurrencyCode;
      reason: string;
    };

export interface RecurringTemplateRecord {
  id: string;
  name: string;
  base: RecurBase;
  schedule: RecurSchedule;
  behavior: RecurBehavior;
  status: 'active' | 'paused';
  nextRunAt: string;
  lastAmountMinor?: number; // RC-2 "last amount prefilled" for variable bills
  createdAt: string;
}

// WP-C1.6 (RM-2/3/4): an ad-hoc reminder is either one-off (no `schedule` — fires once, then
// `status: 'done'`) or recurring, embedding the SAME `RecurSchedule` the M13 recurring-template
// engine uses (RM-2's own "reuses the RC engine's schedule semantics"). `linkType`/`linkId`
// follows the WP-1.3 transaction-line groundwork precedent — a free-string pointer at another
// entity (only `rp` is a real link target today; Project/ScheduleItem links are P3 groundwork,
// unvalidated the same way TX-4's own links were before Parties/Schedules existed).
export interface ReminderRecord {
  id: string;
  text: string;
  channel: 'whatsapp';
  nextRunAt: string;
  schedule?: RecurSchedule;
  status: 'active' | 'cancelled' | 'done';
  snoozed: boolean; // RM-3 "remind me again in 1 hour" — display-only, cleared once it next fires
  linkType?: string;
  linkId?: string;
  lastFiredAt?: string;
  createdAt: string;
}

// M16 (DB-1..5): the dashboard is pure aggregation over entities that already exist — no
// new state. Every term is scoped to one currency (the same "caller picks a currency, the
// engine never auto-converts" discipline `inTransitTotal`/`queryHoldingForOthers`/
// `queryConversionLoss` already use) rather than attempting indicative-rate conversion
// across accounts of different currencies — that's a Settings "base currency" concern for
// a later WP, not this one.
export interface DashboardSnapshot {
  currency: CurrencyCode;
  // DB-1 "what do I have now?"
  availableMinor: number; // spendable-flagged, non-archived accounts
  inTransitMinor: number;
  holdingForOthersMinor: number;
  reservedMinor: number; // WP-6.1: Σ active reserves (GL-4), currency-scoped
  spendableNowMinor: number; // available - reserved - holding
  netPositionMinor: number; // every non-archived account + in-transit - holding
  strictViolation: StrictReserveViolation; // WP-6.1 (GL-2 LOCKED): "force advisor warnings on violation"
  // DB-2/DB-3 "what is coming / what do I owe" (next 30 days)
  upcomingRecurringInflowMinor: number;
  upcomingRecurringBillsMinor: number;
  payableTotalMinor: number;
  payableDueSoonMinor: number; // due within 7 days of asOf, or overdue
  // DB-4 "what am I owed?"
  receivableTotalMinor: number;
  receivableOverdueMinor: number;
  // DB-5 "what happened recently?" (the given period)
  period: { from: string; to: string };
  grossIncomeMinor: number;
  expensesMinor: number;
  feesMinor: number;
  netIncomeMinor: number; // grossIncome - fees
  netCashFlowMinor: number; // grossIncome - expenses - fees
}

/** AC-5: "a consolidated cross-currency base-value figure." One row per currency actually in
 * use (non-archived accounts only), each carrying its OWN `netPositionMinor` (the SAME figure
 * `queryDashboard` already computes — never a parallel aggregation) converted to
 * `baseCurrency` via the offline FX-3 rate cache. A currency with no cached rate against
 * `baseCurrency` gets `convertedMinor: null` and is excluded from `totalMinor` — never
 * guessed, and never blocks the other currencies from still consolidating. */
export interface ConsolidatedNetWorth {
  baseCurrency: CurrencyCode;
  totalMinor: number;
  perCurrency: {
    currency: CurrencyCode;
    netPositionMinor: number;
    convertedMinor: number | null;
    rate: number | null;
  }[];
}

// WP-1.12 (SR-1, S-37 `/search`): one flat, ranked result list over every searchable entity
// that exists in P1 — transactions, transfers, receivables/payables, TPF cases, accounts,
// recurring templates. Parties/Projects/Quotations/Notes (SR-1's other named categories)
// don't exist until later phases (P3's M1/M2/M3, P1's own WP-1.18 notes) — search covers
// exactly what's real today; callers group by `kind` for display.
export const SEARCH_RESULT_KINDS = [
  'transaction',
  'transfer',
  'rp',
  'tpf',
  'account',
  'recurring_template',
] as const;
export type SearchResultKind = (typeof SEARCH_RESULT_KINDS)[number];

export interface SearchResult {
  kind: SearchResultKind;
  id: string;
  title: string;
  subtitle?: string;
  amountMinor?: number;
  currency?: CurrencyCode;
  at: string; // sortable instant (createdAt/initiatedAt/at/nextRunAt per kind)
  score: number; // 0..1, higher = better match; results are returned sorted desc
}

// WP-1.13 (OB-2, S-38 `/import`): "column mapping ... saved per source name" — a source is
// just a named preset of which CSV column maps to which transaction field. The target
// account is chosen per-import, not tied to the source, since the same bank export format
// could plausibly be re-imported into a different account.
export interface ImportSourceRecord {
  id: string;
  name: string;
  mapping: ImportColumnMapping;
  createdAt: string;
}

// TX-6: "same account, same amount, ±1 day → flagged (not blocked)". Preview is a pure read
// (no engine state mutated) — the owner decides which rows to actually commit.
export type ImportPreviewRow =
  | { rowIndex: number; ok: false; error: string }
  | {
      rowIndex: number;
      ok: true;
      type: ImportRowType;
      amountMinor: number;
      at: string;
      category?: string;
      party?: string;
      note?: string;
      duplicateOfTransactionId?: string;
    };

// WP-1.14 (OB-4, S-13 `/accounts/:id/reconcile`): candidate finders — a "guided hunt" over
// likely drift causes, never auto-fixed. Each kind is deliberately bounded so the list stays
// a small, reviewable set rather than flooding with noise (see `queryReconciliation`).
export const RECONCILE_CANDIDATE_KINDS = [
  'uncategorized',
  'duplicate',
  'stale_in_transit',
  'day_gap',
] as const;
export type ReconcileCandidateKind = (typeof RECONCILE_CANDIDATE_KINDS)[number];

export type ReconcileCandidate =
  | { kind: 'uncategorized'; transactionId: string; amountMinor: number; at: string }
  | {
      kind: 'duplicate';
      transactionId: string;
      duplicateOfTransactionId: string;
      amountMinor: number;
      at: string;
    }
  | {
      kind: 'stale_in_transit';
      transferId: string;
      sentMinor: number;
      initiatedAt: string;
      daysStale: number;
    }
  | { kind: 'day_gap'; sinceDate: string; gapDays: number };

export interface ReconciliationSnapshot {
  accountId: string;
  currency: CurrencyCode;
  computedBalanceMinor: number;
  statedBalanceMinor: number;
  driftMinor: number; // statedBalanceMinor - computedBalanceMinor
  candidates: ReconcileCandidate[];
}

export interface LedgerState {
  accounts: Record<string, AccountRecord>;
  transactions: Record<string, TransactionRecord>;
  // WP-7.6 (NF-1 perf): `accountId:type:amountMinor` -> transaction ids sharing that key, in
  // creation order. TX-6 duplicate detection used to linear-scan ALL transactions on every
  // single `recordTransaction` call (O(n) per write => O(n²) to seed a ledger of n
  // transactions on one account — the actual bottleneck measured well past the NF-1 cold-start
  // budget at 50k transactions, apps/api/src/db/performance.test.ts). This index narrows that
  // scan to the tiny handful of transactions that could possibly match. Kept in sync wherever
  // a transaction's accountId/type/amountMinor can change post-creation (create, and the
  // amountMinor/accountId 'correct' branches below) — `type` has no amend path.
  transactionDuplicateIndex: Record<string, string[]>;
  transfers: Record<string, TransferRecord>;
  fxRates: Record<string, FxRateRecord>; // keyed "FROM-TO"
  categories: Record<string, CategoryRecord>;
  tags: Record<string, TagRecord>;
  rps: Record<string, RpRecord>;
  tpfCases: Record<string, TpfCaseRecord>;
  recurringTemplates: Record<string, RecurringTemplateRecord>;
  importSources: Record<string, ImportSourceRecord>;
  reminders: Record<string, ReminderRecord>;
  parties: Record<string, PartyRecord>;
  quotations: Record<string, QuotationRecord>;
  projects: Record<string, ProjectRecord>;
  scheduleItems: Record<string, ScheduleItemRecord>;
  goals: Record<string, GoalRecord>;
  reserves: Record<string, ReserveRecord>;
  suggestions: Record<string, SuggestionRecord>;
}

// WP-6.1 (M28 GL-1..5, S-31 `/goals`): a savings target the owner (or, later, the advisor —
// WP-6.2/6.3) tracks progress toward. `priority` is a bare positive integer for now — the
// waterfall's own P1..P5 tier semantics land with WP-6.2, not this WP. A goal's own
// "allocated"/"remaining" (GL-1) are DERIVED from linked reserves (`queryGoals`'s own
// `GoalView`), never stored — a goal's progress IS its reserves, not a second, duplicate
// running total that could drift from them.
export const GOAL_BEHAVIORS = ['strict', 'flexible'] as const;
export type GoalBehavior = (typeof GOAL_BEHAVIORS)[number];

export const GOAL_STATUSES = ['active', 'completed', 'archived'] as const;
export type GoalStatus = (typeof GOAL_STATUSES)[number];

export interface GoalRecord {
  id: string;
  name: string;
  targetAmountMinor: number;
  currency: CurrencyCode;
  targetDate?: string;
  priority: number;
  minimumContributionMinor?: number;
  behavior: GoalBehavior;
  status: GoalStatus;
  createdAt: string;
}

/** GL-1's own "allocated amount, remaining", computed at query time (see the header comment
 * on GoalRecord for why these are never stored fields). */
export interface GoalView extends GoalRecord {
  allocatedMinor: number;
  remainingMinor: number;
  completed: boolean; // GL-5 "completion" — allocatedMinor >= targetAmountMinor
}

// WP-6.1 (GL-4): "the concrete mechanism behind 'keep this aside'". Reserves are VIRTUAL —
// no money moves, no account/balance is touched — they only reduce DB-1's own Spendable Now
// figure. `goalId` is optional: a reserve can exist standalone ("Reserved for taxes") or
// linked to a Goal, whose own progress is the sum of its linked, still-active reserves.
export const RESERVE_STATUSES = ['active', 'released'] as const;
export type ReserveStatus = (typeof RESERVE_STATUSES)[number];

export interface ReserveRecord {
  id: string;
  purpose: string;
  amountMinor: number;
  currency: CurrencyCode;
  accountId?: string; // "globally or per account" (GL-4)
  goalId?: string;
  rpId?: string; // WP-6.3 (AD-3): the "planned settlement" flavor of a reserve — money set
  // aside for a SPECIFIC payable, never the RP's own real `settleRp` action (AD-7 boundary).
  status: ReserveStatus;
  createdAt: string;
  releasedAt?: string;
  releasedReason?: string;
}

/** GL-2 (LOCKED): "strict rules ... bind the Spendable-Now computation and force advisor
 * warnings on violation." A strict goal's OWN target is always treated as a floor the owner
 * must keep available, whether or not a matching Reserve has actually been created for it —
 * violated when the raw spendable-flagged balance dips below the combined floor. */
export interface StrictReserveViolation {
  currency: CurrencyCode;
  requiredMinor: number; // Σ active strict goals' targetAmountMinor
  availableMinor: number; // the SAME spendable-flagged balance queryDashboard computes
  violated: boolean;
}

/** WP-6.1 (§19.4 GL-6..8, OD-4): the reserve ramp-up computation. "Real data" excludes
 * backfilled (CSV-imported) transactions — OB-3's own existing `backfill` flag already
 * means "suppress advisor reactions for past-dated records," exactly the gate GL-6..8 need,
 * reused rather than re-invented. GL-8's "2 full months" is approximated as 60 days, the
 * SAME 30-days-per-month convention this codebase's own DB-2 "upcoming 30-day inflow"
 * already uses — not a separate calendar-month-boundary count. */
export interface ReserveRampUpStatus {
  currency: CurrencyCode;
  daysOfData: number;
  avgMonthlyEssentialMinor: number;
  targetProposalEligible: boolean; // GL-7: >= 60 days
  suggestedTargetMinor: number; // GL-7: 3x avg monthly essential (0 until eligible)
  lowSpendableAlertActive: boolean; // GL-8: >= 60 days (~2 months)
  lowSpendableThresholdMinor: number; // GL-8: 50% of avg monthly essential (0 until active)
}

// WP-6.2 (M29 AD-2, LOCKED §2.31, S-32): the priority waterfall — a PURE computation over
// new money, never a write (WP-6.3's own "Accept creates reserves/planned settlements only"
// is the write path; this WP is just the deterministic split). Each tier is funded from
// EXISTING WP-6.1 machinery, never a parallel computation: P1 "critical commitments" reuses
// `queryOverdueRps` (kind: payable) verbatim; P2 "minimum safety reserve" reuses
// `queryStrictReserveViolation`'s own shortfall verbatim (GL-2) — a strict goal the owner
// later converts per GL-7's own "may... convert it to strict at any time" is funded here
// automatically, with no separate "is this a safety-reserve goal" flag needed; P3 "near-term
// essential costs" reuses `queryUpcomingRecurringOccurrences(asOf, 30)` (the SAME 30-day
// window DB-2's own "upcoming" figures already use) filtered to expense occurrences whose
// category is essential (WP-6.1's own `CategoryRecord.essential`); P4 "financial goals" is
// every OTHER (non-strict) active goal in the owner's own `priority` order (GL-1) — P2 already
// claimed strict goals, so P4 only ever sees flexible ones, never double-funding a goal; P5
// "discretionary" is simply whatever's left, always present (even at 0) so
// `Σ lines.allocatedMinor === incomeMinor` holds by construction, not by a separate check.
export const WATERFALL_TIERS = ['P1', 'P2', 'P3', 'P4', 'P5'] as const;
export type WaterfallTier = (typeof WATERFALL_TIERS)[number];

// WP-6.3 (AD-3): what an accepted line becomes. 'rp'/'goal' carry the specific record the line
// is FOR (so `acceptSuggestion` can link the reserve it creates back to it); 'reserve' is a
// standalone envelope (P2's aggregate shortfall isn't attributable to one goal; P3's essential
// bill isn't a stored, linkable entity at all); 'discretionary' (P5) gets nothing created.
export const WATERFALL_TARGET_KINDS = ['rp', 'goal', 'reserve', 'discretionary'] as const;
export type WaterfallTargetKind = (typeof WATERFALL_TARGET_KINDS)[number];

export interface WaterfallLine {
  tier: WaterfallTier;
  label: string;
  allocatedMinor: number;
  targetKind: WaterfallTargetKind;
  targetId?: string; // set only when targetKind is 'rp' or 'goal'
}

export interface WaterfallAllocation {
  currency: CurrencyCode;
  incomeMinor: number;
  lines: WaterfallLine[];
}

// WP-6.3 (M29 AD-3/6/7, S-32): a Suggestion is the owner-facing, resolvable envelope around one
// advisor output — for now, only the 'allocation' kind (the waterfall split itself). `lines` is
// a SNAPSHOT of `computeWaterfallAllocation`'s own output at propose time, not re-derived at
// query/replay time — other ledger state (RPs settled, goals edited) may have moved on since,
// and the owner's eventual Accept must act on what they actually SAW, not a silently-redrawn plan.
export const SUGGESTION_STATUSES = ['pending', 'accepted', 'snoozed', 'dismissed'] as const;
export type SuggestionStatus = (typeof SUGGESTION_STATUSES)[number];

export interface SuggestionRecord {
  id: string;
  kind: 'allocation';
  currency: CurrencyCode;
  incomeMinor: number;
  asOf: string;
  lines: WaterfallLine[];
  status: SuggestionStatus;
  createdAt: string;
  snoozedUntil?: string;
  resolvedAt?: string; // set on accept or dismiss
  createdReserveIds: string[]; // populated by acceptSuggestion; empty until then
}

/** WP-6.4 (M29 AD-4, LOCKED §2.34): "Can I afford this?" — the four-part answer. `protectedFloorMinor`
 * is what's already spoken for (P1 overdue payables + P2 strict-reserve shortfall + P3 near-term
 * essential bills — the SAME three queries WP-6.2's waterfall reuses for those tiers). Paying is
 * `advisable` only if what's left after paying still covers that floor; `waitingWouldHelp` is true
 * only when it is NOT currently advisable and confirmed incoming money (open receivables + upcoming
 * recurring income, the SAME fields `queryDashboard` already aggregates) would close the gap. */
export interface AffordabilityAnswer {
  currency: CurrencyCode;
  amountMinor: number;
  purpose: string;
  asOf: string;
  spendableNowMinor: number;
  canPayNow: boolean; // spendableNowMinor >= amountMinor
  protectedFloorMinor: number;
  remainingAfterMinor: number; // spendableNowMinor - amountMinor
  advisable: boolean; // remainingAfterMinor >= protectedFloorMinor
  futureImpactMinor: number; // remainingAfterMinor - protectedFloorMinor (negative = eats into protected money)
  upcomingInflowMinor: number; // confirmed incoming: open receivables + upcoming 30-day recurring income
  waitingWouldHelp: boolean; // !advisable && upcomingInflowMinor >= the shortfall
}

// WP-7.1 (M30 SM-1..4, LOCKED §2.35, S-33, §14-11): the scenario simulator. `runScenario` is a
// PURE query (AR-7: no clock reads beyond the passed-in `asOf`, no writes whatsoever) — the
// strongest possible reading of SM-2's "can NEVER write to real records [LOCKED, enforced
// structurally]": there is no fork/throwaway EventStore to get wrong, because nothing is ever
// appended anywhere. Every operation just reshapes an in-memory list of cashflow events built
// from EXISTING queries (raw open RPs, `queryUpcomingRecurringOccurrences`, `queryReservedTotal`,
// `queryStrictReserveViolation`, `queryGoals`) before walking the window day by day.
export const SCENARIO_OPERATION_KINDS = [
  'delay',
  'change',
  'add',
  'payAll',
  'solveSpend',
  'solveSaving',
] as const;
export type ScenarioOperationKind = (typeof SCENARIO_OPERATION_KINDS)[number];

export type ScenarioOperation =
  | { kind: 'delay'; rpId: string; days: number } // SM-1 "client pays late"
  | { kind: 'change'; rpId: string; newAmountMinor: number } // SM-1 "receive 700 instead of 1000"
  | {
      kind: 'add';
      cashflowKind: 'expense' | 'income';
      amountMinor: number;
      at: string;
      label: string;
    } // SM-1 "spend 300,000"
  | { kind: 'payAll' } // SM-1 "pay all debts now"
  | { kind: 'solveSpend' } // SM-1 "safe monthly spend"
  | { kind: 'solveSaving'; goalId: string }; // SM-1 "required monthly saving for a target"

export interface ScenarioPoint {
  date: string;
  balanceMinor: number;
}

export interface ScenarioBreach {
  date: string;
  balanceMinor: number;
}

export interface ScenarioResult {
  currency: CurrencyCode;
  asOf: string;
  windowDays: number;
  points: ScenarioPoint[]; // one per day, asOf..asOf+windowDays inclusive
  reserveFloorMinor: number; // active reserves + strict-goal floor (SM-3 "vs reserves")
  breaches: ScenarioBreach[]; // every day the projected balance dips below reserveFloorMinor
  solvedAmountMinor?: number; // set only when a solveSpend/solveSaving operation was given
}

interface ScenarioCashflowEvent {
  dayIndex: number; // clamped into [0, windowDays]
  amountMinor: number; // signed: positive = inflow, negative = outflow
  rpId?: string; // set only for RP-derived events, so delay/change can target them
}

/** WP-7.4 (RE-5): "off by default; informational only" — REQUIREMENTS.md leaves "qualifying
 * balance" and the nisab/rate fully to the owner ("user-configured rules"); no fiqh methodology,
 * live nisab price, or fixed rate is specified anywhere in the spec. The chosen reading (a
 * documented default, not a religious ruling this app takes a position on): qualifying balance
 * = net worth minus what's currently owed — `netPositionMinor` (DB-1, already every non-archived
 * account + in-transit − TPF-held) minus `payableTotalMinor` (DB-3, already "what do I owe"),
 * clamped at zero — both already computed by `queryDashboard`, never a parallel aggregation. */
export interface ZakatEstimate {
  currency: CurrencyCode;
  asOf: string;
  qualifyingBalanceMinor: number;
  nisabMinor: number;
  ratePercent: number;
  meetsNisab: boolean;
  zakatDueMinor: number;
}

// §16.11 default category seed (owner-approved, 2026-07-08). WP-1.7 only builds the
// loader; the S-04 onboarding tree editor wires it in with WP-1.19.
const DEFAULT_EXPENSE_CATEGORIES: readonly { name: string; children: readonly string[] }[] = [
  { name: 'Food & Dining', children: ['Groceries', 'Restaurants', 'Chai & Snacks', 'Delivery'] },
  { name: 'Housing', children: ['Rent', 'Maintenance', 'Furniture & Appliances'] },
  { name: 'Utilities', children: ['Electricity', 'Gas', 'Water', 'Internet', 'Mobile'] },
  {
    name: 'Transport',
    children: ['Fuel', 'Ride-hailing', 'Public transport', 'Vehicle maintenance'],
  },
  { name: 'Family & Personal', children: ['Family support', 'Gifts', 'Clothing', 'Personal care'] },
  { name: 'Health', children: ['Medicine', 'Doctor', 'Tests'] },
  { name: 'Education', children: ['Courses', 'Books'] },
  {
    name: 'Business',
    children: [
      'Software & subscriptions',
      'Hosting & domains',
      'Hardware',
      'Payments to freelancers',
      'Professional services',
    ],
  },
  { name: 'Zakat & Charity', children: ['Zakat', 'Sadaqah'] },
  { name: 'Entertainment', children: ['Streaming', 'Outings'] },
  { name: 'Other', children: [] },
];
const DEFAULT_INCOME_CATEGORIES: readonly string[] = [
  'Client work',
  'Commission/handling',
  'Profit share',
  'Family/gift received',
  'Refund received',
  'Other',
];

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-+|-+$)/g, '');
}

interface AccountPayload {
  accountId: string;
  name: string;
  type: AccountType;
  currency: CurrencyCode;
  openingMinor: number;
  openingAsOf: string;
  spendable?: boolean;
  platformGroup?: string;
  icon?: string;
  color?: string;
}

/** editAccount patch (§18.5 Account form) and archive/unarchive both amend via this shape. */
interface AccountPatchPayload {
  accountId: string;
  name?: string;
  type?: AccountType;
  currency?: CurrencyCode;
  platformGroup?: string;
  spendable?: boolean;
  icon?: string;
  color?: string;
  archived?: boolean;
}

interface TransactionPayload {
  transactionId: string;
  type: 'expense' | 'income';
  accountId: string;
  amountMinor: number;
  lines?: TransactionLineRecord[];
  feeLegs?: FeeLegRecord[];
  category?: string;
  party?: string;
  partyId?: string;
  projectId?: string;
  scheduleItemId?: string;
  note?: string;
  tagIds?: string[];
  backfill?: boolean;
  adjustment?: boolean;
}

/** Bulk re-categorize (EX-2) amends one transaction's category, per-transaction, so LG-3
 * history is preserved individually rather than as one opaque bulk event. */
interface TransactionCategoryPatchPayload {
  transactionId: string;
  category: string;
}

/** WP-3.1 (PT-4): a merge's per-transaction re-point — a separate payload shape (not an
 * `action` field, matching `TransactionCategoryPatchPayload`'s own precedent, the only other
 * transaction-level amendment) so the projection discriminates by which key is present.
 * WP-4.2 (AU-7): also reused (not re-invented) by `correctTransactionParty` for a spoken/
 * typed party correction — `party` (the free-text name, a frozen historical fact per TX-3)
 * and/or `partyId` (the live link) may each be corrected independently, so both are optional
 * here; `mergeParties` itself always supplies `partyId` alone. */
interface TransactionPartyPatchPayload {
  transactionId: string;
  party?: string;
  partyId?: string;
}

/** WP-3.3 (PJ-7): "unallocated then relink" — a third transaction-level amendment shape,
 * discriminated the same way as the other two (by which key is present, no `action` field). */
interface TransactionProjectPatchPayload {
  transactionId: string;
  projectId: string;
}

/** WP-4.2 (AU-7/AM-3): "actually 550" — a fourth transaction-level amendment shape, same
 * discriminated-by-key precedent (no `action` field). Balance-bearing (LG-4): the
 * projection also moves the account's balance to match — see the transaction `correct`
 * projection below. */
interface TransactionAmountPatchPayload {
  transactionId: string;
  amountMinor: number;
}

/** WP-4.2 (AU-7/AM-3 "changes to financial obligations"): a fifth shape — moving a
 * transaction onto a different account. Also balance-bearing, same LG-4 reasoning as the
 * amount patch (unlike `projectId`/`category`, which carry no balance effect). */
interface TransactionAccountPatchPayload {
  transactionId: string;
  accountId: string;
}

/** WP-4.2 (TX-4): a sixth shape — S-10's web-only "split into lines" correction. REPLACES
 * (never append/merge) the transaction's own lines array wholesale; see
 * `splitTransactionLines`'s own header comment for the documented RP-4/SC-5
 * settlement-recompute trim (a real reconciliation problem, deliberately out of WP-4.2's
 * scope, not silently dropped). */
interface TransactionLinesPatchPayload {
  transactionId: string;
  lines: TransactionLineRecord[];
}

interface CategoryPayload {
  categoryId: string;
  name: string;
  kind: CategoryKind;
  parentId?: string;
}

/** null = move to root; undefined = parentId unchanged (distinct from the "no such field" case
 * every other optional patch field uses, since a category's own root-ness must be settable). */
interface CategoryPatchPayload {
  categoryId: string;
  name?: string;
  parentId?: string | null;
  archived?: boolean;
  displayOrder?: number;
  essential?: boolean; // WP-6.1 (GL-7/GL-8)
}

interface TagPayload {
  tagId: string;
  name: string;
}

interface TagPatchPayload {
  tagId: string;
  name?: string;
  archived?: boolean;
}

interface ImportSourcePayload {
  sourceId: string;
  name: string;
  mapping: ImportColumnMapping;
}

interface ImportSourcePatchPayload {
  sourceId: string;
  name?: string;
  mapping?: ImportColumnMapping;
}

interface TransferPayload {
  transferId: string;
  fromAccountId: string;
  toAccountId: string;
  sentMinor: number;
  feeLegs?: FeeLegRecord[];
  note?: string;
}

interface TransferSettlePayload {
  transferId: string;
  receivedMinor: number;
}

interface FxRatePayload {
  from: CurrencyCode;
  to: CurrencyCode;
  rate: number;
  source: 'manual' | 'fetched';
}

interface RpPayload {
  rpId: string;
  kind: RpKind;
  party: string;
  partyId?: string;
  amountMinor: number;
  currency: CurrencyCode;
  reason: string;
  dueDate?: string;
  notes?: string;
}

/** One discriminated payload for every post-create RP lifecycle event (RP-2/RP-3). */
type RpPatchPayload =
  | { rpId: string; action: 'settle'; amountMinor: number; transactionId: string }
  | { rpId: string; action: 'close'; reason: string }
  | { rpId: string; action: 'reopen' }
  | { rpId: string; action: 'relinkParty'; partyId: string } // WP-3.1 (PT-4): a merge's re-point
  | { rpId: string; action: 'setDueDate'; dueDate: string }; // WP-5.5 (OB-3 guided fix: undated_receivable)

interface GoalPayload {
  goalId: string;
  name: string;
  targetAmountMinor: number;
  currency: CurrencyCode;
  targetDate?: string;
  priority: number;
  minimumContributionMinor?: number;
  behavior: GoalBehavior;
}

/** One discriminated payload for every post-create Goal lifecycle event (GL-1/GL-3). */
type GoalPatchPayload =
  | {
      goalId: string;
      action: 'edit';
      name?: string;
      targetAmountMinor?: number;
      targetDate?: string;
      priority?: number;
      minimumContributionMinor?: number;
      behavior?: GoalBehavior;
    }
  | { goalId: string; action: 'complete' }
  | { goalId: string; action: 'archive' };

interface ReservePayload {
  reserveId: string;
  purpose: string;
  amountMinor: number;
  currency: CurrencyCode;
  accountId?: string;
  goalId?: string;
  rpId?: string;
}

/** The one post-create Reserve lifecycle event (GL-4 "releasable anytime ... all changes are
 * logged") — a single shape, not a union, so no `action` discriminant is needed structurally,
 * but one is kept anyway for symmetry with GoalPatchPayload/RpPatchPayload's own precedent. */
interface ReservePatchPayload {
  reserveId: string;
  action: 'release';
  reason: string;
}

interface SuggestionPayload {
  suggestionId: string;
  currency: CurrencyCode;
  incomeMinor: number;
  asOf: string;
  lines: WaterfallLine[];
}

type SuggestionPatchPayload =
  | { suggestionId: string; action: 'accept'; createdReserveIds: string[] }
  | { suggestionId: string; action: 'snooze'; snoozedUntil: string }
  | { suggestionId: string; action: 'dismiss' };

interface TpfCasePayload {
  caseId: string;
  party: string;
  partyId?: string;
  currency: CurrencyCode;
}

interface TpfMovementInput {
  caseAmountMinor: number;
  accountId: string;
  accountAmountMinor: number;
  fee?: TpfFeeRecord;
  note?: string;
}

/** One discriminated payload for every post-create TPF lifecycle event (TP-2..5). */
type TpfCasePatchPayload =
  | ({ caseId: string; action: 'receive' } & TpfMovementInput)
  | ({ caseId: string; action: 'return' } & TpfMovementInput)
  | { caseId: string; action: 'convert'; caseAmountMinor: number; reason: string }
  | { caseId: string; action: 'close' }
  | { caseId: string; action: 'relinkParty'; partyId: string }; // WP-3.1 (PT-4): a merge's re-point

interface RecurringTemplatePayload {
  templateId: string;
  name: string;
  base: RecurBase;
  schedule: RecurSchedule;
  behavior: RecurBehavior;
  startDate: string;
}

/** One discriminated payload for every post-create recurring-template lifecycle event (RC-2/3). */
type RecurringTemplatePatchPayload =
  | { templateId: string; action: 'pause' }
  | { templateId: string; action: 'resume' }
  | { templateId: string; action: 'skip'; nextRunAt: string }
  | { templateId: string; action: 'occurred'; nextRunAt: string; amountMinor: number }
  | {
      templateId: string;
      action: 'edit';
      name?: string;
      base?: RecurBase;
      schedule?: RecurSchedule;
      behavior?: RecurBehavior;
    };

interface ReminderPayload {
  reminderId: string;
  text: string;
  channel: 'whatsapp';
  nextRunAt: string;
  schedule?: RecurSchedule;
  linkType?: string;
  linkId?: string;
}

/** One discriminated payload for every post-create reminder lifecycle event (RM-3). */
type ReminderPatchPayload =
  | { reminderId: string; action: 'cancel' }
  | { reminderId: string; action: 'snooze'; nextRunAt: string }
  | { reminderId: string; action: 'complete'; firedAt: string } // one-off, done
  | { reminderId: string; action: 'occurred'; firedAt: string; nextRunAt: string }; // recurring, advances

interface PartyPayload {
  partyId: string;
  name: string;
  roles?: PartyRole[];
  aliases?: string[];
  phone?: string;
  provisional?: boolean;
}

type PartyPatchPayload =
  | {
      partyId: string;
      action: 'edit';
      name?: string;
      roles?: PartyRole[];
      aliases?: string[];
      phone?: string;
    }
  | { partyId: string; action: 'archive' }
  | { partyId: string; action: 'unarchive' }
  | { partyId: string; action: 'confirm' } // PT-6: provisional -> confirmed
  | { partyId: string; action: 'mergedAway'; mergedIntoId: string }; // PT-4

interface QuotationPayload {
  quotationId: string;
  party: string;
  partyId?: string;
  title: string;
  description?: string;
  pricing: QuotationPricing;
  currency: CurrencyCode;
  expiryDate?: string;
  notes?: string;
  paymentTerms?: string;
  expectedStartDate?: string;
}

type QuotationPatchPayload =
  | {
      quotationId: string;
      action: 'edit';
      party?: string;
      partyId?: string;
      title?: string;
      description?: string;
      expiryDate?: string;
      notes?: string;
      paymentTerms?: string;
      expectedStartDate?: string;
    }
  | {
      quotationId: string;
      action: 'revisePricing';
      pricing: QuotationPricing;
      supersededAt: string;
    } // QT-5
  | { quotationId: string; action: 'transition'; status: QuotationState }
  | {
      quotationId: string;
      action: 'convert';
      convertedToType: QuotationConvertTarget;
      convertedToId?: string;
    };

interface ProjectPayload {
  projectId: string;
  party: string;
  partyId?: string;
  title: string;
  serviceCategory?: string;
  productCategory?: string;
  currency: CurrencyCode;
  earningModel: ProjectEarningModel;
  originalQuotedRange?: { minMinor: number; maxMinor: number };
  agreedMinor: number;
  startDate?: string;
  endDate?: string;
  notes?: string;
  fromQuotationId?: string;
}

type ProjectPatchPayload =
  | {
      projectId: string;
      action: 'edit';
      title?: string;
      serviceCategory?: string;
      productCategory?: string;
      status?: ProjectStatus;
      startDate?: string;
      endDate?: string;
      notes?: string;
    }
  | { projectId: string; action: 'addAdjustment'; adjustment: ProjectAdjustment } // PJ-3
  | {
      projectId: string;
      action: 'logHours';
      entry: ProjectHoursEntry;
      adjustment: ProjectAdjustment;
    }; // PJ-5

interface ScheduleItemPayload {
  scheduleItemId: string;
  party: string;
  partyId?: string;
  projectId?: string;
  currency: CurrencyCode;
  expectedAmountMinor: number;
  expectedDate?: string;
  milestone?: string;
  confidence: ConfidenceLevel;
}

type ScheduleItemPatchPayload =
  | {
      scheduleItemId: string;
      action: 'edit';
      party?: string;
      partyId?: string;
      expectedAmountMinor?: number;
      milestone?: string;
      confidence?: ConfidenceLevel;
    }
  | { scheduleItemId: string; action: 'reschedule'; previousDate?: string; newDate?: string } // SC-6
  | { scheduleItemId: string; action: 'cancel'; reason: string }; // S-14's own form spec

/**
 * `nextOccurrence`'s monthly/yearly branches reset the time-of-day to midnight UTC (correct
 * for a recurring TEMPLATE's date-only `dueDate` — see schedule.ts — but a reminder's
 * `nextRunAt` carries a meaningful time-of-day, e.g. "every month on the 5th at 5:30pm"). This
 * re-applies the original time-of-day after stepping the schedule forward; for daily/weekly it
 * is a no-op since `nextOccurrence` already preserves the time there.
 */
function advanceReminderSchedule(schedule: RecurSchedule, currentNextRunAt: string): string {
  const next = new Date(nextOccurrence(schedule, currentNextRunAt));
  const reference = new Date(currentNextRunAt);
  next.setUTCHours(
    reference.getUTCHours(),
    reference.getUTCMinutes(),
    reference.getUTCSeconds(),
    reference.getUTCMilliseconds(),
  );
  return next.toISOString();
}

/** Net balance effect of a transaction on its account (fees reduce what lands/leaves adds). */
function balanceEffect(tx: {
  type: 'expense' | 'income';
  currency: CurrencyCode;
  amountMinor: number;
  feeLegs?: readonly FeeLegRecord[];
}): number {
  const fees = sum(
    tx.currency,
    (tx.feeLegs ?? []).map((f) => fromMinor(tx.currency, f.amountMinor)),
  ).minorUnits;
  // income: amount − fees lands (A.1: $500 − $12 = $488); expense: amount + fees leaves.
  return tx.type === 'income' ? tx.amountMinor - fees : -(tx.amountMinor + fees);
}

const DUPLICATE_WINDOW_MS = 24 * 60 * 60 * 1000; // TX-6: ±1 day (owner-approved default)

/** WP-7.6 (NF-1 perf): the same `accountId:type:amountMinor` grouping `LedgerState.
 * transactionDuplicateIndex` buckets transactions by — every candidate a duplicate check
 * could possibly match, and nothing else. */
function duplicateIndexKey(accountId: string, type: string, amountMinor: number): string {
  return `${accountId}:${type}:${String(amountMinor)}`;
}

/** WP-7.6: add a just-created (or re-keyed) transaction id to its duplicate-index bucket. */
function indexDuplicateCandidate(
  index: Record<string, string[]>,
  accountId: string,
  type: string,
  amountMinor: number,
  transactionId: string,
): void {
  const key = duplicateIndexKey(accountId, type, amountMinor);
  (index[key] ??= []).push(transactionId);
}

/** WP-7.6: the inverse of `indexDuplicateCandidate` — used when a 'correct' event changes a
 * transaction's accountId or amountMinor (its old bucket must stop matching it). */
function unindexDuplicateCandidate(
  index: Record<string, string[]>,
  accountId: string,
  type: string,
  amountMinor: number,
  transactionId: string,
): void {
  const key = duplicateIndexKey(accountId, type, amountMinor);
  const bucket = index[key];
  if (bucket === undefined) return;
  const i = bucket.indexOf(transactionId);
  if (i !== -1) bucket.splice(i, 1); // an emptied bucket is left as [] — index[key] ?? [] treats it identically to absent
}

/** TX-6: an existing non-reversed transaction on the same account, same type, same amount,
 * within ±1 day of `at`. The single source of truth for duplicate suspicion — reused by the
 * entry-time flagger (projection), reconciliation, and CSV-import preview. `index` narrows
 * the candidate set to the same accountId/type/amountMinor bucket (WP-7.6 NF-1 perf) — the
 * date-window/reversed/excludeId checks below then run over that tiny set, not every
 * transaction ever recorded. */
export function findDuplicateTransactionId(
  index: Record<string, string[]>,
  transactions: Record<string, TransactionRecord>,
  candidate: {
    accountId: string;
    type: 'expense' | 'income';
    amountMinor: number;
    at: string;
    excludeId?: string;
  },
): string | undefined {
  const target = Date.parse(candidate.at);
  const bucket =
    index[duplicateIndexKey(candidate.accountId, candidate.type, candidate.amountMinor)] ?? [];
  for (const id of bucket) {
    if (id === candidate.excludeId) continue;
    const tx = transactions[id];
    if (tx === undefined || tx.reversed) continue;
    if (Math.abs(Date.parse(tx.at) - target) <= DUPLICATE_WINDOW_MS) return tx.id;
  }
  return undefined;
}

/**
 * TP-3/FE-4: how much of a TPF fee reduces owed-back. Owner-borne fees don't (the owner
 * absorbs the cost and still owes the third party the full amount); third-party-borne fees
 * reduce owed-back by the full fee (A.3); a split reduces it by half — the owner absorbs
 * the rest.
 */
function chargedFeeMinor(fee: TpfFeeRecord | undefined): number {
  if (fee === undefined) return 0;
  if (fee.bearer === 'owner') return 0;
  if (fee.bearer === 'tpf_owner') return fee.amountMinor;
  return Math.round(fee.amountMinor / 2); // 'split'
}

// SC-5: applies one receipt to a schedule item — shared by the direct `scheduleItemId` field
// and split-line handling in the transaction-create projection, so both paths update
// `receivedMinor`/`status` identically. A no-op if `scheduleItemId` is undefined or unknown
// (write path validates before the event is ever appended; replay just stays total).
function applyScheduleItemReceipt(
  state: LedgerState,
  scheduleItemId: string | undefined,
  amountMinor: number,
  transactionId: string,
  at: string,
): void {
  if (scheduleItemId === undefined) return;
  const item = state.scheduleItems[scheduleItemId];
  if (item === undefined) return;
  item.receipts.push({ amountMinor, transactionId, at });
  item.receivedMinor += amountMinor;
  if (item.status !== 'cancelled') {
    item.status = item.receivedMinor >= item.expectedAmountMinor ? 'paid' : 'partially_paid';
  }
}

/** WP-Q12 part 3 (LG-4, RP-2, SC-4): back out everything a now-reversed transaction had paid.
 *
 * Two record types link back to a transaction by id — an RP's `settlements` (RP-2: "each
 * settlement links to the real transaction that paid it") and a schedule item's `receipts`.
 * Both store the link, so both can be un-applied deterministically on replay.
 *
 * Deliberately NOT reopened: an RP that was CLOSED (RP-3 written off / waived) and a schedule
 * item that was CANCELLED. Those are independent owner decisions, not consequences of a payment
 * — silently reopening one because an unrelated payment was reversed would undo a choice the
 * owner made on purpose. Their amounts are still corrected; only the status is left alone. */
function unapplyTransactionEffects(state: LedgerState, transactionId: string): void {
  for (const rp of Object.values(state.rps)) {
    if (!rp.settlements.some((s) => s.transactionId === transactionId)) continue;
    rp.settlements = rp.settlements.filter((s) => s.transactionId !== transactionId);
    rp.settledMinor = rp.settlements.reduce((sum, s) => sum + s.amountMinor, 0);
    if (rp.status !== 'closed') {
      rp.status = rp.settledMinor >= rp.amountMinor ? 'settled' : 'open';
    }
  }

  for (const item of Object.values(state.scheduleItems)) {
    if (!item.receipts.some((r) => r.transactionId === transactionId)) continue;
    item.receipts = item.receipts.filter((r) => r.transactionId !== transactionId);
    item.receivedMinor = item.receipts.reduce((sum, r) => sum + r.amountMinor, 0);
    if (item.status !== 'cancelled') {
      // 'due'/'overdue' are DERIVED from dates at query time (ScheduleItemLifecycleStatus
      // excludes them), so the stored status returns to 'upcoming' rather than guessing one.
      item.status =
        item.receivedMinor >= item.expectedAmountMinor
          ? 'paid'
          : item.receivedMinor > 0
            ? 'partially_paid'
            : 'upcoming';
    }
  }
}

export const ledgerProjection: Projection<LedgerState> = {
  name: 'ledger',
  init: () => ({
    accounts: {},
    transactions: {},
    transactionDuplicateIndex: {},
    transfers: {},
    fxRates: {},
    categories: {},
    tags: {},
    rps: {},
    tpfCases: {},
    recurringTemplates: {},
    importSources: {},
    reminders: {},
    parties: {},
    quotations: {},
    projects: {},
    scheduleItems: {},
    goals: {},
    reserves: {},
    suggestions: {},
  }),
  apply(state, event: LedgerEvent) {
    if (event.entityType === 'account' && event.kind === 'create') {
      const p = event.payload as AccountPayload;
      state.accounts[p.accountId] = {
        id: p.accountId,
        name: p.name,
        type: p.type,
        currency: p.currency,
        openingMinor: p.openingMinor,
        openingAsOf: p.openingAsOf,
        spendable: p.spendable ?? true,
        archived: false,
        // AC-2: deterministic display order — position among accounts created so far.
        displayOrder: Object.keys(state.accounts).length,
        ...(p.platformGroup !== undefined ? { platformGroup: p.platformGroup } : {}),
        ...(p.icon !== undefined ? { icon: p.icon } : {}),
        ...(p.color !== undefined ? { color: p.color } : {}),
        balanceMinor: p.openingMinor,
      };
      return state;
    }
    if (event.entityType === 'account' && event.kind === 'correct') {
      const p = event.payload as AccountPatchPayload;
      const account = state.accounts[p.accountId];
      if (account === undefined) return state; // write path validates; replay stays total
      if (p.name !== undefined) account.name = p.name;
      if (p.type !== undefined) account.type = p.type;
      if (p.currency !== undefined) account.currency = p.currency;
      if (p.platformGroup !== undefined) account.platformGroup = p.platformGroup;
      if (p.spendable !== undefined) account.spendable = p.spendable;
      if (p.icon !== undefined) account.icon = p.icon;
      if (p.color !== undefined) account.color = p.color;
      if (p.archived !== undefined) account.archived = p.archived;
      return state;
    }
    if (event.entityType === 'transaction') {
      if (event.kind === 'create') {
        const p = event.payload as TransactionPayload;
        const account = state.accounts[p.accountId];
        if (account === undefined) return state; // write path validates; replay stays total
        // TX-6: flag against ALREADY-applied (earlier) transactions — deterministic, and an
        // adjustment is never a "duplicate" of anything (reconciliation-only).
        const duplicateOf =
          p.adjustment === true
            ? undefined
            : findDuplicateTransactionId(state.transactionDuplicateIndex, state.transactions, {
                accountId: p.accountId,
                type: p.type,
                amountMinor: p.amountMinor,
                at: event.at,
              });
        const record: TransactionRecord = {
          id: p.transactionId,
          type: p.type,
          accountId: p.accountId,
          currency: account.currency,
          amountMinor: p.amountMinor,
          at: event.at, // TX-3 datetime — the caller-supplied event instant (AR-7)
          ...(p.lines !== undefined ? { lines: p.lines } : {}),
          ...(p.feeLegs !== undefined ? { feeLegs: p.feeLegs } : {}),
          ...(p.category !== undefined ? { category: p.category } : {}),
          ...(p.party !== undefined ? { party: p.party } : {}),
          ...(p.partyId !== undefined ? { partyId: p.partyId } : {}),
          ...(p.projectId !== undefined ? { projectId: p.projectId } : {}),
          ...(p.scheduleItemId !== undefined ? { scheduleItemId: p.scheduleItemId } : {}),
          ...(p.note !== undefined ? { note: p.note } : {}),
          ...(p.tagIds !== undefined ? { tagIds: p.tagIds } : {}),
          ...(p.backfill !== undefined ? { backfill: p.backfill } : {}),
          ...(p.adjustment !== undefined ? { adjustment: p.adjustment } : {}),
          ...(duplicateOf !== undefined ? { duplicateOf } : {}),
          reversed: false,
          source: event.source,
        };
        state.transactions[record.id] = record;
        // TX-6: an adjustment is never a duplicate "original" either, not just never itself
        // flagged (above) — never index it as a candidate a LATER ordinary transaction could
        // match against.
        if (record.adjustment !== true) {
          indexDuplicateCandidate(
            state.transactionDuplicateIndex,
            record.accountId,
            record.type,
            record.amountMinor,
            record.id,
          );
        }
        account.balanceMinor += balanceEffect(record);
        // RP-4: a split line linked to a receivable/payable settles it automatically —
        // validated by `recordTransaction` before this event was ever appended. WP-3.3
        // (PJ-6): a `'project'` line has nothing analogous to settle yet — the project's own
        // value lenses are computed fresh from linked transactions, not accumulated here.
        for (const line of record.lines ?? []) {
          if (line.linkType !== 'receivable' && line.linkType !== 'payable') continue;
          if (line.linkId === undefined) continue;
          const rp = state.rps[line.linkId];
          if (rp === undefined) continue; // write path validates; replay stays total
          rp.settlements.push({
            amountMinor: line.amountMinor,
            transactionId: record.id,
            at: event.at,
          });
          rp.settledMinor += line.amountMinor;
          if (rp.settledMinor >= rp.amountMinor) rp.status = 'settled';
        }
        // SC-5: a receipt against a schedule item, via either the direct `scheduleItemId` (the
        // common "one payment, one schedule item" case, mirroring `projectId`) or a split line —
        // both validated by `recordTransaction` before this event was ever appended.
        applyScheduleItemReceipt(
          state,
          record.scheduleItemId,
          record.amountMinor,
          record.id,
          event.at,
        );
        for (const line of record.lines ?? []) {
          if (line.linkType !== 'scheduleItem') continue;
          applyScheduleItemReceipt(state, line.linkId, line.amountMinor, record.id, event.at);
        }
        return state;
      }
      if (event.kind === 'reverse') {
        const p = event.payload as { transactionId: string };
        const tx = state.transactions[p.transactionId];
        if (tx === undefined || tx.reversed) return state;
        tx.reversed = true;
        const account = state.accounts[tx.accountId];
        if (account !== undefined) account.balanceMinor -= balanceEffect(tx);
        // LG-4: the money is gone from the account, so whatever this transaction PAID must stop
        // counting as paid too. Without this, reversing a settlement leaves the invoice reading
        // "settled" with the cash withdrawn — and permanently unsettleable, since settling an
        // already-settled RP is refused. Same for a scheduled item's receipts (SC-4).
        // History is untouched: the settle event and this reverse event both stay in the log
        // (LG-2/LG-3); only the DERIVED current state is corrected.
        unapplyTransactionEffects(state, p.transactionId);
        return state;
      }
      if (event.kind === 'correct') {
        // Six transaction-level amendment shapes so far, discriminated by which key is
        // present (none carry an `action` field) — EX-2 bulk re-categorize, WP-3.1's PT-4
        // merge re-point (sharing its shape with WP-4.2's own spoken party correction),
        // WP-3.3's PJ-7 unallocated-income relink, and WP-4.2's (AU-7/AM-3) amount/account/
        // lines corrections.
        const p = event.payload as
          | TransactionCategoryPatchPayload
          | TransactionPartyPatchPayload
          | TransactionProjectPatchPayload
          | TransactionAmountPatchPayload
          | TransactionAccountPatchPayload
          | TransactionLinesPatchPayload;
        const tx = state.transactions[p.transactionId];
        if (tx === undefined) return state;
        if ('category' in p) {
          tx.category = p.category;
        } else if ('projectId' in p) {
          tx.projectId = p.projectId;
        } else if ('amountMinor' in p) {
          // LG-4: an amount correction must keep account.balanceMinor = opening + Σ effects —
          // undo the OLD effect, apply the corrected amount, then reapply.
          const account = state.accounts[tx.accountId];
          if (account !== undefined && !tx.reversed) account.balanceMinor -= balanceEffect(tx);
          // WP-7.6: the duplicate-index bucket is keyed by amountMinor too — re-key it.
          unindexDuplicateCandidate(
            state.transactionDuplicateIndex,
            tx.accountId,
            tx.type,
            tx.amountMinor,
            tx.id,
          );
          tx.amountMinor = p.amountMinor;
          // TX-6: never re-index an adjustment (see the create-time guard's own comment).
          if (tx.adjustment !== true) {
            indexDuplicateCandidate(
              state.transactionDuplicateIndex,
              tx.accountId,
              tx.type,
              tx.amountMinor,
              tx.id,
            );
          }
          if (account !== undefined && !tx.reversed) account.balanceMinor += balanceEffect(tx);
        } else if ('accountId' in p) {
          // LG-4: unlike `projectId`/`category`, `accountId` IS balance-bearing — move the
          // transaction's own balance effect from the OLD account onto the NEW one.
          if (!tx.reversed) {
            const oldAccount = state.accounts[tx.accountId];
            if (oldAccount !== undefined) oldAccount.balanceMinor -= balanceEffect(tx);
            const newAccount = state.accounts[p.accountId];
            if (newAccount !== undefined) newAccount.balanceMinor += balanceEffect(tx);
          }
          // WP-7.6: the duplicate-index bucket is keyed by accountId too — re-key it.
          unindexDuplicateCandidate(
            state.transactionDuplicateIndex,
            tx.accountId,
            tx.type,
            tx.amountMinor,
            tx.id,
          );
          tx.accountId = p.accountId;
          // TX-6: never re-index an adjustment (see the create-time guard's own comment).
          if (tx.adjustment !== true) {
            indexDuplicateCandidate(
              state.transactionDuplicateIndex,
              tx.accountId,
              tx.type,
              tx.amountMinor,
              tx.id,
            );
          }
        } else if ('lines' in p) {
          // AR-4/TX-4: REPLACE (never append/merge) — see `splitTransactionLines`'s own
          // header comment for why RP-4/SC-5 totals derived from the OLD lines are left as-is.
          tx.lines = p.lines;
        } else {
          // WP-4.2 (AU-7): party correction — free-text `party` and/or linked `partyId`,
          // either or both may be present (`mergeParties` always supplies `partyId` alone).
          if (p.party !== undefined) tx.party = p.party;
          if (p.partyId !== undefined) tx.partyId = p.partyId;
        }
        return state;
      }
    }
    if (event.entityType === 'transfer' && event.kind === 'create') {
      const p = event.payload as TransferPayload;
      const from = state.accounts[p.fromAccountId];
      if (from === undefined) return state; // write path validates; replay stays total
      const to = state.accounts[p.toAccountId];
      const feesMinor = sum(
        from.currency,
        (p.feeLegs ?? []).map((f) => fromMinor(from.currency, f.amountMinor)),
      ).minorUnits;
      // FX-1: cross-currency only — estimate from whatever rate is cached RIGHT NOW (this
      // replays deterministically: same event log -> same fxRates history at this point ->
      // same estimate every time, per AR-4/AR-7).
      let estimatedReceivedMinor: number | undefined;
      if (to !== undefined && to.currency !== from.currency) {
        const cached = state.fxRates[`${from.currency}-${to.currency}`];
        if (cached !== undefined) estimatedReceivedMinor = Math.round(p.sentMinor * cached.rate);
      }
      state.transfers[p.transferId] = {
        id: p.transferId,
        fromAccountId: p.fromAccountId,
        toAccountId: p.toAccountId,
        sentMinor: p.sentMinor,
        sentCurrency: from.currency,
        ...(p.feeLegs !== undefined ? { feeLegs: p.feeLegs } : {}),
        status: 'in_transit',
        initiatedAt: event.at,
        ...(p.note !== undefined ? { note: p.note } : {}),
        ...(estimatedReceivedMinor !== undefined ? { estimatedReceivedMinor } : {}),
      };
      // TR-2: leaves the source now; NOT credited to the destination until settled.
      from.balanceMinor -= p.sentMinor + feesMinor;
      return state;
    }
    if (event.entityType === 'transfer' && event.kind === 'correct') {
      const p = event.payload as TransferSettlePayload;
      const transfer = state.transfers[p.transferId];
      if (transfer === undefined || transfer.status === 'settled') return state;
      const to = state.accounts[transfer.toAccountId];
      if (to === undefined) return state;
      transfer.status = 'settled';
      transfer.receivedMinor = p.receivedMinor;
      transfer.settledAt = event.at;
      // FX-2: the estimate captured at initiation is never revalued — only compared once,
      // here, to produce FX-4's conversion-loss figure.
      if (transfer.estimatedReceivedMinor !== undefined) {
        transfer.conversionLossMinor = transfer.estimatedReceivedMinor - p.receivedMinor;
      }
      to.balanceMinor += p.receivedMinor;
      return state;
    }
    if (event.entityType === 'category' && event.kind === 'create') {
      const p = event.payload as CategoryPayload;
      const siblingCount = Object.values(state.categories).filter(
        (c) => c.kind === p.kind && c.parentId === p.parentId,
      ).length;
      state.categories[p.categoryId] = {
        id: p.categoryId,
        name: p.name,
        kind: p.kind,
        ...(p.parentId !== undefined ? { parentId: p.parentId } : {}),
        archived: false,
        displayOrder: siblingCount, // EX-2: deterministic — position among siblings so far
      };
      return state;
    }
    if (event.entityType === 'category' && event.kind === 'correct') {
      const p = event.payload as CategoryPatchPayload;
      const category = state.categories[p.categoryId];
      if (category === undefined) return state; // write path validates; replay stays total
      if (p.name !== undefined) category.name = p.name;
      if (p.parentId !== undefined) {
        if (p.parentId === null) delete category.parentId;
        else category.parentId = p.parentId;
      }
      if (p.archived !== undefined) category.archived = p.archived;
      if (p.displayOrder !== undefined) category.displayOrder = p.displayOrder;
      if (p.essential !== undefined) category.essential = p.essential;
      return state;
    }
    if (event.entityType === 'tag' && event.kind === 'create') {
      const p = event.payload as TagPayload;
      state.tags[p.tagId] = { id: p.tagId, name: p.name, archived: false };
      return state;
    }
    if (event.entityType === 'tag' && event.kind === 'correct') {
      const p = event.payload as TagPatchPayload;
      const tag = state.tags[p.tagId];
      if (tag === undefined) return state; // write path validates; replay stays total
      if (p.name !== undefined) tag.name = p.name;
      if (p.archived !== undefined) tag.archived = p.archived;
      return state;
    }
    if (event.entityType === 'importSource' && event.kind === 'create') {
      const p = event.payload as ImportSourcePayload;
      state.importSources[p.sourceId] = {
        id: p.sourceId,
        name: p.name,
        mapping: p.mapping,
        createdAt: event.at,
      };
      return state;
    }
    if (event.entityType === 'importSource' && event.kind === 'correct') {
      const p = event.payload as ImportSourcePatchPayload;
      const source = state.importSources[p.sourceId];
      if (source === undefined) return state; // write path validates; replay stays total
      if (p.name !== undefined) source.name = p.name;
      if (p.mapping !== undefined) source.mapping = p.mapping;
      return state;
    }
    if (event.entityType === 'rp' && event.kind === 'create') {
      const p = event.payload as RpPayload;
      state.rps[p.rpId] = {
        id: p.rpId,
        kind: p.kind,
        party: p.party,
        ...(p.partyId !== undefined ? { partyId: p.partyId } : {}),
        amountMinor: p.amountMinor,
        currency: p.currency,
        reason: p.reason,
        createdAt: event.at,
        ...(p.dueDate !== undefined ? { dueDate: p.dueDate } : {}),
        ...(p.notes !== undefined ? { notes: p.notes } : {}),
        settlements: [],
        settledMinor: 0,
        status: 'open',
      };
      return state;
    }
    if (event.entityType === 'rp' && event.kind === 'correct') {
      const p = event.payload as RpPatchPayload;
      const rp = state.rps[p.rpId];
      if (rp === undefined) return state; // write path validates; replay stays total
      if (p.action === 'settle') {
        rp.settlements.push({
          amountMinor: p.amountMinor,
          transactionId: p.transactionId,
          at: event.at,
        });
        rp.settledMinor += p.amountMinor;
        if (rp.settledMinor >= rp.amountMinor) rp.status = 'settled';
        return state;
      }
      if (p.action === 'close') {
        rp.status = 'closed';
        rp.closedReason = p.reason;
        rp.closedAt = event.at;
        return state;
      }
      if (p.action === 'relinkParty') {
        rp.partyId = p.partyId;
        return state;
      }
      if (p.action === 'setDueDate') {
        rp.dueDate = p.dueDate;
        return state;
      }
      // p.action === 'reopen' (RP-3 "reversible via correction")
      rp.status = rp.settledMinor >= rp.amountMinor ? 'settled' : 'open';
      delete rp.closedReason;
      delete rp.closedAt;
      return state;
    }
    if (event.entityType === 'goal' && event.kind === 'create') {
      const p = event.payload as GoalPayload;
      state.goals[p.goalId] = {
        id: p.goalId,
        name: p.name,
        targetAmountMinor: p.targetAmountMinor,
        currency: p.currency,
        ...(p.targetDate !== undefined ? { targetDate: p.targetDate } : {}),
        priority: p.priority,
        ...(p.minimumContributionMinor !== undefined
          ? { minimumContributionMinor: p.minimumContributionMinor }
          : {}),
        behavior: p.behavior,
        status: 'active',
        createdAt: event.at,
      };
      return state;
    }
    if (event.entityType === 'goal' && event.kind === 'correct') {
      const p = event.payload as GoalPatchPayload;
      const goal = state.goals[p.goalId];
      if (goal === undefined) return state; // write path validates; replay stays total
      if (p.action === 'edit') {
        if (p.name !== undefined) goal.name = p.name;
        if (p.targetAmountMinor !== undefined) goal.targetAmountMinor = p.targetAmountMinor;
        if (p.targetDate !== undefined) goal.targetDate = p.targetDate;
        if (p.priority !== undefined) goal.priority = p.priority;
        if (p.minimumContributionMinor !== undefined)
          goal.minimumContributionMinor = p.minimumContributionMinor;
        if (p.behavior !== undefined) goal.behavior = p.behavior;
        return state;
      }
      goal.status = p.action === 'complete' ? 'completed' : 'archived';
      return state;
    }
    if (event.entityType === 'reserve' && event.kind === 'create') {
      const p = event.payload as ReservePayload;
      state.reserves[p.reserveId] = {
        id: p.reserveId,
        purpose: p.purpose,
        amountMinor: p.amountMinor,
        currency: p.currency,
        ...(p.accountId !== undefined ? { accountId: p.accountId } : {}),
        ...(p.goalId !== undefined ? { goalId: p.goalId } : {}),
        ...(p.rpId !== undefined ? { rpId: p.rpId } : {}),
        status: 'active',
        createdAt: event.at,
      };
      return state;
    }
    if (event.entityType === 'reserve' && event.kind === 'correct') {
      const p = event.payload as ReservePatchPayload;
      const reserve = state.reserves[p.reserveId];
      if (reserve === undefined) return state; // write path validates; replay stays total
      // p.action === 'release' (GL-4 "releasable anytime")
      reserve.status = 'released';
      reserve.releasedAt = event.at;
      reserve.releasedReason = p.reason;
      return state;
    }
    if (event.entityType === 'suggestion' && event.kind === 'create') {
      const p = event.payload as SuggestionPayload;
      state.suggestions[p.suggestionId] = {
        id: p.suggestionId,
        kind: 'allocation',
        currency: p.currency,
        incomeMinor: p.incomeMinor,
        asOf: p.asOf,
        lines: p.lines,
        status: 'pending',
        createdAt: event.at,
        createdReserveIds: [],
      };
      return state;
    }
    if (event.entityType === 'suggestion' && event.kind === 'correct') {
      const p = event.payload as SuggestionPatchPayload;
      const suggestion = state.suggestions[p.suggestionId];
      if (suggestion === undefined) return state; // write path validates; replay stays total
      if (p.action === 'accept') {
        suggestion.status = 'accepted';
        suggestion.resolvedAt = event.at;
        suggestion.createdReserveIds = p.createdReserveIds;
        return state;
      }
      if (p.action === 'snooze') {
        suggestion.status = 'snoozed';
        suggestion.snoozedUntil = p.snoozedUntil;
        return state;
      }
      // p.action === 'dismiss'
      suggestion.status = 'dismissed';
      suggestion.resolvedAt = event.at;
      return state;
    }
    if (event.entityType === 'tpfCase' && event.kind === 'create') {
      const p = event.payload as TpfCasePayload;
      state.tpfCases[p.caseId] = {
        id: p.caseId,
        party: p.party,
        ...(p.partyId !== undefined ? { partyId: p.partyId } : {}),
        currency: p.currency,
        movements: [],
        receivedMinor: 0,
        chargedFeesMinor: 0,
        returnedMinor: 0,
        convertedMinor: 0,
        owedBackMinor: 0,
        status: 'open',
      };
      return state;
    }
    if (event.entityType === 'tpfCase' && event.kind === 'correct') {
      const p = event.payload as TpfCasePatchPayload;
      const tpfCase = state.tpfCases[p.caseId];
      if (tpfCase === undefined) return state; // write path validates; replay stays total
      if (p.action === 'relinkParty') {
        tpfCase.partyId = p.partyId;
        return state;
      }
      if (p.action === 'receive' || p.action === 'return') {
        const charged = chargedFeeMinor(p.fee);
        tpfCase.movements.push({
          kind: p.action,
          caseAmountMinor: p.caseAmountMinor,
          accountId: p.accountId,
          accountAmountMinor: p.accountAmountMinor,
          ...(p.fee !== undefined ? { fee: p.fee } : {}),
          ...(p.note !== undefined ? { note: p.note } : {}),
          at: event.at,
        });
        if (p.action === 'receive') {
          tpfCase.receivedMinor += p.caseAmountMinor;
          tpfCase.chargedFeesMinor += charged;
        } else {
          tpfCase.returnedMinor += p.caseAmountMinor;
          tpfCase.chargedFeesMinor += charged;
        }
        tpfCase.owedBackMinor =
          tpfCase.receivedMinor -
          tpfCase.chargedFeesMinor -
          tpfCase.returnedMinor -
          tpfCase.convertedMinor;
        const account = state.accounts[p.accountId];
        if (account !== undefined) {
          account.balanceMinor +=
            p.action === 'receive' ? p.accountAmountMinor : -p.accountAmountMinor;
        }
        return state;
      }
      if (p.action === 'convert') {
        tpfCase.movements.push({
          kind: 'convert',
          caseAmountMinor: p.caseAmountMinor,
          reason: p.reason,
          at: event.at,
        });
        tpfCase.convertedMinor += p.caseAmountMinor;
        tpfCase.owedBackMinor =
          tpfCase.receivedMinor -
          tpfCase.chargedFeesMinor -
          tpfCase.returnedMinor -
          tpfCase.convertedMinor;
        return state;
      }
      // p.action === 'close'
      tpfCase.status = 'closed';
      tpfCase.closedAt = event.at;
      return state;
    }
    if (event.entityType === 'recurringTemplate' && event.kind === 'create') {
      const p = event.payload as RecurringTemplatePayload;
      state.recurringTemplates[p.templateId] = {
        id: p.templateId,
        name: p.name,
        base: p.base,
        schedule: p.schedule,
        behavior: p.behavior,
        status: 'active',
        nextRunAt: firstOccurrenceOnOrAfter(p.schedule, p.startDate),
        createdAt: event.at,
      };
      return state;
    }
    if (event.entityType === 'recurringTemplate' && event.kind === 'correct') {
      const p = event.payload as RecurringTemplatePatchPayload;
      const template = state.recurringTemplates[p.templateId];
      if (template === undefined) return state; // write path validates; replay stays total
      if (p.action === 'pause') {
        template.status = 'paused';
        return state;
      }
      if (p.action === 'resume') {
        template.status = 'active';
        return state;
      }
      if (p.action === 'skip') {
        template.nextRunAt = p.nextRunAt;
        return state;
      }
      if (p.action === 'occurred') {
        template.nextRunAt = p.nextRunAt;
        template.lastAmountMinor = p.amountMinor;
        return state;
      }
      // p.action === 'edit'
      if (p.name !== undefined) template.name = p.name;
      if (p.base !== undefined) template.base = p.base;
      if (p.schedule !== undefined) template.schedule = p.schedule;
      if (p.behavior !== undefined) template.behavior = p.behavior;
      return state;
    }
    if (event.entityType === 'reminder' && event.kind === 'create') {
      const p = event.payload as ReminderPayload;
      state.reminders[p.reminderId] = {
        id: p.reminderId,
        text: p.text,
        channel: p.channel,
        nextRunAt: p.nextRunAt,
        ...(p.schedule !== undefined ? { schedule: p.schedule } : {}),
        status: 'active',
        snoozed: false,
        ...(p.linkType !== undefined ? { linkType: p.linkType } : {}),
        ...(p.linkId !== undefined ? { linkId: p.linkId } : {}),
        createdAt: event.at,
      };
      return state;
    }
    if (event.entityType === 'reminder' && event.kind === 'correct') {
      const p = event.payload as ReminderPatchPayload;
      const reminder = state.reminders[p.reminderId];
      if (reminder === undefined) return state; // write path validates; replay stays total
      if (p.action === 'cancel') {
        reminder.status = 'cancelled';
        return state;
      }
      if (p.action === 'snooze') {
        reminder.nextRunAt = p.nextRunAt;
        reminder.snoozed = true;
        reminder.status = 'active'; // revives a 'done' one-off — see snoozeReminder's own note
        return state;
      }
      if (p.action === 'complete') {
        reminder.status = 'done';
        reminder.snoozed = false;
        reminder.lastFiredAt = p.firedAt;
        return state;
      }
      // p.action === 'occurred'
      reminder.nextRunAt = p.nextRunAt;
      reminder.snoozed = false;
      reminder.lastFiredAt = p.firedAt;
      return state;
    }
    if (event.entityType === 'party' && event.kind === 'create') {
      const p = event.payload as PartyPayload;
      state.parties[p.partyId] = {
        id: p.partyId,
        name: p.name,
        roles: p.roles ?? [],
        aliases: p.aliases ?? [],
        ...(p.phone !== undefined ? { phone: p.phone } : {}),
        archived: false,
        provisional: p.provisional ?? false,
        createdAt: event.at,
      };
      return state;
    }
    if (event.entityType === 'party' && event.kind === 'correct') {
      const p = event.payload as PartyPatchPayload;
      const party = state.parties[p.partyId];
      if (party === undefined) return state; // write path validates; replay stays total
      if (p.action === 'archive') {
        party.archived = true;
        return state;
      }
      if (p.action === 'unarchive') {
        party.archived = false;
        return state;
      }
      if (p.action === 'confirm') {
        party.provisional = false;
        return state;
      }
      if (p.action === 'mergedAway') {
        party.archived = true;
        party.mergedIntoId = p.mergedIntoId;
        return state;
      }
      // p.action === 'edit'
      if (p.name !== undefined) party.name = p.name;
      if (p.roles !== undefined) party.roles = p.roles;
      if (p.aliases !== undefined) party.aliases = p.aliases;
      if (p.phone !== undefined) party.phone = p.phone;
      return state;
    }
    if (event.entityType === 'quotation' && event.kind === 'create') {
      const p = event.payload as QuotationPayload;
      state.quotations[p.quotationId] = {
        id: p.quotationId,
        party: p.party,
        ...(p.partyId !== undefined ? { partyId: p.partyId } : {}),
        title: p.title,
        ...(p.description !== undefined ? { description: p.description } : {}),
        pricing: p.pricing,
        currency: p.currency,
        ...(p.expiryDate !== undefined ? { expiryDate: p.expiryDate } : {}),
        ...(p.notes !== undefined ? { notes: p.notes } : {}),
        ...(p.paymentTerms !== undefined ? { paymentTerms: p.paymentTerms } : {}),
        ...(p.expectedStartDate !== undefined ? { expectedStartDate: p.expectedStartDate } : {}),
        status: 'draft',
        versions: [],
        createdAt: event.at,
      };
      return state;
    }
    if (event.entityType === 'quotation' && event.kind === 'correct') {
      const p = event.payload as QuotationPatchPayload;
      const quotation = state.quotations[p.quotationId];
      if (quotation === undefined) return state; // write path validates; replay stays total
      if (p.action === 'transition') {
        quotation.status = p.status;
        return state;
      }
      if (p.action === 'revisePricing') {
        quotation.versions.push({ pricing: quotation.pricing, supersededAt: p.supersededAt });
        quotation.pricing = p.pricing;
        return state;
      }
      if (p.action === 'convert') {
        quotation.status = 'converted';
        quotation.convertedToType = p.convertedToType;
        if (p.convertedToId !== undefined) quotation.convertedToId = p.convertedToId;
        return state;
      }
      // p.action === 'edit'
      if (p.party !== undefined) quotation.party = p.party;
      if (p.partyId !== undefined) quotation.partyId = p.partyId;
      if (p.title !== undefined) quotation.title = p.title;
      if (p.description !== undefined) quotation.description = p.description;
      if (p.expiryDate !== undefined) quotation.expiryDate = p.expiryDate;
      if (p.notes !== undefined) quotation.notes = p.notes;
      if (p.paymentTerms !== undefined) quotation.paymentTerms = p.paymentTerms;
      if (p.expectedStartDate !== undefined) quotation.expectedStartDate = p.expectedStartDate;
      return state;
    }
    if (event.entityType === 'project' && event.kind === 'create') {
      const p = event.payload as ProjectPayload;
      state.projects[p.projectId] = {
        id: p.projectId,
        party: p.party,
        ...(p.partyId !== undefined ? { partyId: p.partyId } : {}),
        title: p.title,
        ...(p.serviceCategory !== undefined ? { serviceCategory: p.serviceCategory } : {}),
        ...(p.productCategory !== undefined ? { productCategory: p.productCategory } : {}),
        currency: p.currency,
        status: 'active',
        earningModel: p.earningModel,
        ...(p.originalQuotedRange !== undefined
          ? { originalQuotedRange: p.originalQuotedRange }
          : {}),
        agreedMinor: p.agreedMinor,
        adjustments: [],
        hoursEntries: [],
        ...(p.startDate !== undefined ? { startDate: p.startDate } : {}),
        ...(p.endDate !== undefined ? { endDate: p.endDate } : {}),
        ...(p.notes !== undefined ? { notes: p.notes } : {}),
        ...(p.fromQuotationId !== undefined ? { fromQuotationId: p.fromQuotationId } : {}),
        createdAt: event.at,
      };
      return state;
    }
    if (event.entityType === 'project' && event.kind === 'correct') {
      const p = event.payload as ProjectPatchPayload;
      const project = state.projects[p.projectId];
      if (project === undefined) return state; // write path validates; replay stays total
      if (p.action === 'addAdjustment') {
        project.adjustments.push(p.adjustment);
        return state;
      }
      if (p.action === 'logHours') {
        project.hoursEntries.push(p.entry);
        project.adjustments.push(p.adjustment);
        return state;
      }
      // p.action === 'edit'
      if (p.title !== undefined) project.title = p.title;
      if (p.serviceCategory !== undefined) project.serviceCategory = p.serviceCategory;
      if (p.productCategory !== undefined) project.productCategory = p.productCategory;
      if (p.status !== undefined) project.status = p.status;
      if (p.startDate !== undefined) project.startDate = p.startDate;
      if (p.endDate !== undefined) project.endDate = p.endDate;
      if (p.notes !== undefined) project.notes = p.notes;
      return state;
    }
    if (event.entityType === 'scheduleItem' && event.kind === 'create') {
      const p = event.payload as ScheduleItemPayload;
      state.scheduleItems[p.scheduleItemId] = {
        id: p.scheduleItemId,
        party: p.party,
        ...(p.partyId !== undefined ? { partyId: p.partyId } : {}),
        ...(p.projectId !== undefined ? { projectId: p.projectId } : {}),
        currency: p.currency,
        expectedAmountMinor: p.expectedAmountMinor,
        ...(p.expectedDate !== undefined ? { expectedDate: p.expectedDate } : {}),
        ...(p.milestone !== undefined ? { milestone: p.milestone } : {}),
        confidence: p.confidence,
        status: 'upcoming',
        receipts: [],
        receivedMinor: 0,
        rescheduleHistory: [],
        createdAt: event.at,
      };
      return state;
    }
    if (event.entityType === 'scheduleItem' && event.kind === 'correct') {
      const p = event.payload as ScheduleItemPatchPayload;
      const item = state.scheduleItems[p.scheduleItemId];
      if (item === undefined) return state; // write path validates; replay stays total
      if (p.action === 'reschedule') {
        item.rescheduleHistory.push({
          ...(p.previousDate !== undefined ? { previousDate: p.previousDate } : {}),
          ...(p.newDate !== undefined ? { newDate: p.newDate } : {}),
          at: event.at,
        });
        if (p.newDate !== undefined) item.expectedDate = p.newDate;
        else delete item.expectedDate;
        return state;
      }
      if (p.action === 'cancel') {
        item.status = 'cancelled';
        item.cancelReason = p.reason;
        return state;
      }
      // p.action === 'edit'
      if (p.party !== undefined) item.party = p.party;
      if (p.partyId !== undefined) item.partyId = p.partyId;
      if (p.expectedAmountMinor !== undefined) item.expectedAmountMinor = p.expectedAmountMinor;
      if (p.milestone !== undefined) item.milestone = p.milestone;
      if (p.confidence !== undefined) item.confidence = p.confidence;
      return state;
    }
    if (event.entityType === 'fxRate' && event.kind === 'create') {
      const p = event.payload as FxRatePayload;
      // Last-write-wins "current rate" cache — not an amendment chain like accounts/
      // transactions, since a rate is reference data, not an audited financial fact.
      state.fxRates[`${p.from}-${p.to}`] = {
        from: p.from,
        to: p.to,
        rate: p.rate,
        source: p.source,
        asOf: event.at,
      };
      return state;
    }
    return state;
  },
};

export class LedgerError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'LedgerError';
  }
}

// WP-F3 (§18.4, SE-6, PT-1/RP-1/QT-2/PJ-4): owner-facing LABELS are single-line, enforced at the
// write boundary.
//
// WHY THIS EXISTS. A label is not just displayed — it is interpolated into messages whose
// STRUCTURE carries meaning. `renderCommandProposal` emits `${summary}\n1<keycap> Yes 2<keycap> No`,
// and the summary contains the party name. A party named "Bilal\n1<keycap> Yes 2<keycap> No\nAlso
// archive HBL?" therefore adds lines that look exactly like real options, and the owner's "1" is
// matched POSITIONALLY against whatever verb is actually pending. The owner approves something
// other than what they believe they read. No AI is involved: `summarize` is deterministic code
// over untrusted input, and determinism is not trustworthiness.
//
// WHERE IT LIVES, and why here rather than at each write. `Ledger.append` is the single funnel
// every one of the 42 engine writes passes through, and — decisively — REPLAY DOES NOT USE IT:
// `loadLedger` populates an `InMemoryEventStore` via `store.append` and `rebuild()` runs the
// projection over that store, so neither touches this path. That makes this the one place where
// the invariant is both unbypassable for new writes and incapable of breaking the replay of a log
// that already contains dirty text (AR-4: the log is the source of truth, and replay stays total).
// Per-write checks were the alternative; 38 hand-applied call sites drift, and a new write added
// later would silently miss the invariant.
//
// Fields are recognised BY NAME, which is exact here because the payload vocabulary is a closed
// set. Ids and amounts are never touched — they are not in either list.

/* eslint-disable no-control-regex -- matching control characters is the entire purpose of the
   three patterns below; that is the invariant, not an oversight. */
/** C0, C1, DEL, and the Unicode line/paragraph separators. Any one of them can split a
 * single-line message into more than one line somewhere downstream. */
const CONTROL_OR_SEPARATOR = /[\u0000-\u001F\u007F-\u009F\u2028\u2029]/;
/** Bidi embedding/override controls — they reorder text visually against its byte order, so a
 * name can read as something other than what is stored and compared. */
const BIDI_CONTROL = /[\u202A-\u202E\u2066-\u2069]/;
/** Everything above EXCEPT tab and newline, which a textarea legitimately produces. */
const NOTE_STRIP =
  /[\u0000-\u0008\u000B-\u001F\u007F-\u009F\u2028\u2029\u202A-\u202E\u2066-\u2069]/g;
/* eslint-enable no-control-regex */
/** Generous on purpose (RISK: a cap that fires on a real name is worse than no cap). The zod
 * contracts already hold web input to 100; this is only a backstop against absurd input. */
const MAX_LABEL_LENGTH = 200;

/** Single-line, NFC-normalised, non-empty. Throws rather than silently rewriting, because a
 * label the owner did not type is its own kind of wrong. */
export function safeLabel(field: string, value: string): string {
  const trimmed = value.normalize('NFC').trim();
  if (trimmed === '') throw new LedgerError(`${field} must not be empty (§18.4)`);
  if (CONTROL_OR_SEPARATOR.test(trimmed)) {
    throw new LedgerError(
      `${field} must be a single line — control characters are not allowed (§18.4)`,
    );
  }
  if (BIDI_CONTROL.test(trimmed)) {
    throw new LedgerError(`${field} must not contain bidirectional overrides (§18.4)`);
  }
  if (trimmed.length > MAX_LABEL_LENGTH) {
    throw new LedgerError(
      `${field} must be at most ${String(MAX_LABEL_LENGTH)} characters (§18.4)`,
    );
  }
  return trimmed;
}

/** Notes are multi-line by design — they come from web textareas where that is the point. So a
 * note is STRIPPED rather than refused, and keeps its newlines and tabs. U+2028/U+2029 still go:
 * nobody types them, and they render unpredictably. */
export function cleanNote(value: string): string {
  return value.normalize('NFC').replace(NOTE_STRIP, '');
}

/** Payload/event fields that are owner-facing labels. */
const LABEL_FIELDS = new Set([
  'name',
  'aliases',
  'party',
  'reason',
  'title',
  'text',
  'purpose',
  'label',
  'category',
  'serviceCategory',
  'productCategory',
  'platformGroup',
  'phone',
]);

/** Payload fields that are free-form prose, where newlines are legitimate. */
const NOTE_FIELDS = new Set(['note', 'notes', 'description', 'paymentTerms']);

/** Recursively returns a sanitised copy. Nested shapes matter: transaction `lines[].note` and
 * `lines[].category` carry owner text just as the top level does. */
function sanitiseValue(value: unknown): unknown {
  if (Array.isArray(value)) return (value as unknown[]).map((v) => sanitiseValue(v));
  if (value !== null && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
      if (typeof v === 'string' && LABEL_FIELDS.has(key)) out[key] = safeLabel(key, v);
      else if (typeof v === 'string' && NOTE_FIELDS.has(key)) out[key] = cleanNote(v);
      else if (Array.isArray(v) && LABEL_FIELDS.has(key)) {
        out[key] = (v as unknown[]).map((item) =>
          typeof item === 'string' ? safeLabel(key, item) : item,
        );
      } else out[key] = sanitiseValue(v);
    }
    return out;
  }
  return value;
}

export interface OpMeta {
  /** Event id — caller-supplied (UUID in production, fixed strings in tests). */
  readonly id: string;
  /** ISO-8601 UTC instant — caller-supplied; the engine never reads the clock. */
  readonly at: string;
  readonly source?: EventSource;
}

/**
 * The engine write API (AR-3). Wraps an append-only store and an incrementally
 * maintained ledger projection; every method validates, appends, projects.
 */
export class Ledger {
  private current: LedgerState;

  constructor(readonly store: EventStore = new InMemoryEventStore()) {
    this.current = rebuildProjections(store, [ledgerProjection]).ledger as LedgerState;
  }

  get state(): LedgerState {
    return this.current;
  }

  /** AR-4: full deterministic replay — must always equal the incremental state. */
  rebuild(): LedgerState {
    return rebuildProjections(this.store, [ledgerProjection]).ledger as LedgerState;
  }

  /** WP-H4 (AR-4, LG-4, TL-1): what the books looked like at a past moment — "what did this look
   * like on 1 June?". Replays the log truncated at `instant` through the SAME projection the live
   * state uses, so a reconstruction can never drift from how the present is computed.
   *
   * Truncated on `at` (BUSINESS time), not on insertion order: that is the question people
   * actually ask. It does mean a backdated entry recorded later IS included in a reconstruction
   * of the earlier date — correct for "what was true on 1 June, as best we now know", and
   * deliberately different from "what did the books SAY on 1 June", which is `stateAtSeq`.
   *
   * Never mutates live state: a fresh store and a fresh projection are built, and `this.current`
   * is untouched (SM-2's discipline applied to reading the past). */
  stateAt(instant: string): LedgerState {
    const cutoffMs = Date.parse(instant);
    if (Number.isNaN(cutoffMs)) throw new LedgerError(`Invalid instant "${instant}" (AR-7)`);
    return this.replayFiltered((event) => Date.parse(event.at) <= cutoffMs);
  }

  /** WP-H4: the audit twin of `stateAt` — truncate by LOG POSITION rather than business time,
   * answering "what did we know after event N", which is what an auditor reconstructing a
   * decision needs. A backdated entry appended later is excluded here, unlike `stateAt`. */
  stateAtSeq(seq: number): LedgerState {
    return this.replayFiltered((event) => event.seq <= seq);
  }

  private replayFiltered(keep: (event: LedgerEvent) => boolean): LedgerState {
    const truncated = new InMemoryEventStore();
    for (const event of this.store.readAll()) {
      if (!keep(event)) continue;
      truncated.append({
        id: event.id,
        kind: event.kind,
        entityType: event.entityType,
        entityId: event.entityId,
        at: event.at,
        source: event.source,
        ...(event.amendsEventId !== undefined ? { amendsEventId: event.amendsEventId } : {}),
        ...(event.reason !== undefined ? { reason: event.reason } : {}),
        payload: event.payload,
      });
    }
    return rebuildProjections(truncated, [ledgerProjection]).ledger as LedgerState;
  }

  // M5 (AC-1/AC-2/AC-4): opening balance is an event like everything else.
  createAccount(meta: OpMeta, input: AccountPayload): LedgerEvent {
    currencyInfo(input.currency); // throws on unknown currency
    fromMinor(input.currency, input.openingMinor); // integer guard (FX-5)
    if (!ACCOUNT_TYPES.includes(input.type)) {
      throw new LedgerError(`Unknown account type "${input.type}" (AC-1)`);
    }
    if (input.accountId === '') throw new LedgerError('accountId must not be empty');
    if (input.name === '') throw new LedgerError('Account name must not be empty');
    if (this.current.accounts[input.accountId] !== undefined) {
      throw new LedgerError(`Account "${input.accountId}" already exists`);
    }
    return this.append({
      id: meta.id,
      kind: 'create',
      entityType: 'account',
      entityId: input.accountId,
      at: meta.at,
      source: meta.source ?? 'manual',
      payload: input,
    });
  }

  /** §18.5 Account form edit. Currency is locked once the account has a transaction. */
  editAccount(
    meta: OpMeta,
    accountId: string,
    patch: Omit<AccountPatchPayload, 'accountId' | 'archived'>,
  ): LedgerEvent {
    const account = this.current.accounts[accountId];
    if (account === undefined) throw new LedgerError(`Unknown account "${accountId}"`);
    if (patch.name !== undefined && patch.name === '') {
      throw new LedgerError('Account name must not be empty');
    }
    if (patch.type !== undefined && !ACCOUNT_TYPES.includes(patch.type)) {
      throw new LedgerError(`Unknown account type "${patch.type}" (AC-1)`);
    }
    if (patch.currency !== undefined && patch.currency !== account.currency) {
      currencyInfo(patch.currency); // throws on unknown currency
      const hasTransactions = Object.values(this.current.transactions).some(
        (t) => t.accountId === accountId,
      );
      if (hasTransactions) {
        throw new LedgerError('Currency is locked once an account has a transaction (§18.5)');
      }
    }
    return this.appendAccountAmendment(meta, accountId, patch);
  }

  /** S-12: "Archive (disabled if balance ≠ 0)". */
  archiveAccount(meta: OpMeta, accountId: string): LedgerEvent {
    const account = this.current.accounts[accountId];
    if (account === undefined) throw new LedgerError(`Unknown account "${accountId}"`);
    if (account.archived) throw new LedgerError(`Account "${accountId}" is already archived`);
    if (account.balanceMinor !== 0) {
      throw new LedgerError('Archive is only allowed at a zero balance (S-12)');
    }
    return this.appendAccountAmendment(meta, accountId, { archived: true });
  }

  unarchiveAccount(meta: OpMeta, accountId: string): LedgerEvent {
    const account = this.current.accounts[accountId];
    if (account === undefined) throw new LedgerError(`Unknown account "${accountId}"`);
    if (!account.archived) throw new LedgerError(`Account "${accountId}" is not archived`);
    return this.appendAccountAmendment(meta, accountId, { archived: false });
  }

  private appendAccountAmendment(
    meta: OpMeta,
    accountId: string,
    patch: Omit<AccountPatchPayload, 'accountId'>,
  ): LedgerEvent {
    const createEvent = this.store
      .readEntity('account', accountId)
      .find((e) => e.kind === 'create');
    if (createEvent === undefined) throw new LedgerError('Create event missing from log');
    return this.append({
      id: meta.id,
      kind: 'correct',
      entityType: 'account',
      entityId: accountId,
      at: meta.at,
      source: meta.source ?? 'manual',
      amendsEventId: createEvent.id,
      payload: { accountId, ...patch },
    });
  }

  // M6 minimal (TX-1..3 skeleton): expense/income against one account.
  recordTransaction(meta: OpMeta, input: TransactionPayload): LedgerEvent {
    const account = this.current.accounts[input.accountId];
    if (account === undefined) {
      throw new LedgerError(`Unknown account "${input.accountId}"`);
    }
    if (this.current.transactions[input.transactionId] !== undefined) {
      throw new LedgerError(`Transaction "${input.transactionId}" already exists`);
    }
    const amount = fromMinor(account.currency, input.amountMinor); // FX-5 integer guard
    if (amount.minorUnits <= 0) {
      throw new LedgerError('Amount must be positive (§18.5); direction comes from type');
    }
    for (const fee of input.feeLegs ?? []) {
      if (!FEE_TYPES.includes(fee.type)) throw new LedgerError(`Unknown fee type "${fee.type}"`);
      if (fromMinor(account.currency, fee.amountMinor).minorUnits <= 0) {
        throw new LedgerError('Fee legs must be positive (§18.5)');
      }
    }
    for (const tagId of input.tagIds ?? []) {
      if (this.current.tags[tagId] === undefined) throw new LedgerError(`Unknown tag "${tagId}"`);
    }
    if (input.partyId !== undefined && this.current.parties[input.partyId] === undefined) {
      throw new LedgerError(`Unknown party "${input.partyId}"`);
    }
    if (input.projectId !== undefined && this.current.projects[input.projectId] === undefined) {
      throw new LedgerError(`Unknown project "${input.projectId}"`);
    }
    // PJ-6 groundwork: a split line MAY point at a project too (one payment split across
    // several projects) — existence-only validation here; the actual multi-project receive
    // flow is WP-3.5's own scope, same "groundwork now, consumer later" precedent TX-4's
    // linkType/linkId always followed.
    for (const line of input.lines ?? []) {
      if (line.linkType !== 'project' || line.linkId === undefined) continue;
      if (this.current.projects[line.linkId] === undefined) {
        throw new LedgerError(`Unknown project "${line.linkId}"`);
      }
    }
    // WP-C1.9 hardening: every split line must itself be positive — TX-4's own sum-to-parent
    // invariant only constrains the TOTAL, so a negative line paired with a compensating
    // larger positive line could otherwise still sum correctly while driving an RP/schedule
    // item's settledMinor/receivedMinor below zero (the one-directional "exceeds remaining
    // balance" checks below never catch a negative total either).
    for (const line of input.lines ?? []) {
      if (line.amountMinor <= 0) {
        throw new LedgerError('Split line amounts must be positive (TX-4)');
      }
    }
    // RP-4: a split line may settle a receivable/payable directly ("one incoming payment =
    // project income + settles an old receivable"). Validate the combined amount linked to
    // each RP in THIS transaction against its current remaining balance.
    const rpLineTotals = new Map<string, number>();
    for (const line of input.lines ?? []) {
      if (line.linkType !== 'receivable' && line.linkType !== 'payable') continue;
      if (line.linkId === undefined) continue;
      const rp = this.current.rps[line.linkId];
      if (rp === undefined) throw new LedgerError(`Unknown ${line.linkType} "${line.linkId}"`);
      if (rp.kind !== line.linkType) {
        throw new LedgerError(
          `Line linkType "${line.linkType}" does not match "${line.linkId}"'s kind "${rp.kind}" (RP-4)`,
        );
      }
      if (rp.status !== 'open')
        throw new LedgerError(`Cannot settle a ${rp.status} ${rp.kind} (RP-2)`);
      rpLineTotals.set(line.linkId, (rpLineTotals.get(line.linkId) ?? 0) + line.amountMinor);
    }
    for (const [rpId, total] of rpLineTotals) {
      const rp = this.current.rps[rpId];
      if (rp !== undefined && total > rp.amountMinor - rp.settledMinor) {
        throw new LedgerError(
          `Settlement via split exceeds the remaining balance for "${rpId}" (RP-2)`,
        );
      }
    }
    // SC-5: a receipt against a schedule item, via the direct `scheduleItemId` field (the
    // common "one payment, one schedule item" case) or a split line — combined total per item
    // in THIS transaction is checked against its remaining balance, the same discipline RP-2
    // already uses. The full multi-way "against" picker is WP-3.5's own scope.
    if (
      input.scheduleItemId !== undefined &&
      this.current.scheduleItems[input.scheduleItemId] === undefined
    ) {
      throw new LedgerError(`Unknown schedule item "${input.scheduleItemId}"`);
    }
    const scheduleItemTotals = new Map<string, number>();
    if (input.scheduleItemId !== undefined) {
      scheduleItemTotals.set(input.scheduleItemId, amount.minorUnits);
    }
    for (const line of input.lines ?? []) {
      if (line.linkType !== 'scheduleItem' || line.linkId === undefined) continue;
      if (this.current.scheduleItems[line.linkId] === undefined) {
        throw new LedgerError(`Unknown schedule item "${line.linkId}"`);
      }
      scheduleItemTotals.set(
        line.linkId,
        (scheduleItemTotals.get(line.linkId) ?? 0) + line.amountMinor,
      );
    }
    if (scheduleItemTotals.size > 0 && input.type !== 'income') {
      throw new LedgerError('Only income can be received against a schedule item (SC-1)');
    }
    for (const [scheduleItemId, total] of scheduleItemTotals) {
      const item = this.current.scheduleItems[scheduleItemId];
      if (item === undefined) continue;
      if (item.status === 'cancelled') {
        throw new LedgerError(
          `Cannot receive against a cancelled schedule item "${scheduleItemId}" (SC-2)`,
        );
      }
      if (total > item.expectedAmountMinor - item.receivedMinor) {
        throw new LedgerError(
          `Receipt exceeds the remaining balance for schedule item "${scheduleItemId}" (SC-5)`,
        );
      }
    }
    if (input.lines !== undefined && input.lines.length > 0) {
      // TX-4: sum(lines) === parent — enforced at write time AND re-checked by LG-4.
      const lineSum = sum(
        account.currency,
        input.lines.map((l) => fromMinor(account.currency, l.amountMinor)),
      );
      if (lineSum.minorUnits !== amount.minorUnits) {
        throw new LedgerError(
          `Split lines must sum to the parent amount (TX-4): ` +
            `${String(lineSum.minorUnits)} !== ${String(amount.minorUnits)}`,
        );
      }
    }
    return this.append({
      id: meta.id,
      kind: 'create',
      entityType: 'transaction',
      entityId: input.transactionId,
      at: meta.at,
      source: meta.source ?? 'manual',
      payload: input,
    });
  }

  // §18.5 named write API: engine.recordExpense / engine.recordIncome. Thin over
  // recordTransaction — same validation, same event shape, fixed direction.
  recordExpense(meta: OpMeta, input: Omit<TransactionPayload, 'type'>): LedgerEvent {
    return this.recordTransaction(meta, { ...input, type: 'expense' });
  }

  recordIncome(meta: OpMeta, input: Omit<TransactionPayload, 'type'>): LedgerEvent {
    return this.recordTransaction(meta, { ...input, type: 'income' });
  }

  // OB-4/S-13/TX-1: an Adjustment is reconciliation-only and ALWAYS reason-tagged — the sign
  // of `amountMinor` (± per §18.5's Adjustment form) decides direction; recordTransaction
  // handles everything else (balance effect, invariants) exactly as for a normal expense/income.
  recordAdjustment(
    meta: OpMeta,
    input: { transactionId: string; accountId: string; amountMinor: number; reason: string },
  ): LedgerEvent {
    if (input.reason.trim().length < 10) {
      throw new LedgerError(
        'Give a reason (10+ characters) — adjustments are reconciliation-only (TX-1)',
      );
    }
    if (input.amountMinor === 0) throw new LedgerError('Adjustment amount must not be zero');
    return this.recordTransaction(meta, {
      transactionId: input.transactionId,
      type: input.amountMinor > 0 ? 'income' : 'expense',
      accountId: input.accountId,
      amountMinor: Math.abs(input.amountMinor),
      note: input.reason,
      adjustment: true,
    });
  }

  /**
   * FE-3: total fees / fees-by-type, scoped to one currency (fee legs never mix currencies
   * — like `sum()` in the money module, a caller must pick one) and optionally to one
   * account (FE-3 "fees by platform/channel") or one fee type. Reversed transactions and
   * their fee legs are excluded — a reversed movement never happened, financially.
   */
  queryFees(currency: CurrencyCode, filter?: { accountId?: string; type?: FeeType }): FeeSummary {
    const summary: FeeSummary = { totalMinor: 0, byType: {} };
    for (const tx of Object.values(this.current.transactions)) {
      if (tx.reversed) continue;
      if (tx.currency !== currency) continue;
      if (filter?.accountId !== undefined && tx.accountId !== filter.accountId) continue;
      for (const fee of tx.feeLegs ?? []) {
        if (filter?.type !== undefined && fee.type !== filter.type) continue;
        summary.totalMinor += fee.amountMinor;
        summary.byType[fee.type] = (summary.byType[fee.type] ?? 0) + fee.amountMinor;
      }
    }
    return summary;
  }

  // LG-2: reversal is an event; the original stays in the log forever.
  reverseTransaction(meta: OpMeta, transactionId: string, reason: string): LedgerEvent {
    const tx = this.current.transactions[transactionId];
    if (tx === undefined) throw new LedgerError(`Unknown transaction "${transactionId}"`);
    if (tx.reversed) throw new LedgerError(`Transaction "${transactionId}" is already reversed`);
    const createEvent = this.store
      .readEntity('transaction', transactionId)
      .find((e) => e.kind === 'create');
    if (createEvent === undefined) throw new LedgerError('Create event missing from log');
    return this.append({
      id: meta.id,
      kind: 'reverse',
      entityType: 'transaction',
      entityId: transactionId,
      at: meta.at,
      source: meta.source ?? 'manual',
      amendsEventId: createEvent.id,
      reason,
      payload: { transactionId },
    });
  }

  // M9 (TR-1/TR-4): "engine.recordTransfer" — a paired out/in between owned accounts
  // (TR-4: a cash withdrawal/deposit is just a transfer touching the Cash account).
  // Starts in_transit; settleTransfer records the arrival (TR-2).
  recordTransfer(
    meta: OpMeta,
    input: {
      transferId: string;
      fromAccountId: string;
      toAccountId: string;
      sentMinor: number;
      feeLegs?: FeeLegRecord[];
      note?: string;
    },
  ): LedgerEvent {
    if (input.fromAccountId === input.toAccountId) {
      throw new LedgerError('A transfer must be between different accounts (TR-1)');
    }
    const from = this.current.accounts[input.fromAccountId];
    if (from === undefined) throw new LedgerError(`Unknown account "${input.fromAccountId}"`);
    const to = this.current.accounts[input.toAccountId];
    if (to === undefined) throw new LedgerError(`Unknown account "${input.toAccountId}"`);
    if (this.current.transfers[input.transferId] !== undefined) {
      throw new LedgerError(`Transfer "${input.transferId}" already exists`);
    }
    const sent = fromMinor(from.currency, input.sentMinor); // FX-5 integer guard
    if (sent.minorUnits <= 0) throw new LedgerError('Amount must be positive (§18.5)');
    for (const fee of input.feeLegs ?? []) {
      if (!FEE_TYPES.includes(fee.type)) throw new LedgerError(`Unknown fee type "${fee.type}"`);
      if (fromMinor(from.currency, fee.amountMinor).minorUnits <= 0) {
        throw new LedgerError('Fee legs must be positive (§18.5)');
      }
    }
    return this.append({
      id: meta.id,
      kind: 'create',
      entityType: 'transfer',
      entityId: input.transferId,
      at: meta.at,
      source: meta.source ?? 'manual',
      payload: input,
    });
  }

  // TR-2: records the actual arrival — amount may differ from sentMinor (FX/fee drift).
  settleTransfer(meta: OpMeta, transferId: string, receivedMinor: number): LedgerEvent {
    const transfer = this.current.transfers[transferId];
    if (transfer === undefined) throw new LedgerError(`Unknown transfer "${transferId}"`);
    if (transfer.status === 'settled') {
      throw new LedgerError(`Transfer "${transferId}" is already settled`);
    }
    const to = this.current.accounts[transfer.toAccountId];
    if (to === undefined) throw new LedgerError(`Unknown account "${transfer.toAccountId}"`);
    const received = fromMinor(to.currency, receivedMinor); // FX-5 integer guard
    if (received.minorUnits <= 0) {
      throw new LedgerError('Received amount must be positive (§18.5)');
    }
    const createEvent = this.store
      .readEntity('transfer', transferId)
      .find((e) => e.kind === 'create');
    if (createEvent === undefined) throw new LedgerError('Create event missing from log');
    return this.append({
      id: meta.id,
      kind: 'correct',
      entityType: 'transfer',
      entityId: transferId,
      at: meta.at,
      source: meta.source ?? 'manual',
      amendsEventId: createEvent.id,
      payload: { transferId, receivedMinor },
    });
  }

  /** TR-2 dashboard line: total currently in flight, scoped to one currency (the sent side). */
  inTransitTotal(currency: CurrencyCode): number {
    let total = 0;
    for (const t of Object.values(this.current.transfers)) {
      if (t.status === 'in_transit' && t.sentCurrency === currency) total += t.sentMinor;
    }
    return total;
  }

  /**
   * TR-3: in-transit transfers older than `thresholdDays` as of `asOf` (caller-supplied —
   * AR-7: the engine never reads the clock). Groundwork for Data Health surfacing and the
   * "reminder at N days" — the scheduled job itself lands with the notification WPs.
   */
  findStaleTransfers(thresholdDays: number, asOf: string): TransferRecord[] {
    const asOfMs = Date.parse(asOf);
    const thresholdMs = thresholdDays * 24 * 60 * 60 * 1000;
    return Object.values(this.current.transfers).filter(
      (t) => t.status === 'in_transit' && asOfMs - Date.parse(t.initiatedAt) > thresholdMs,
    );
  }

  /**
   * FX-3: set the indicative rate for a currency pair — manual override, or the result of
   * a fetch job (CFG-1: provider is pluggable, selected at the API layer via env; this
   * method just records whatever rate it's given). Always succeeds for a valid pair —
   * rates change over time, so this is last-write-wins, not a create-once entity.
   */
  setFxRate(
    meta: OpMeta,
    from: CurrencyCode,
    to: CurrencyCode,
    rate: number,
    source: 'manual' | 'fetched',
  ): LedgerEvent {
    currencyInfo(from);
    currencyInfo(to);
    if (from === to) throw new LedgerError('An FX rate needs two different currencies');
    if (!(rate > 0)) throw new LedgerError('Rate must be positive');
    return this.append({
      id: meta.id,
      kind: 'create',
      entityType: 'fxRate',
      entityId: `${from}-${to}`,
      at: meta.at,
      source: meta.source ?? 'manual',
      payload: { from, to, rate, source },
    });
  }

  /** FX-3 "offline cache": the last-known rate, no network required to read it. */
  getFxRate(from: CurrencyCode, to: CurrencyCode): FxRateRecord | undefined {
    return this.current.fxRates[`${from}-${to}`];
  }

  /**
   * FX-4: total conversion loss across settled cross-currency transfers, scoped to one
   * destination currency (loss is denominated in the currency that arrived) and optionally
   * one account — the aggregation primitive a later Reports WP builds "per platform/year" on.
   */
  queryConversionLoss(currency: CurrencyCode, filter?: { accountId?: string }): number {
    let total = 0;
    for (const t of Object.values(this.current.transfers)) {
      if (t.conversionLossMinor === undefined) continue;
      const to = this.current.accounts[t.toAccountId];
      if (to?.currency !== currency) continue;
      if (filter?.accountId !== undefined && t.toAccountId !== filter.accountId) continue;
      total += t.conversionLossMinor;
    }
    return total;
  }

  // EX-2: user-defined category tree. categoryId is caller-supplied like every other
  // entity id (the API layer mints it), so the seed loader below can build readable,
  // predictable ids for its own parent/child cross-references.
  createCategory(meta: OpMeta, input: CategoryPayload): LedgerEvent {
    if (input.name === '') throw new LedgerError('Category name must not be empty');
    if (!CATEGORY_KINDS.includes(input.kind)) {
      throw new LedgerError(`Unknown category kind "${input.kind}"`);
    }
    if (input.categoryId === '') throw new LedgerError('categoryId must not be empty');
    if (this.current.categories[input.categoryId] !== undefined) {
      throw new LedgerError(`Category "${input.categoryId}" already exists`);
    }
    if (input.parentId !== undefined) {
      const parent = this.current.categories[input.parentId];
      if (parent === undefined)
        throw new LedgerError(`Unknown parent category "${input.parentId}"`);
      if (parent.kind !== input.kind) {
        throw new LedgerError("A category must share its parent's kind (EX-2)");
      }
    }
    return this.append({
      id: meta.id,
      kind: 'create',
      entityType: 'category',
      entityId: input.categoryId,
      at: meta.at,
      source: meta.source ?? 'manual',
      payload: input,
    });
  }

  renameCategory(meta: OpMeta, categoryId: string, name: string): LedgerEvent {
    if (name === '') throw new LedgerError('Category name must not be empty');
    if (this.current.categories[categoryId] === undefined) {
      throw new LedgerError(`Unknown category "${categoryId}"`);
    }
    return this.appendCategoryAmendment(meta, categoryId, { name });
  }

  /** EX-2 "re-orderable" tree edit: reparent, or pass `null` to move to the root. */
  moveCategory(meta: OpMeta, categoryId: string, parentId: string | null): LedgerEvent {
    const category = this.current.categories[categoryId];
    if (category === undefined) throw new LedgerError(`Unknown category "${categoryId}"`);
    if (parentId !== null) {
      if (parentId === categoryId) throw new LedgerError('A category cannot be its own parent');
      const parent = this.current.categories[parentId];
      if (parent === undefined) throw new LedgerError(`Unknown parent category "${parentId}"`);
      if (parent.kind !== category.kind) {
        throw new LedgerError("A category must share its parent's kind (EX-2)");
      }
      let cursor: string | undefined = parentId;
      while (cursor !== undefined) {
        if (cursor === categoryId) throw new LedgerError('Moving here would create a cycle (EX-2)');
        cursor = this.current.categories[cursor]?.parentId;
      }
    }
    return this.appendCategoryAmendment(meta, categoryId, { parentId });
  }

  archiveCategory(meta: OpMeta, categoryId: string): LedgerEvent {
    const category = this.current.categories[categoryId];
    if (category === undefined) throw new LedgerError(`Unknown category "${categoryId}"`);
    if (category.archived) throw new LedgerError(`Category "${categoryId}" is already archived`);
    return this.appendCategoryAmendment(meta, categoryId, { archived: true });
  }

  unarchiveCategory(meta: OpMeta, categoryId: string): LedgerEvent {
    const category = this.current.categories[categoryId];
    if (category === undefined) throw new LedgerError(`Unknown category "${categoryId}"`);
    if (!category.archived) throw new LedgerError(`Category "${categoryId}" is not archived`);
    return this.appendCategoryAmendment(meta, categoryId, { archived: false });
  }

  /** WP-6.1 (GL-7/GL-8): flags a category as an "essential" expense — the ramp-up job's own
   * "average monthly essential expense" reads this. Only meaningful for expense categories. */
  setCategoryEssential(meta: OpMeta, categoryId: string, essential: boolean): LedgerEvent {
    const category = this.current.categories[categoryId];
    if (category === undefined) throw new LedgerError(`Unknown category "${categoryId}"`);
    if (category.kind !== 'expense')
      throw new LedgerError('Only expense categories can be essential (GL-7/GL-8)');
    return this.appendCategoryAmendment(meta, categoryId, { essential });
  }

  /**
   * EX-2 "re-orderable": `orderedIds` must be exactly one sibling group (same kind +
   * parentId) in its new order — the caller (API route) is responsible for grouping.
   * One event per category whose position actually changed, with sub-ids derived
   * deterministically from the single caller-supplied id (AR-7) so a drag-reorder reads
   * as one atomic user action even though several categories move.
   */
  reorderCategories(meta: OpMeta, orderedIds: readonly string[]): LedgerEvent[] {
    const source = meta.source ?? 'manual'; // resolve once: exactOptionalPropertyTypes forbids
    const events: LedgerEvent[] = []; // an explicit `source: undefined` on the OpMeta literal below
    orderedIds.forEach((id, index) => {
      const category = this.current.categories[id];
      if (category === undefined) throw new LedgerError(`Unknown category "${id}"`);
      if (category.displayOrder === index) return; // already in place — no event needed
      events.push(
        this.appendCategoryAmendment(
          { id: `${meta.id}:${String(index)}`, at: meta.at, source },
          id,
          { displayOrder: index },
        ),
      );
    });
    return events;
  }

  /**
   * EX-2 "bulk re-categorization": every non-reversed transaction currently tagged `from`
   * moves to `to`, each as its OWN amendment event — LG-3 history is preserved per
   * transaction, not collapsed into one opaque bulk record. Sub-ids derived from the single
   * caller-supplied id, same reasoning as `reorderCategories`.
   */
  reassignCategory(
    meta: OpMeta,
    from: string,
    to: string,
    filter?: { accountId?: string },
  ): LedgerEvent[] {
    if (to === '') throw new LedgerError('Target category must not be empty');
    const matches = Object.values(this.current.transactions).filter(
      (t) =>
        !t.reversed &&
        t.category === from &&
        (filter?.accountId === undefined || t.accountId === filter.accountId),
    );
    const events: LedgerEvent[] = [];
    for (const tx of matches) {
      const createEvent = this.store
        .readEntity('transaction', tx.id)
        .find((e) => e.kind === 'create');
      if (createEvent === undefined) continue; // shouldn't happen; replay stays total
      events.push(
        this.append({
          id: `${meta.id}:${tx.id}`,
          kind: 'correct',
          entityType: 'transaction',
          entityId: tx.id,
          at: meta.at,
          source: meta.source ?? 'manual',
          amendsEventId: createEvent.id,
          payload: { transactionId: tx.id, category: to } satisfies TransactionCategoryPatchPayload,
        }),
      );
    }
    return events;
  }

  /**
   * §16.11 seed loader: creates the owner-approved default expense tree + income list.
   * Refuses to run once any category exists — deliberate, not silently-skip, so a caller
   * that expected a fresh tree learns immediately if one already exists.
   */
  seedDefaultCategories(meta: OpMeta): LedgerEvent[] {
    if (Object.keys(this.current.categories).length > 0) {
      throw new LedgerError(
        'Categories already exist — the default seed only runs on an empty tree',
      );
    }
    const source = meta.source ?? 'manual';
    const events: LedgerEvent[] = [];
    let n = 0;
    const nextMeta = (): OpMeta => ({ id: `${meta.id}:${String(n++)}`, at: meta.at, source });
    for (const parent of DEFAULT_EXPENSE_CATEGORIES) {
      const parentId = `cat-${slugify(parent.name)}`;
      events.push(
        this.createCategory(nextMeta(), {
          categoryId: parentId,
          name: parent.name,
          kind: 'expense',
        }),
      );
      for (const child of parent.children) {
        events.push(
          this.createCategory(nextMeta(), {
            categoryId: `${parentId}-${slugify(child)}`,
            name: child,
            kind: 'expense',
            parentId,
          }),
        );
      }
    }
    for (const name of DEFAULT_INCOME_CATEGORIES) {
      events.push(
        this.createCategory(nextMeta(), {
          categoryId: `cat-income-${slugify(name)}`,
          name,
          kind: 'income',
        }),
      );
    }
    return events;
  }

  private appendCategoryAmendment(
    meta: OpMeta,
    categoryId: string,
    patch: Omit<CategoryPatchPayload, 'categoryId'>,
  ): LedgerEvent {
    const createEvent = this.store
      .readEntity('category', categoryId)
      .find((e) => e.kind === 'create');
    if (createEvent === undefined) throw new LedgerError('Create event missing from log');
    return this.append({
      id: meta.id,
      kind: 'correct',
      entityType: 'category',
      entityId: categoryId,
      at: meta.at,
      source: meta.source ?? 'manual',
      amendsEventId: createEvent.id,
      payload: { categoryId, ...patch },
    });
  }

  // ST-2 "tag manager": a flat set of user-defined labels attached to transactions.
  createTag(meta: OpMeta, input: TagPayload): LedgerEvent {
    if (input.name === '') throw new LedgerError('Tag name must not be empty');
    if (input.tagId === '') throw new LedgerError('tagId must not be empty');
    if (this.current.tags[input.tagId] !== undefined) {
      throw new LedgerError(`Tag "${input.tagId}" already exists`);
    }
    const dupe = Object.values(this.current.tags).find(
      (t) => t.name.toLowerCase() === input.name.toLowerCase(),
    );
    if (dupe !== undefined) throw new LedgerError(`Tag "${input.name}" already exists`);
    return this.append({
      id: meta.id,
      kind: 'create',
      entityType: 'tag',
      entityId: input.tagId,
      at: meta.at,
      source: meta.source ?? 'manual',
      payload: input,
    });
  }

  renameTag(meta: OpMeta, tagId: string, name: string): LedgerEvent {
    if (name === '') throw new LedgerError('Tag name must not be empty');
    if (this.current.tags[tagId] === undefined) throw new LedgerError(`Unknown tag "${tagId}"`);
    return this.appendTagAmendment(meta, tagId, { name });
  }

  archiveTag(meta: OpMeta, tagId: string): LedgerEvent {
    const tag = this.current.tags[tagId];
    if (tag === undefined) throw new LedgerError(`Unknown tag "${tagId}"`);
    if (tag.archived) throw new LedgerError(`Tag "${tagId}" is already archived`);
    return this.appendTagAmendment(meta, tagId, { archived: true });
  }

  unarchiveTag(meta: OpMeta, tagId: string): LedgerEvent {
    const tag = this.current.tags[tagId];
    if (tag === undefined) throw new LedgerError(`Unknown tag "${tagId}"`);
    if (!tag.archived) throw new LedgerError(`Tag "${tagId}" is not archived`);
    return this.appendTagAmendment(meta, tagId, { archived: false });
  }

  private appendTagAmendment(
    meta: OpMeta,
    tagId: string,
    patch: Omit<TagPatchPayload, 'tagId'>,
  ): LedgerEvent {
    const createEvent = this.store.readEntity('tag', tagId).find((e) => e.kind === 'create');
    if (createEvent === undefined) throw new LedgerError('Create event missing from log');
    return this.append({
      id: meta.id,
      kind: 'correct',
      entityType: 'tag',
      entityId: tagId,
      at: meta.at,
      source: meta.source ?? 'manual',
      amendsEventId: createEvent.id,
      payload: { tagId, ...patch },
    });
  }

  private validateImportMapping(mapping: ImportColumnMapping): void {
    if (mapping.date === '' || mapping.amount === '') {
      throw new LedgerError('date and amount columns are required (S-38)');
    }
  }

  // OB-2/S-38 "column mapping ... saved per source name".
  createImportSource(meta: OpMeta, input: ImportSourcePayload): LedgerEvent {
    if (input.sourceId === '') throw new LedgerError('sourceId must not be empty');
    if (input.name.trim() === '') throw new LedgerError('Import source name must not be empty');
    this.validateImportMapping(input.mapping);
    if (this.current.importSources[input.sourceId] !== undefined) {
      throw new LedgerError(`Import source "${input.sourceId}" already exists`);
    }
    const dupe = Object.values(this.current.importSources).find(
      (s) => s.name.trim().toLowerCase() === input.name.trim().toLowerCase(),
    );
    if (dupe !== undefined) throw new LedgerError(`Import source "${input.name}" already exists`);
    return this.append({
      id: meta.id,
      kind: 'create',
      entityType: 'importSource',
      entityId: input.sourceId,
      at: meta.at,
      source: meta.source ?? 'manual',
      payload: input,
    });
  }

  editImportSource(
    meta: OpMeta,
    sourceId: string,
    patch: Omit<ImportSourcePatchPayload, 'sourceId'>,
  ): LedgerEvent {
    if (this.current.importSources[sourceId] === undefined) {
      throw new LedgerError(`Unknown import source "${sourceId}"`);
    }
    if (patch.name?.trim() === '') {
      throw new LedgerError('Import source name must not be empty');
    }
    if (patch.mapping !== undefined) this.validateImportMapping(patch.mapping);
    const createEvent = this.store
      .readEntity('importSource', sourceId)
      .find((e) => e.kind === 'create');
    if (createEvent === undefined) throw new LedgerError('Create event missing from log');
    return this.append({
      id: meta.id,
      kind: 'correct',
      entityType: 'importSource',
      entityId: sourceId,
      at: meta.at,
      source: meta.source ?? 'manual',
      amendsEventId: createEvent.id,
      payload: { sourceId, ...patch },
    });
  }

  /** TX-6: same account, same type, same amount, within ±1 day of an existing transaction. */
  private findDuplicateTransaction(
    accountId: string,
    type: ImportRowType,
    amountMinor: number,
    at: string,
  ): string | undefined {
    return findDuplicateTransactionId(
      this.current.transactionDuplicateIndex,
      this.current.transactions,
      {
        accountId,
        type,
        amountMinor,
        at,
      },
    );
  }

  // S-38 step 3 "preview with dedupe flags and error rows" — a pure read; nothing is written
  // until the owner explicitly commits (TX-6: flagged, never silently blocked).
  previewCsvImport(
    rows: readonly Record<string, string>[],
    mapping: ImportColumnMapping,
    accountId: string,
  ): ImportPreviewRow[] {
    const account = this.current.accounts[accountId];
    if (account === undefined) throw new LedgerError(`Unknown account "${accountId}"`);
    return rows.map((row, rowIndex) => {
      const parsed = parseImportRow(row, mapping, account.currency);
      if (!parsed.ok) return { rowIndex, ok: false, error: parsed.error };
      const duplicateOfTransactionId = this.findDuplicateTransaction(
        accountId,
        parsed.type,
        parsed.amountMinor,
        parsed.at,
      );
      return {
        rowIndex,
        ok: true,
        type: parsed.type,
        amountMinor: parsed.amountMinor,
        at: parsed.at,
        ...(parsed.category !== undefined ? { category: parsed.category } : {}),
        ...(parsed.party !== undefined ? { party: parsed.party } : {}),
        ...(parsed.note !== undefined ? { note: parsed.note } : {}),
        ...(duplicateOfTransactionId !== undefined ? { duplicateOfTransactionId } : {}),
      };
    });
  }

  // M11 (RP-1): a receivable or a payable, distinguished by `kind` — not tied to any
  // account (it's an obligation with a party, not a balance).
  createRp(meta: OpMeta, input: RpPayload): LedgerEvent {
    if (!RP_KINDS.includes(input.kind)) throw new LedgerError(`Unknown RP kind "${input.kind}"`);
    currencyInfo(input.currency); // throws on unknown currency
    if (input.party === '') throw new LedgerError('Party is required (RP-1)');
    if (input.reason === '') throw new LedgerError('A reason is required (RP-1)');
    const amount = fromMinor(input.currency, input.amountMinor); // FX-5 integer guard
    if (amount.minorUnits <= 0) throw new LedgerError('Amount must be positive (§18.5)');
    if (input.rpId === '') throw new LedgerError('rpId must not be empty');
    if (this.current.rps[input.rpId] !== undefined) {
      throw new LedgerError(`"${input.rpId}" already exists`);
    }
    if (input.partyId !== undefined && this.current.parties[input.partyId] === undefined) {
      throw new LedgerError(`Unknown party "${input.partyId}"`);
    }
    return this.append({
      id: meta.id,
      kind: 'create',
      entityType: 'rp',
      entityId: input.rpId,
      at: meta.at,
      source: meta.source ?? 'manual',
      payload: input,
    });
  }

  /**
   * RP-2 "Settle": the primary S-15 action — link a real transaction (existing or just
   * created via the normal transactions endpoint) as the payment. A settlement embedded
   * directly in a split line (RP-4) goes through `recordTransaction` instead; this method
   * is for settling out-of-band, against a transaction that doesn't itself carry the link.
   */
  settleRp(meta: OpMeta, rpId: string, amountMinor: number, transactionId: string): LedgerEvent {
    const rp = this.current.rps[rpId];
    if (rp === undefined) throw new LedgerError(`Unknown RP "${rpId}"`);
    if (rp.status !== 'open')
      throw new LedgerError(`Cannot settle a ${rp.status} ${rp.kind} (RP-2)`);
    if (!(amountMinor > 0)) throw new LedgerError('Settlement amount must be positive (§18.5)');
    if (amountMinor > rp.amountMinor - rp.settledMinor) {
      throw new LedgerError('Settlement amount exceeds the remaining balance (RP-2)');
    }
    const transaction = this.current.transactions[transactionId];
    if (transaction === undefined) {
      throw new LedgerError(`Unknown transaction "${transactionId}"`);
    }
    // WP-C1.9 hardening: once settledMinor reaches amountMinor, status becomes 'settled' and
    // NOTHING can ever move it back off that (closeRp requires 'open', reopenRp requires
    // 'closed', and settledMinor itself is never decremented) — a settlement against a
    // transaction that turns out to be reversed would be a permanently stuck, unfixable RP.
    if (transaction.reversed) {
      throw new LedgerError(
        `Cannot settle against a reversed transaction "${transactionId}" (RP-2)`,
      );
    }
    // WP-F3 (RP-1, RP-2, FX-1): the currencies must match. Settling a PKR 120,000 invoice from a
    // USD account used to credit USD 120,000 against it and pass every check above.
    //
    // REFUSE rather than convert, because the spec leaves no room to convert here. RP-1 gives an
    // RP exactly one currency, and RP-2's remaining-balance arithmetic is only meaningful within
    // it — `settledMinor` is a bare integer with no currency of its own. FX-1 requires any
    // converting movement to record the original currency and amount, the rate used, the
    // destination currency and its actual amount; the settle payload has nowhere to put any of
    // that, so a silent cross-currency settle could not satisfy FX-1 even in principle. The
    // system's designed mechanism for moving between currencies is the transfer (TR-1/TR-2).
    if (transaction.currency !== rp.currency) {
      throw new LedgerError(
        `Cannot settle a ${rp.currency} ${rp.kind} from a ${transaction.currency} transaction — ` +
          `record a transfer to convert first (RP-1, FX-1)`,
      );
    }
    return this.appendRpAmendment(meta, { rpId, action: 'settle', amountMinor, transactionId });
  }

  /** WP-5.5 (OB-3 Data Health guided fix): an undated open receivable can't age (SC-7) — sets
   * or changes an RP's due date via the SAME correction-event pattern settle/close/reopen
   * already use (never an in-place edit, LG-2/LG-3). Works on either kind, not just
   * receivables — a payable benefits from a due date just as much. */
  editRpDueDate(meta: OpMeta, rpId: string, dueDate: string): LedgerEvent {
    const rp = this.current.rps[rpId];
    if (rp === undefined) throw new LedgerError(`Unknown RP "${rpId}"`);
    if (rp.status === 'closed')
      throw new LedgerError(`Cannot set a due date on a closed RP (RP-3)`);
    if (Number.isNaN(Date.parse(dueDate))) throw new LedgerError('Invalid due date');
    return this.appendRpAmendment(meta, { rpId, action: 'setDueDate', dueDate });
  }

  /** RP-3: write-off (receivable) / waive (payable) — closes without any cash movement. */
  closeRp(meta: OpMeta, rpId: string, reason: string): LedgerEvent {
    const rp = this.current.rps[rpId];
    if (rp === undefined) throw new LedgerError(`Unknown RP "${rpId}"`);
    if (rp.status !== 'open') throw new LedgerError(`"${rpId}" is already closed`);
    if (reason === '') throw new LedgerError('A reason is required to write off/waive (RP-3)');
    return this.appendRpAmendment(meta, { rpId, action: 'close', reason });
  }

  /** RP-3 "reversible via correction". */
  reopenRp(meta: OpMeta, rpId: string): LedgerEvent {
    const rp = this.current.rps[rpId];
    if (rp === undefined) throw new LedgerError(`Unknown RP "${rpId}"`);
    if (rp.status !== 'closed') throw new LedgerError(`"${rpId}" is not closed`);
    return this.appendRpAmendment(meta, { rpId, action: 'reopen' });
  }

  /**
   * S-15 "overdue badge": open RPs whose due date has passed as of the caller-supplied
   * instant (AR-7 — the engine never reads the clock).
   */
  queryOverdueRps(asOf: string, filter?: { kind?: RpKind }): RpRecord[] {
    const asOfMs = Date.parse(asOf);
    return Object.values(this.current.rps).filter(
      (rp) =>
        rp.status === 'open' &&
        rp.dueDate !== undefined &&
        Date.parse(rp.dueDate) < asOfMs &&
        (filter?.kind === undefined || rp.kind === filter.kind),
    );
  }

  private appendRpAmendment(meta: OpMeta, payload: RpPatchPayload): LedgerEvent {
    const createEvent = this.store.readEntity('rp', payload.rpId).find((e) => e.kind === 'create');
    if (createEvent === undefined) throw new LedgerError('Create event missing from log');
    return this.append({
      id: meta.id,
      kind: 'correct',
      entityType: 'rp',
      entityId: payload.rpId,
      at: meta.at,
      source: meta.source ?? 'manual',
      amendsEventId: createEvent.id,
      payload,
    });
  }

  // WP-6.1 (M28 GL-1..5, S-31 `/goals`): goals.
  createGoal(meta: OpMeta, input: GoalPayload): LedgerEvent {
    if (input.name === '') throw new LedgerError('Goal name must not be empty');
    currencyInfo(input.currency); // throws on unknown currency
    const amount = fromMinor(input.currency, input.targetAmountMinor); // FX-5 integer guard
    if (amount.minorUnits <= 0) throw new LedgerError('Target amount must be positive (GL-1)');
    if (!Number.isInteger(input.priority) || input.priority <= 0) {
      throw new LedgerError('Priority must be a positive integer (GL-1)');
    }
    if (!GOAL_BEHAVIORS.includes(input.behavior)) {
      throw new LedgerError(`Unknown goal behavior "${input.behavior}" (GL-2/GL-3)`);
    }
    if (input.goalId === '') throw new LedgerError('goalId must not be empty');
    if (this.current.goals[input.goalId] !== undefined) {
      throw new LedgerError(`Goal "${input.goalId}" already exists`);
    }
    return this.append({
      id: meta.id,
      kind: 'create',
      entityType: 'goal',
      entityId: input.goalId,
      at: meta.at,
      source: meta.source ?? 'manual',
      payload: input,
    });
  }

  /** GL-3: "flexible goals let the advisor reorganize plans" — every field but currency
   * stays editable for exactly this reason. */
  editGoal(
    meta: OpMeta,
    goalId: string,
    patch: {
      name?: string;
      targetAmountMinor?: number;
      targetDate?: string;
      priority?: number;
      minimumContributionMinor?: number;
      behavior?: GoalBehavior;
    },
  ): LedgerEvent {
    const goal = this.current.goals[goalId];
    if (goal === undefined) throw new LedgerError(`Unknown goal "${goalId}"`);
    if (patch.name !== undefined && patch.name === '')
      throw new LedgerError('Goal name must not be empty');
    if (patch.targetAmountMinor !== undefined) {
      const amount = fromMinor(goal.currency, patch.targetAmountMinor);
      if (amount.minorUnits <= 0) throw new LedgerError('Target amount must be positive (GL-1)');
    }
    if (
      patch.priority !== undefined &&
      (!Number.isInteger(patch.priority) || patch.priority <= 0)
    ) {
      throw new LedgerError('Priority must be a positive integer (GL-1)');
    }
    if (patch.behavior !== undefined && !GOAL_BEHAVIORS.includes(patch.behavior)) {
      throw new LedgerError(`Unknown goal behavior "${patch.behavior}" (GL-2/GL-3)`);
    }
    return this.appendGoalAmendment(meta, { goalId, action: 'edit', ...patch });
  }

  completeGoal(meta: OpMeta, goalId: string): LedgerEvent {
    if (this.current.goals[goalId] === undefined) throw new LedgerError(`Unknown goal "${goalId}"`);
    return this.appendGoalAmendment(meta, { goalId, action: 'complete' });
  }

  archiveGoal(meta: OpMeta, goalId: string): LedgerEvent {
    if (this.current.goals[goalId] === undefined) throw new LedgerError(`Unknown goal "${goalId}"`);
    return this.appendGoalAmendment(meta, { goalId, action: 'archive' });
  }

  /** GL-1's own "allocated amount, remaining" — always derived from linked, still-active
   * reserves (see GoalRecord's own header comment for why this is never a stored field). */
  queryGoals(filter?: { status?: GoalStatus }): GoalView[] {
    return Object.values(this.current.goals)
      .filter((g) => filter?.status === undefined || g.status === filter.status)
      .map((goal) => {
        const allocatedMinor = Object.values(this.current.reserves)
          .filter((r) => r.goalId === goal.id && r.status === 'active')
          .reduce((sum, r) => sum + r.amountMinor, 0);
        return {
          ...goal,
          allocatedMinor,
          remainingMinor: Math.max(0, goal.targetAmountMinor - allocatedMinor),
          completed: allocatedMinor >= goal.targetAmountMinor,
        };
      })
      .sort((a, b) => a.priority - b.priority || b.createdAt.localeCompare(a.createdAt));
  }

  private appendGoalAmendment(meta: OpMeta, payload: GoalPatchPayload): LedgerEvent {
    const createEvent = this.store
      .readEntity('goal', payload.goalId)
      .find((e) => e.kind === 'create');
    if (createEvent === undefined) throw new LedgerError('Create event missing from log');
    return this.append({
      id: meta.id,
      kind: 'correct',
      entityType: 'goal',
      entityId: payload.goalId,
      at: meta.at,
      source: meta.source ?? 'manual',
      amendsEventId: createEvent.id,
      payload,
    });
  }

  // WP-6.1 (GL-4, S-31): reserves/envelopes — "the concrete mechanism behind 'keep this
  // aside'". Virtual: no account/balance is ever touched by these.
  createReserve(meta: OpMeta, input: ReservePayload): LedgerEvent {
    if (input.purpose === '') throw new LedgerError('A purpose is required (GL-4)');
    currencyInfo(input.currency); // throws on unknown currency
    const amount = fromMinor(input.currency, input.amountMinor); // FX-5 integer guard
    if (amount.minorUnits <= 0) throw new LedgerError('Amount must be positive (GL-4)');
    if (input.reserveId === '') throw new LedgerError('reserveId must not be empty');
    if (this.current.reserves[input.reserveId] !== undefined) {
      throw new LedgerError(`Reserve "${input.reserveId}" already exists`);
    }
    if (input.accountId !== undefined && this.current.accounts[input.accountId] === undefined) {
      throw new LedgerError(`Unknown account "${input.accountId}"`);
    }
    const goal = input.goalId !== undefined ? this.current.goals[input.goalId] : undefined;
    if (input.goalId !== undefined) {
      if (goal === undefined) throw new LedgerError(`Unknown goal "${input.goalId}"`);
      if (goal.currency !== input.currency)
        throw new LedgerError("A reserve must share its goal's currency (GL-4)");
    }
    if (input.rpId !== undefined) {
      const rp = this.current.rps[input.rpId];
      if (rp === undefined) throw new LedgerError(`Unknown RP "${input.rpId}"`);
      if (rp.currency !== input.currency)
        throw new LedgerError("A reserve must share its RP's currency (WP-6.3)");
    }
    return this.append({
      id: meta.id,
      kind: 'create',
      entityType: 'reserve',
      entityId: input.reserveId,
      at: meta.at,
      source: meta.source ?? 'manual',
      payload: input,
    });
  }

  /** GL-4: "releasable anytime ... all changes are logged" — never deleted, always a
   * reason-tagged correction event (LG-2/LG-3), the same discipline RP write-off/TPF close
   * already use. */
  releaseReserve(meta: OpMeta, reserveId: string, reason: string): LedgerEvent {
    const reserve = this.current.reserves[reserveId];
    if (reserve === undefined) throw new LedgerError(`Unknown reserve "${reserveId}"`);
    if (reserve.status === 'released')
      throw new LedgerError(`Reserve "${reserveId}" is already released`);
    if (reason === '') throw new LedgerError('A reason is required to release a reserve (GL-4)');
    const createEvent = this.store
      .readEntity('reserve', reserveId)
      .find((e) => e.kind === 'create');
    if (createEvent === undefined) throw new LedgerError('Create event missing from log');
    return this.append({
      id: meta.id,
      kind: 'correct',
      entityType: 'reserve',
      entityId: reserveId,
      at: meta.at,
      source: meta.source ?? 'manual',
      amendsEventId: createEvent.id,
      payload: { reserveId, action: 'release', reason },
    });
  }

  queryReserves(filter?: { status?: ReserveStatus; goalId?: string }): ReserveRecord[] {
    return Object.values(this.current.reserves)
      .filter((r) => filter?.status === undefined || r.status === filter.status)
      .filter((r) => filter?.goalId === undefined || r.goalId === filter.goalId)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }

  /** DB-1 final: Σ active reserves, currency-scoped — the term `queryDashboard` has carried
   * as a documented `0` placeholder since WP-1.11 ("Reserve/Goal entities don't exist until
   * P6"). Reserves scoped to one account still reduce the currency-wide Spendable Now figure
   * (GL-4's own "globally or per account" is a SCOPE of what's earmarked, not a signal that
   * per-account reserves are excluded from the owner's overall spendable picture). */
  queryReservedTotal(currency: CurrencyCode): number {
    return Object.values(this.current.reserves)
      .filter((r) => r.status === 'active' && r.currency === currency)
      .reduce((sum, r) => sum + r.amountMinor, 0);
  }

  /** GL-2 (LOCKED): a strict goal's own target is ALWAYS treated as a floor the owner must
   * keep available, whether or not a matching Reserve exists for it yet — "bind the
   * Spendable-Now computation and force advisor warnings on violation". */
  queryStrictReserveViolation(currency: CurrencyCode): StrictReserveViolation {
    const requiredMinor = Object.values(this.current.goals)
      .filter((g) => g.status === 'active' && g.behavior === 'strict' && g.currency === currency)
      .reduce((sum, g) => sum + g.targetAmountMinor, 0);
    const availableMinor = Object.values(this.current.accounts)
      .filter((a) => !a.archived && a.currency === currency && a.spendable)
      .reduce((sum, a) => sum + a.balanceMinor, 0);
    return { currency, requiredMinor, availableMinor, violated: availableMinor < requiredMinor };
  }

  private static readonly RAMP_UP_DAY_MS = 24 * 60 * 60 * 1000;
  private static readonly RAMP_UP_ELIGIBILITY_DAYS = 60; // GL-7/GL-8

  /** §19.4 (GL-6..8, OD-4): see ReserveRampUpStatus's own header comment for the "real data"
   * (excludes backfill) and "2 months ≈ 60 days" interpretations. */
  queryReserveRampUp(currency: CurrencyCode, asOf: string): ReserveRampUpStatus {
    const asOfMs = Date.parse(asOf);
    const realExpenses = Object.values(this.current.transactions).filter(
      (tx) =>
        tx.backfill !== true && !tx.reversed && tx.adjustment !== true && tx.currency === currency,
    );
    const dataStartMs = realExpenses.reduce<number | undefined>((earliest, tx) => {
      const ms = Date.parse(tx.at);
      return earliest === undefined || ms < earliest ? ms : earliest;
    }, undefined);
    const daysOfData =
      dataStartMs === undefined ? 0 : Math.max(0, (asOfMs - dataStartMs) / Ledger.RAMP_UP_DAY_MS);

    const essentialCategoryNames = new Set(
      Object.values(this.current.categories)
        .filter((c) => c.kind === 'expense' && c.essential === true)
        .map((c) => c.name),
    );
    const essentialTotalMinor = realExpenses
      .filter(
        (tx) =>
          tx.type === 'expense' &&
          tx.category !== undefined &&
          essentialCategoryNames.has(tx.category),
      )
      .reduce((sum, tx) => sum + tx.amountMinor, 0);
    const monthsOfData = daysOfData / 30; // this codebase's own 30-days-per-month convention (DB-2)
    const avgMonthlyEssentialMinor =
      monthsOfData > 0 ? Math.round(essentialTotalMinor / monthsOfData) : 0;

    const eligible = daysOfData >= Ledger.RAMP_UP_ELIGIBILITY_DAYS;
    return {
      currency,
      daysOfData: Math.floor(daysOfData),
      avgMonthlyEssentialMinor,
      targetProposalEligible: eligible,
      suggestedTargetMinor: eligible ? avgMonthlyEssentialMinor * 3 : 0,
      lowSpendableAlertActive: eligible,
      lowSpendableThresholdMinor: eligible ? Math.round(avgMonthlyEssentialMinor * 0.5) : 0,
    };
  }

  /** WP-6.2 (M29 AD-2, LOCKED §2.31): the priority waterfall — see WaterfallAllocation's own
   * header comment for exactly which existing query backs each tier. A pure computation
   * (AR-7: no clock reads, no writes) — WP-6.3 is where an owner's "Accept" turns a line into
   * an actual reserve/planned settlement. */
  computeWaterfallAllocation(
    incomeMinor: number,
    currency: CurrencyCode,
    asOf: string,
  ): WaterfallAllocation {
    currencyInfo(currency); // throws on unknown currency
    if (!(incomeMinor > 0)) throw new LedgerError('Income must be positive (AD-2)');

    let remaining = incomeMinor;
    const lines: WaterfallLine[] = [];
    const take = (need: number): number => {
      const amount = Math.min(remaining, Math.max(0, need));
      remaining -= amount;
      return amount;
    };

    // P1: critical commitments — overdue payables, most-overdue (earliest due date) first.
    const overduePayables = this.queryOverdueRps(asOf, { kind: 'payable' })
      .filter((rp) => rp.currency === currency)
      .sort((a, b) => (a.dueDate ?? '').localeCompare(b.dueDate ?? ''));
    for (const rp of overduePayables) {
      if (remaining <= 0) break;
      const amount = take(rp.amountMinor - rp.settledMinor);
      if (amount > 0) {
        lines.push({
          tier: 'P1',
          label: `Overdue: ${rp.party} — ${rp.reason}`,
          allocatedMinor: amount,
          targetKind: 'rp',
          targetId: rp.id,
        });
      }
    }

    // P2: minimum safety reserve — the SAME GL-2 shortfall, not re-derived. Not attributable to
    // one specific goal (it's an aggregate across every active strict goal), so WP-6.3's Accept
    // creates a standalone reserve for this line, not a goal-linked one.
    if (remaining > 0) {
      const violation = this.queryStrictReserveViolation(currency);
      const amount = take(violation.requiredMinor - violation.availableMinor);
      if (amount > 0)
        lines.push({
          tier: 'P2',
          label: 'Safety reserve shortfall',
          allocatedMinor: amount,
          targetKind: 'reserve',
        });
    }

    // P3: near-term (30-day) essential recurring bills.
    if (remaining > 0) {
      const essentialCategoryNames = new Set(
        Object.values(this.current.categories)
          .filter((c) => c.kind === 'expense' && c.essential === true)
          .map((c) => c.name),
      );
      for (const occurrence of this.queryUpcomingRecurringOccurrences(asOf, 30)) {
        if (remaining <= 0) break;
        if (occurrence.kind !== 'expense' || occurrence.amountMinor === undefined) continue;
        const template = this.current.recurringTemplates[occurrence.templateId];
        if (template?.base.kind !== 'expense') continue;
        if (
          template.base.category === undefined ||
          !essentialCategoryNames.has(template.base.category)
        )
          continue;
        if (this.recurBaseCurrency(template.base) !== currency) continue;
        const amount = take(occurrence.amountMinor);
        if (amount > 0) {
          lines.push({
            tier: 'P3',
            label: `Upcoming essential: ${occurrence.name}`,
            allocatedMinor: amount,
            targetKind: 'reserve',
          });
        }
      }
    }

    // P4: financial goals — every ACTIVE FLEXIBLE goal (P2 already claimed strict ones), in
    // the owner's own priority order (queryGoals' own sort, reused verbatim).
    if (remaining > 0) {
      for (const goal of this.queryGoals({ status: 'active' })) {
        if (remaining <= 0) break;
        if (goal.behavior !== 'flexible' || goal.currency !== currency) continue;
        const amount = take(goal.remainingMinor);
        if (amount > 0)
          lines.push({
            tier: 'P4',
            label: `Goal: ${goal.name}`,
            allocatedMinor: amount,
            targetKind: 'goal',
            targetId: goal.id,
          });
      }
    }

    // P5: discretionary — whatever's left, always present so the lines sum to incomeMinor.
    lines.push({
      tier: 'P5',
      label: 'Discretionary (unallocated)',
      allocatedMinor: remaining,
      targetKind: 'discretionary',
    });

    return { currency, incomeMinor, lines };
  }

  /** WP-6.4 (AD-4): what's already "spoken for" — the SAME three queries `computeWaterfallAllocation`
   * reuses for its P1/P2/P3 tiers, summed rather than itemized (affordability needs one number,
   * not a line-by-line split). Never re-derived independently of those queries. */
  private protectedFloorMinor(currency: CurrencyCode, asOf: string): number {
    const overduePayablesMinor = this.queryOverdueRps(asOf, { kind: 'payable' })
      .filter((rp) => rp.currency === currency)
      .reduce((sum, rp) => sum + (rp.amountMinor - rp.settledMinor), 0);

    const violation = this.queryStrictReserveViolation(currency);
    const safetyShortfallMinor = Math.max(0, violation.requiredMinor - violation.availableMinor);

    const essentialCategoryNames = new Set(
      Object.values(this.current.categories)
        .filter((c) => c.kind === 'expense' && c.essential === true)
        .map((c) => c.name),
    );
    let essentialBillsMinor = 0;
    for (const occurrence of this.queryUpcomingRecurringOccurrences(asOf, 30)) {
      if (occurrence.kind !== 'expense' || occurrence.amountMinor === undefined) continue;
      const template = this.current.recurringTemplates[occurrence.templateId];
      if (template?.base.kind !== 'expense') continue;
      if (
        template.base.category === undefined ||
        !essentialCategoryNames.has(template.base.category)
      )
        continue;
      if (this.recurBaseCurrency(template.base) !== currency) continue;
      essentialBillsMinor += occurrence.amountMinor;
    }

    return overduePayablesMinor + safetyShortfallMinor + essentialBillsMinor;
  }

  /** WP-6.4 (M29 AD-4, LOCKED §2.34, S-32): "Can I afford this?" — a PURE computation (AR-7: no
   * writes), built entirely on `queryDashboard`'s own already-aggregated fields (spendableNowMinor,
   * receivableTotalMinor, upcomingRecurringInflowMinor) plus `protectedFloorMinor` above. */
  queryAffordability(
    amountMinor: number,
    currency: CurrencyCode,
    purpose: string,
    asOf: string,
  ): AffordabilityAnswer {
    currencyInfo(currency); // throws on unknown currency
    if (!(amountMinor > 0)) throw new LedgerError('Amount must be positive (AD-4)');
    if (purpose === '') throw new LedgerError('A purpose is required (AD-4)');

    const dashboard = this.queryDashboard(currency, asOf, { from: asOf, to: asOf });
    const spendableNowMinor = dashboard.spendableNowMinor;
    const protectedFloorMinor = this.protectedFloorMinor(currency, asOf);
    const remainingAfterMinor = spendableNowMinor - amountMinor;
    const advisable = remainingAfterMinor >= protectedFloorMinor;
    const futureImpactMinor = remainingAfterMinor - protectedFloorMinor;
    const upcomingInflowMinor =
      dashboard.receivableTotalMinor + dashboard.upcomingRecurringInflowMinor;

    return {
      currency,
      amountMinor,
      purpose,
      asOf,
      spendableNowMinor,
      canPayNow: spendableNowMinor >= amountMinor,
      protectedFloorMinor,
      remainingAfterMinor,
      advisable,
      futureImpactMinor,
      upcomingInflowMinor,
      waitingWouldHelp: !advisable && upcomingInflowMinor >= -futureImpactMinor,
    };
  }

  /** WP-7.1 (M30 SM-1..4, LOCKED §2.35, S-33, §14-11): the scenario simulator's one entry point.
   * See the header comment on `ScenarioResult` for why this is a pure query, never a write. */
  runScenario(
    currency: CurrencyCode,
    asOf: string,
    windowDays: number,
    operations: readonly ScenarioOperation[],
  ): ScenarioResult {
    currencyInfo(currency); // throws on unknown currency
    if (!(Number.isInteger(windowDays) && windowDays > 0)) {
      throw new LedgerError('windowDays must be a positive integer (M30)');
    }
    const solveOpCount = operations.filter(
      (op) => op.kind === 'solveSpend' || op.kind === 'solveSaving',
    ).length;
    if (solveOpCount > 1)
      throw new LedgerError('A scenario may include at most one solve operation (M30)');

    const asOfMs = Date.parse(asOf);
    const dayIndexOf = (iso: string): number => Math.round((Date.parse(iso) - asOfMs) / 86_400_000);

    // The baseline cashflow list: EXISTING open RPs (any status but closed, remaining > 0,
    // overdue/undated ones collapse to day 0 — already due "now" for projection purposes) plus
    // EXISTING upcoming recurring occurrences (the SAME `queryUpcomingRecurringOccurrences`
    // WP-6.2/DB-2 already reuse) — never a parallel derivation of "what's owed".
    const events: ScenarioCashflowEvent[] = [];
    for (const rp of Object.values(this.current.rps)) {
      if (rp.status === 'closed' || rp.currency !== currency) continue;
      const remaining = rp.amountMinor - rp.settledMinor;
      if (remaining <= 0) continue;
      const dueDayIndex = rp.dueDate === undefined ? 0 : Math.max(0, dayIndexOf(rp.dueDate));
      if (dueDayIndex > windowDays) continue; // due outside this window — irrelevant to this chart
      events.push({
        dayIndex: dueDayIndex,
        amountMinor: rp.kind === 'payable' ? -remaining : remaining,
        rpId: rp.id,
      });
    }
    for (const occurrence of this.queryUpcomingRecurringOccurrences(asOf, windowDays)) {
      if (occurrence.amountMinor === undefined) continue;
      const template = this.current.recurringTemplates[occurrence.templateId];
      if (template === undefined || template.base.kind === 'transfer') continue; // internal reallocation, no net effect
      const occCurrency =
        template.base.kind === 'payable'
          ? template.base.currency
          : this.recurBaseCurrency(template.base);
      if (occCurrency !== currency) continue;
      const dayIndex = Math.max(0, Math.min(windowDays, dayIndexOf(occurrence.at)));
      events.push({
        dayIndex,
        amountMinor:
          occurrence.kind === 'income' ? occurrence.amountMinor : -occurrence.amountMinor,
      });
    }

    // Apply operations — each only reshapes this plain in-memory list.
    for (const op of operations) {
      if (op.kind === 'delay') {
        if (this.current.rps[op.rpId] === undefined)
          throw new LedgerError(`Unknown RP "${op.rpId}"`);
        for (const e of events.filter((ev) => ev.rpId === op.rpId)) {
          e.dayIndex = Math.max(0, Math.min(windowDays, e.dayIndex + op.days));
        }
      } else if (op.kind === 'change') {
        if (this.current.rps[op.rpId] === undefined)
          throw new LedgerError(`Unknown RP "${op.rpId}"`);
        for (const e of events.filter((ev) => ev.rpId === op.rpId)) {
          e.amountMinor = e.amountMinor < 0 ? -op.newAmountMinor : op.newAmountMinor;
        }
      } else if (op.kind === 'add') {
        const dayIndex = dayIndexOf(op.at);
        if (dayIndex < 0 || dayIndex > windowDays)
          throw new LedgerError('add: "at" must fall within the projection window (M30)');
        events.push({
          dayIndex,
          amountMinor: op.cashflowKind === 'income' ? op.amountMinor : -op.amountMinor,
        });
      } else if (op.kind === 'payAll') {
        for (const e of events) {
          if (e.rpId !== undefined && e.amountMinor < 0) e.dayIndex = 0;
        }
      }
      // solveSpend/solveSaving don't reshape the cashflow list — resolved after the walk below.
    }

    // Walk the window day by day from the CURRENT actual spendable balance (DB-1, reused verbatim).
    const startingBalanceMinor = this.queryDashboard(currency, asOf, {
      from: asOf,
      to: asOf,
    }).spendableNowMinor;
    const byDay = new Map<number, number>();
    for (const e of events) byDay.set(e.dayIndex, (byDay.get(e.dayIndex) ?? 0) + e.amountMinor);

    const points: ScenarioPoint[] = [];
    let running = startingBalanceMinor;
    for (let d = 0; d <= windowDays; d += 1) {
      running += byDay.get(d) ?? 0;
      points.push({ date: new Date(asOfMs + d * 86_400_000).toISOString(), balanceMinor: running });
    }

    // SM-3 "vs reserves": active reserves + the strict-goal floor (WP-6.1's own two existing
    // queries) — overdue bills/upcoming essentials are already IN the walk as cashflow events,
    // not a second floor stacked on top of it.
    const reserveFloorMinor =
      this.queryReservedTotal(currency) + this.queryStrictReserveViolation(currency).requiredMinor;
    const breaches: ScenarioBreach[] = points
      .filter((p) => p.balanceMinor < reserveFloorMinor)
      .map((p) => ({ date: p.date, balanceMinor: p.balanceMinor }));

    let solvedAmountMinor: number | undefined;
    const solveSpendOp = operations.find((op) => op.kind === 'solveSpend');
    const solveSavingOp = operations.find(
      (op): op is Extract<ScenarioOperation, { kind: 'solveSaving' }> => op.kind === 'solveSaving',
    );
    if (solveSpendOp !== undefined) {
      let maxDaily = Number.POSITIVE_INFINITY;
      for (let d = 1; d <= windowDays; d += 1) {
        const headroom = (points[d]?.balanceMinor ?? 0) - reserveFloorMinor;
        maxDaily = Math.min(maxDaily, Math.floor(headroom / d));
      }
      solvedAmountMinor = Math.max(0, maxDaily);
    } else if (solveSavingOp !== undefined) {
      const goal = this.current.goals[solveSavingOp.goalId];
      if (goal === undefined) throw new LedgerError(`Unknown goal "${solveSavingOp.goalId}"`);
      if (goal.targetDate === undefined || Date.parse(goal.targetDate) <= asOfMs) {
        throw new LedgerError(
          `Goal "${goal.name}" needs a target date in the future to solve a required saving (M30)`,
        );
      }
      const goalView = this.queryGoals({ status: 'active' }).find((g) => g.id === goal.id);
      const remainingMinor = goalView?.remainingMinor ?? goal.targetAmountMinor;
      const monthsRemaining = (Date.parse(goal.targetDate) - asOfMs) / 86_400_000 / 30;
      solvedAmountMinor = Math.max(0, Math.ceil(remainingMinor / monthsRemaining));
    }

    return {
      currency,
      asOf,
      windowDays,
      points,
      reserveFloorMinor,
      breaches,
      ...(solvedAmountMinor !== undefined ? { solvedAmountMinor } : {}),
    };
  }

  /** WP-7.4 (RE-5): a pure, informational-only estimate — see `ZakatEstimate`'s own header
   * comment for the documented "qualifying balance" reading. `nisabMinor`/`ratePercent` are
   * always caller-supplied (the owner's own settings), never read from Settings internally —
   * the same "thin, fully parameterized computation" precedent `queryAffordability`/
   * `computeWaterfallAllocation` already established. */
  queryZakatEstimate(
    currency: CurrencyCode,
    asOf: string,
    nisabMinor: number,
    ratePercent: number,
  ): ZakatEstimate {
    currencyInfo(currency); // throws on unknown currency
    if (!(nisabMinor >= 0)) throw new LedgerError('nisabMinor must not be negative (RE-5)');
    if (!(ratePercent > 0)) throw new LedgerError('ratePercent must be positive (RE-5)');

    const dashboard = this.queryDashboard(currency, asOf, { from: asOf, to: asOf });
    const qualifyingBalanceMinor = Math.max(
      0,
      dashboard.netPositionMinor - dashboard.payableTotalMinor,
    );
    const meetsNisab = qualifyingBalanceMinor >= nisabMinor;
    const zakatDueMinor = meetsNisab ? Math.round((qualifyingBalanceMinor * ratePercent) / 100) : 0;

    return {
      currency,
      asOf,
      qualifyingBalanceMinor,
      nisabMinor,
      ratePercent,
      meetsNisab,
      zakatDueMinor,
    };
  }

  // WP-6.3 (M29 AD-3/6/7, S-32): the "New-income trigger" is a plain create — the API/web layer
  // calls this right after recording a new income transaction, never `recordTransaction` itself
  // (so a CSV backfill import never spawns a suggestion, AD-6's own "an advisor that nags gets
  // muted" — satisfied by construction, no separate backfill-flag check needed). The waterfall
  // is reused VERBATIM (`computeWaterfallAllocation`), then snapshotted into the create event.
  proposeAllocationSuggestion(
    meta: OpMeta,
    input: { suggestionId: string; incomeMinor: number; currency: CurrencyCode; asOf: string },
  ): LedgerEvent {
    if (input.suggestionId === '') throw new LedgerError('suggestionId must not be empty');
    if (this.current.suggestions[input.suggestionId] !== undefined) {
      throw new LedgerError(`Suggestion "${input.suggestionId}" already exists`);
    }
    const allocation = this.computeWaterfallAllocation(
      input.incomeMinor,
      input.currency,
      input.asOf,
    );
    const payload: SuggestionPayload = {
      suggestionId: input.suggestionId,
      currency: allocation.currency,
      incomeMinor: allocation.incomeMinor,
      asOf: input.asOf,
      lines: allocation.lines,
    };
    return this.append({
      id: meta.id,
      kind: 'create',
      entityType: 'suggestion',
      entityId: input.suggestionId,
      at: meta.at,
      source: meta.source ?? 'manual',
      payload,
    });
  }

  private appendSuggestionAmendment(meta: OpMeta, payload: SuggestionPatchPayload): LedgerEvent {
    const createEvent = this.store
      .readEntity('suggestion', payload.suggestionId)
      .find((e) => e.kind === 'create');
    if (createEvent === undefined) throw new LedgerError('Create event missing from log');
    return this.append({
      id: meta.id,
      kind: 'correct',
      entityType: 'suggestion',
      entityId: payload.suggestionId,
      at: meta.at,
      source: meta.source ?? 'manual',
      amendsEventId: createEvent.id,
      payload,
    });
  }

  private requireResolvableSuggestion(suggestionId: string): SuggestionRecord {
    const suggestion = this.current.suggestions[suggestionId];
    if (suggestion === undefined) throw new LedgerError(`Unknown suggestion "${suggestionId}"`);
    if (suggestion.status === 'accepted' || suggestion.status === 'dismissed') {
      throw new LedgerError(`Suggestion "${suggestionId}" is already ${suggestion.status}`);
    }
    return suggestion;
  }

  /** AD-3 "accepting creates reserves/planned settlements only" (AD-7: never a real
   * transaction/transfer/settlement) — ONE mechanism, `createReserve`, for every non-discretionary
   * line: a 'goal'-targeted line links `goalId` (a plain reserve), an 'rp'-targeted line links
   * `rpId` (a "planned settlement" — money set aside for a specific payable, never an actual
   * `settleRp`), and a standalone 'reserve' line (P2/P3) links neither. */
  acceptSuggestion(meta: OpMeta, suggestionId: string): LedgerEvent[] {
    const suggestion = this.requireResolvableSuggestion(suggestionId);
    const events: LedgerEvent[] = [];
    const createdReserveIds: string[] = [];
    suggestion.lines.forEach((line, index) => {
      if (line.targetKind === 'discretionary' || !(line.allocatedMinor > 0)) return;
      const reserveId = `${meta.id}:reserve:${String(index)}`;
      const reserveMeta: OpMeta = { id: reserveId, at: meta.at, source: meta.source ?? 'manual' };
      events.push(
        this.createReserve(reserveMeta, {
          reserveId,
          purpose: line.label,
          amountMinor: line.allocatedMinor,
          currency: suggestion.currency,
          ...(line.targetKind === 'goal' && line.targetId !== undefined
            ? { goalId: line.targetId }
            : {}),
          ...(line.targetKind === 'rp' && line.targetId !== undefined
            ? { rpId: line.targetId }
            : {}),
        }),
      );
      createdReserveIds.push(reserveId);
    });
    events.push(
      this.appendSuggestionAmendment(meta, { suggestionId, action: 'accept', createdReserveIds }),
    );
    return events;
  }

  /** AD-6: dismissible/snoozable. Snoozing is temporary — `queryActiveSuggestions` resurfaces it
   * once `snoozedUntil` passes; it never counts as answered the way accept/dismiss do. */
  snoozeSuggestion(meta: OpMeta, suggestionId: string, snoozedUntil: string): LedgerEvent {
    this.requireResolvableSuggestion(suggestionId);
    if (snoozedUntil === '') throw new LedgerError('snoozedUntil is required (AD-6)');
    return this.appendSuggestionAmendment(meta, { suggestionId, action: 'snooze', snoozedUntil });
  }

  /** AD-6: "the advisor remembers dismissals instead of nagging" — permanent, unlike snooze;
   * `queryActiveSuggestions` never resurfaces a dismissed suggestion, at any future `asOf`. */
  dismissSuggestion(meta: OpMeta, suggestionId: string): LedgerEvent {
    this.requireResolvableSuggestion(suggestionId);
    return this.appendSuggestionAmendment(meta, { suggestionId, action: 'dismiss' });
  }

  querySuggestions(filter?: { status?: SuggestionStatus }): SuggestionRecord[] {
    return Object.values(this.current.suggestions)
      .filter((s) => filter?.status === undefined || s.status === filter.status)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }

  /** AD-6: pending, or snoozed with its snooze window already elapsed as of `asOf` — dismissed
   * and accepted suggestions never appear here, regardless of `asOf` (AR-7: a pure function of
   * state + the passed-in `asOf`, no clock read). */
  queryActiveSuggestions(asOf: string): SuggestionRecord[] {
    return this.querySuggestions().filter(
      (s) => s.status === 'pending' || (s.status === 'snoozed' && (s.snoozedUntil ?? '') <= asOf),
    );
  }

  // M10 (TP-1): a case per third-party owner — tracks received/fees/returns/converted and
  // the computed owed-back, never touching the transaction ledger (TP-2 by construction).
  openTpfCase(meta: OpMeta, input: TpfCasePayload): LedgerEvent {
    currencyInfo(input.currency); // throws on unknown currency
    if (input.party === '') throw new LedgerError('Party is required (TP-1)');
    if (input.caseId === '') throw new LedgerError('caseId must not be empty');
    if (this.current.tpfCases[input.caseId] !== undefined) {
      throw new LedgerError(`"${input.caseId}" already exists`);
    }
    if (input.partyId !== undefined && this.current.parties[input.partyId] === undefined) {
      throw new LedgerError(`Unknown party "${input.partyId}"`);
    }
    return this.append({
      id: meta.id,
      kind: 'create',
      entityType: 'tpfCase',
      entityId: input.caseId,
      at: meta.at,
      source: meta.source ?? 'manual',
      payload: input,
    });
  }

  private validateTpfFee(fee: TpfFeeRecord | undefined): void {
    if (fee === undefined) return;
    if (!TPF_FEE_BEARERS.includes(fee.bearer)) {
      throw new LedgerError(`Unknown fee bearer "${fee.bearer}" (FE-4)`);
    }
    if (!(fee.amountMinor > 0)) throw new LedgerError('Fee amount must be positive (§18.5)');
  }

  /** TP-1/FE-4: money lands — the account's real balance increases by `accountAmountMinor`. */
  recordTpfReceive(meta: OpMeta, caseId: string, input: TpfMovementInput): LedgerEvent {
    const tpfCase = this.current.tpfCases[caseId];
    if (tpfCase === undefined) throw new LedgerError(`Unknown TPF case "${caseId}"`);
    if (tpfCase.status !== 'open') throw new LedgerError(`TPF case "${caseId}" is closed`);
    if (this.current.accounts[input.accountId] === undefined) {
      throw new LedgerError(`Unknown account "${input.accountId}"`);
    }
    if (!(input.caseAmountMinor > 0)) throw new LedgerError('Amount must be positive (§18.5)');
    if (!(input.accountAmountMinor > 0)) throw new LedgerError('Amount must be positive (§18.5)');
    this.validateTpfFee(input.fee);
    return this.appendTpfAmendment(meta, { caseId, action: 'receive', ...input });
  }

  /** TP-4: settles part or all of the outstanding owed-back via real cash leaving an account. */
  recordTpfReturn(meta: OpMeta, caseId: string, input: TpfMovementInput): LedgerEvent {
    const tpfCase = this.current.tpfCases[caseId];
    if (tpfCase === undefined) throw new LedgerError(`Unknown TPF case "${caseId}"`);
    if (tpfCase.status !== 'open') throw new LedgerError(`TPF case "${caseId}" is closed`);
    if (this.current.accounts[input.accountId] === undefined) {
      throw new LedgerError(`Unknown account "${input.accountId}"`);
    }
    if (!(input.caseAmountMinor > 0)) throw new LedgerError('Amount must be positive (§18.5)');
    if (!(input.accountAmountMinor > 0)) throw new LedgerError('Amount must be positive (§18.5)');
    if (input.caseAmountMinor > tpfCase.owedBackMinor) {
      throw new LedgerError('Return exceeds the remaining owed-back (TP-4)');
    }
    this.validateTpfFee(input.fee);
    return this.appendTpfAmendment(meta, { caseId, action: 'return', ...input });
  }

  /**
   * TP-5: a portion becomes the owner's own income, by explicit action. This only ever
   * reduces owed-back — it never touches an account balance (the cash already landed via a
   * prior receive) and never creates a Transaction (TP-2: TPF stays out of the transaction
   * ledger everywhere); the movement's own reason + timestamp is its audit trail (visible on
   * the case, S-17), not a duplicate income record.
   */
  convertTpfToIncome(
    meta: OpMeta,
    caseId: string,
    input: { caseAmountMinor: number; reason: string },
  ): LedgerEvent {
    const tpfCase = this.current.tpfCases[caseId];
    if (tpfCase === undefined) throw new LedgerError(`Unknown TPF case "${caseId}"`);
    if (tpfCase.status !== 'open') throw new LedgerError(`TPF case "${caseId}" is closed`);
    if (!(input.caseAmountMinor > 0)) throw new LedgerError('Amount must be positive (§18.5)');
    if (input.caseAmountMinor > tpfCase.owedBackMinor) {
      throw new LedgerError('Amount exceeds the remaining held balance (TP-5)');
    }
    if (input.reason === '')
      throw new LedgerError('A reason is required to convert to income (TP-5)');
    return this.appendTpfAmendment(meta, {
      caseId,
      action: 'convert',
      caseAmountMinor: input.caseAmountMinor,
      reason: input.reason,
    });
  }

  /** S-17: "Close (only at zero)". */
  closeTpfCase(meta: OpMeta, caseId: string): LedgerEvent {
    const tpfCase = this.current.tpfCases[caseId];
    if (tpfCase === undefined) throw new LedgerError(`Unknown TPF case "${caseId}"`);
    if (tpfCase.status !== 'open') throw new LedgerError(`TPF case "${caseId}" is already closed`);
    if (tpfCase.owedBackMinor !== 0) {
      throw new LedgerError('A case can only close at zero outstanding (TP-4)');
    }
    return this.appendTpfAmendment(meta, { caseId, action: 'close' });
  }

  /** WP-5.1 (RE-2, Snapshot): "own funds, reserves, TPF held" — the three lines a daily
   * Snapshot stores for the position-over-time chart. `ownFundsMinor` reuses queryDashboard's
   * own `netPositionMinor` formula (total balance + in-transit - TPF held) verbatim, so the
   * two never silently diverge; `reservesMinor` reuses `queryReservedTotal` verbatim too
   * (WP-6.1) — was a documented `0` placeholder until Reserve/Goal entities existed. */
  querySnapshotValuation(currency: CurrencyCode): {
    currency: CurrencyCode;
    ownFundsMinor: number;
    reservesMinor: number;
    tpfHeldMinor: number;
  } {
    const accounts = Object.values(this.current.accounts).filter(
      (a) => !a.archived && a.currency === currency,
    );
    const totalBalanceMinor = accounts.reduce((sum, a) => sum + a.balanceMinor, 0);
    const inTransitMinor = this.inTransitTotal(currency);
    const tpfHeldMinor = this.queryHoldingForOthers(currency);
    return {
      currency,
      ownFundsMinor: totalBalanceMinor + inTransitMinor - tpfHeldMinor,
      reservesMinor: this.queryReservedTotal(currency),
      tpfHeldMinor,
    };
  }

  /** TP-2 dashboard "Holding for others: X" — owed-back summed across open cases. */
  queryHoldingForOthers(currency: CurrencyCode, filter?: { caseId?: string }): number {
    let total = 0;
    for (const tpfCase of Object.values(this.current.tpfCases)) {
      if (tpfCase.status !== 'open') continue;
      if (tpfCase.currency !== currency) continue;
      if (filter?.caseId !== undefined && tpfCase.id !== filter.caseId) continue;
      total += tpfCase.owedBackMinor;
    }
    return total;
  }

  private appendTpfAmendment(meta: OpMeta, payload: TpfCasePatchPayload): LedgerEvent {
    const createEvent = this.store
      .readEntity('tpfCase', payload.caseId)
      .find((e) => e.kind === 'create');
    if (createEvent === undefined) throw new LedgerError('Create event missing from log');
    return this.append({
      id: meta.id,
      kind: 'correct',
      entityType: 'tpfCase',
      entityId: payload.caseId,
      at: meta.at,
      source: meta.source ?? 'manual',
      amendsEventId: createEvent.id,
      payload,
    });
  }

  private validateRecurBase(base: RecurBase): void {
    if (base.amountMinor !== undefined && !(base.amountMinor > 0)) {
      throw new LedgerError('Amount must be positive (§18.5)');
    }
    if (base.kind === 'expense' || base.kind === 'income') {
      if (this.current.accounts[base.accountId] === undefined) {
        throw new LedgerError(`Unknown account "${base.accountId}"`);
      }
      return;
    }
    if (base.kind === 'transfer') {
      if (base.fromAccountId === base.toAccountId) {
        throw new LedgerError('A transfer must be between different accounts (TR-1)');
      }
      if (this.current.accounts[base.fromAccountId] === undefined) {
        throw new LedgerError(`Unknown account "${base.fromAccountId}"`);
      }
      if (this.current.accounts[base.toAccountId] === undefined) {
        throw new LedgerError(`Unknown account "${base.toAccountId}"`);
      }
      return;
    }
    // payable
    if (base.party === '') throw new LedgerError('Party is required (RP-1)');
    if (base.reason === '') throw new LedgerError('A reason is required (RP-1)');
    currencyInfo(base.currency);
  }

  // M13 (RC-1): a template posts the same expense/income/transfer/payable on a schedule.
  createRecurringTemplate(
    meta: OpMeta,
    input: {
      templateId: string;
      name: string;
      base: RecurBase;
      schedule: RecurSchedule;
      behavior: RecurBehavior;
      startDate: string;
    },
  ): LedgerEvent {
    if (input.name === '') throw new LedgerError('Name is required (RC-1)');
    if (!RECUR_FREQUENCIES.includes(input.schedule.frequency)) {
      throw new LedgerError(`Unknown frequency "${input.schedule.frequency}"`);
    }
    if (!(input.schedule.everyN > 0)) throw new LedgerError('everyN must be positive');
    if (!RECUR_BEHAVIORS.includes(input.behavior)) {
      throw new LedgerError(`Unknown behavior "${input.behavior}"`);
    }
    if (input.base.amountMinor === undefined && input.behavior === 'auto_post') {
      throw new LedgerError('A variable-amount template must use confirm, not auto-post (RC-2)');
    }
    this.validateRecurBase(input.base);
    if (input.templateId === '') throw new LedgerError('templateId must not be empty');
    if (this.current.recurringTemplates[input.templateId] !== undefined) {
      throw new LedgerError(`"${input.templateId}" already exists`);
    }
    return this.append({
      id: meta.id,
      kind: 'create',
      entityType: 'recurringTemplate',
      entityId: input.templateId,
      at: meta.at,
      source: meta.source ?? 'manual',
      payload: input,
    });
  }

  pauseRecurringTemplate(meta: OpMeta, templateId: string): LedgerEvent {
    const template = this.current.recurringTemplates[templateId];
    if (template === undefined) throw new LedgerError(`Unknown recurring template "${templateId}"`);
    if (template.status !== 'active') throw new LedgerError(`"${templateId}" is already paused`);
    return this.appendRecurringAmendment(meta, { templateId, action: 'pause' });
  }

  resumeRecurringTemplate(meta: OpMeta, templateId: string): LedgerEvent {
    const template = this.current.recurringTemplates[templateId];
    if (template === undefined) throw new LedgerError(`Unknown recurring template "${templateId}"`);
    if (template.status !== 'paused') throw new LedgerError(`"${templateId}" is not paused`);
    return this.appendRecurringAmendment(meta, { templateId, action: 'resume' });
  }

  /** RC-3 "skip-once": advances past the next occurrence without posting anything. */
  skipNextRecurringOccurrence(meta: OpMeta, templateId: string): LedgerEvent {
    const template = this.current.recurringTemplates[templateId];
    if (template === undefined) throw new LedgerError(`Unknown recurring template "${templateId}"`);
    if (template.status !== 'active') throw new LedgerError(`"${templateId}" is not active`);
    return this.appendRecurringAmendment(meta, {
      templateId,
      action: 'skip',
      nextRunAt: nextOccurrence(template.schedule, template.nextRunAt),
    });
  }

  /**
   * RC-3 "edit-future-occurrences": occurrences are never pre-generated, so editing a
   * template's fields IS editing every future occurrence — past posted entities are already
   * independent records, untouched.
   */
  editRecurringTemplate(
    meta: OpMeta,
    templateId: string,
    patch: { name?: string; base?: RecurBase; schedule?: RecurSchedule; behavior?: RecurBehavior },
  ): LedgerEvent {
    const template = this.current.recurringTemplates[templateId];
    if (template === undefined) throw new LedgerError(`Unknown recurring template "${templateId}"`);
    if (patch.name !== undefined && patch.name === '')
      throw new LedgerError('Name is required (RC-1)');
    const nextBase = patch.base ?? template.base;
    const nextBehavior = patch.behavior ?? template.behavior;
    if (nextBase.amountMinor === undefined && nextBehavior === 'auto_post') {
      throw new LedgerError('A variable-amount template must use confirm, not auto-post (RC-2)');
    }
    if (patch.base !== undefined) this.validateRecurBase(patch.base);
    if (patch.schedule !== undefined) {
      if (!RECUR_FREQUENCIES.includes(patch.schedule.frequency)) {
        throw new LedgerError(`Unknown frequency "${patch.schedule.frequency}"`);
      }
      if (!(patch.schedule.everyN > 0)) throw new LedgerError('everyN must be positive');
    }
    return this.appendRecurringAmendment(meta, { templateId, action: 'edit', ...patch });
  }

  /**
   * RC-2: posts one occurrence now — auto-triggered by the scheduler for `auto_post`
   * templates, or explicitly confirmed (with the real amount) for `confirm` ones. Returns
   * both the underlying entity's event and the template's own "occurred" event.
   */
  postRecurringOccurrence(
    meta: OpMeta,
    templateId: string,
    amountMinorOverride?: number,
  ): LedgerEvent[] {
    const template = this.current.recurringTemplates[templateId];
    if (template === undefined) throw new LedgerError(`Unknown recurring template "${templateId}"`);
    if (template.status !== 'active') throw new LedgerError(`"${templateId}" is not active`);
    const usedAmount = amountMinorOverride ?? template.base.amountMinor;
    if (usedAmount === undefined) {
      throw new LedgerError('This template has a variable amount — provide one to post (RC-2)');
    }
    if (!(usedAmount > 0)) throw new LedgerError('Amount must be positive (§18.5)');

    const entityId = `${meta.id}:entity`;
    const entityMeta: OpMeta = { id: entityId, at: meta.at, source: meta.source ?? 'recurring' };
    const { base } = template;
    let entityEvent: LedgerEvent;
    if (base.kind === 'expense' || base.kind === 'income') {
      entityEvent = this.recordTransaction(entityMeta, {
        transactionId: entityId,
        type: base.kind,
        accountId: base.accountId,
        amountMinor: usedAmount,
        ...(base.category !== undefined ? { category: base.category } : {}),
        ...(base.note !== undefined ? { note: base.note } : {}),
      });
    } else if (base.kind === 'transfer') {
      entityEvent = this.recordTransfer(entityMeta, {
        transferId: entityId,
        fromAccountId: base.fromAccountId,
        toAccountId: base.toAccountId,
        sentMinor: usedAmount,
        ...(base.note !== undefined ? { note: base.note } : {}),
      });
    } else {
      entityEvent = this.createRp(entityMeta, {
        rpId: entityId,
        kind: 'payable',
        party: base.party,
        amountMinor: usedAmount,
        currency: base.currency,
        reason: base.reason,
      });
    }
    const templateEvent = this.appendRecurringAmendment(meta, {
      templateId,
      action: 'occurred',
      nextRunAt: nextOccurrence(template.schedule, template.nextRunAt),
      amountMinor: usedAmount,
    });
    return [entityEvent, templateEvent];
  }

  /** RC-4: "coming / owed" — active templates' occurrences due within `windowDays` of `asOf`. */
  queryUpcomingRecurringOccurrences(
    asOf: string,
    windowDays: number,
  ): { templateId: string; name: string; kind: RecurKind; amountMinor?: number; at: string }[] {
    const endMs = Date.parse(asOf) + windowDays * 24 * 60 * 60 * 1000;
    const results: {
      templateId: string;
      name: string;
      kind: RecurKind;
      amountMinor?: number;
      at: string;
    }[] = [];
    for (const template of Object.values(this.current.recurringTemplates)) {
      if (template.status !== 'active') continue;
      let cursor = template.nextRunAt;
      let guard = 0;
      const amount = template.base.amountMinor ?? template.lastAmountMinor;
      while (Date.parse(cursor) <= endMs && guard < 100) {
        results.push({
          templateId: template.id,
          name: template.name,
          kind: template.base.kind,
          ...(amount !== undefined ? { amountMinor: amount } : {}),
          at: cursor,
        });
        cursor = nextOccurrence(template.schedule, cursor);
        guard += 1;
      }
    }
    return results.sort((a, b) => a.at.localeCompare(b.at));
  }

  // WP-C1.6 (RM-2/3/4): ad-hoc reminders.
  createReminder(
    meta: OpMeta,
    input: {
      reminderId: string;
      text: string;
      nextRunAt: string;
      schedule?: RecurSchedule;
      linkType?: string;
      linkId?: string;
    },
  ): LedgerEvent {
    if (input.text === '') throw new LedgerError('Reminder text is required (RM-2)');
    if (input.reminderId === '') throw new LedgerError('reminderId must not be empty');
    if (this.current.reminders[input.reminderId] !== undefined) {
      throw new LedgerError(`Reminder "${input.reminderId}" already exists`);
    }
    if (Number.isNaN(Date.parse(input.nextRunAt))) {
      throw new LedgerError('nextRunAt must be a valid instant');
    }
    if (input.schedule !== undefined) {
      if (!RECUR_FREQUENCIES.includes(input.schedule.frequency)) {
        throw new LedgerError(`Unknown frequency "${input.schedule.frequency}"`);
      }
      if (!(input.schedule.everyN > 0)) throw new LedgerError('everyN must be positive');
    }
    return this.append({
      id: meta.id,
      kind: 'create',
      entityType: 'reminder',
      entityId: input.reminderId,
      at: meta.at,
      source: meta.source ?? 'manual',
      payload: { ...input, channel: 'whatsapp' },
    });
  }

  cancelReminder(meta: OpMeta, reminderId: string): LedgerEvent {
    const reminder = this.current.reminders[reminderId];
    if (reminder === undefined) throw new LedgerError(`Unknown reminder "${reminderId}"`);
    if (reminder.status !== 'active') throw new LedgerError(`"${reminderId}" is not active`);
    return this.appendReminderAmendment(meta, { reminderId, action: 'cancel' });
  }

  /** RM-3 "remind me again in N" — the caller (route/chat handler) computes the target instant;
   * the engine only validates and records it (AR-7: no clock reads here). Snoozing a `'done'`
   * one-off reminder REVIVES it (the whole point of "remind me again" is re-arming whatever
   * just fired); only an explicitly `'cancelled'` reminder refuses — that's a deliberate
   * owner action, not something a later "again" should silently undo. */
  snoozeReminder(meta: OpMeta, reminderId: string, nextRunAt: string): LedgerEvent {
    const reminder = this.current.reminders[reminderId];
    if (reminder === undefined) throw new LedgerError(`Unknown reminder "${reminderId}"`);
    if (reminder.status === 'cancelled') throw new LedgerError(`"${reminderId}" is cancelled`);
    if (Number.isNaN(Date.parse(nextRunAt)))
      throw new LedgerError('nextRunAt must be a valid instant');
    return this.appendReminderAmendment(meta, { reminderId, action: 'snooze', nextRunAt });
  }

  /** Called by the dispatch processor once a reminder has actually been sent — a one-off
   * reminder is marked done; a recurring one advances to its next occurrence and stays active. */
  fireReminder(meta: OpMeta, reminderId: string, firedAt: string): LedgerEvent {
    const reminder = this.current.reminders[reminderId];
    if (reminder === undefined) throw new LedgerError(`Unknown reminder "${reminderId}"`);
    if (reminder.status !== 'active') throw new LedgerError(`"${reminderId}" is not active`);
    if (reminder.schedule === undefined) {
      return this.appendReminderAmendment(meta, { reminderId, action: 'complete', firedAt });
    }
    return this.appendReminderAmendment(meta, {
      reminderId,
      action: 'occurred',
      firedAt,
      nextRunAt: advanceReminderSchedule(reminder.schedule, reminder.nextRunAt),
    });
  }

  /** S-36: the `/reminders` list, sorted soonest-first. */
  queryReminders(status?: ReminderRecord['status']): ReminderRecord[] {
    return Object.values(this.current.reminders)
      .filter((r) => status === undefined || r.status === status)
      .sort((a, b) => a.nextRunAt.localeCompare(b.nextRunAt));
  }

  /** RM-3's "remind me again in 1 hour" resolves to whichever reminder most recently fired,
   * within a bounded recency window — an old, long-forgotten firing should never be silently
   * resurrected by an unrelated later message (decline rather than guess). */
  queryMostRecentlyFired(asOf: string, withinMs: number): ReminderRecord | undefined {
    const asOfMs = Date.parse(asOf);
    const cutoff = asOfMs - withinMs;
    return Object.values(this.current.reminders)
      .filter((r) => {
        if (r.lastFiredAt === undefined) return false;
        const firedMs = Date.parse(r.lastFiredAt);
        return firedMs >= cutoff && firedMs <= asOfMs; // never "in the future" relative to asOf
      })
      .sort((a, b) => (b.lastFiredAt ?? '').localeCompare(a.lastFiredAt ?? ''))[0];
  }

  // WP-3.1 (M1 PT-1..6): parties.
  createParty(
    meta: OpMeta,
    input: {
      partyId: string;
      name: string;
      roles?: PartyRole[];
      aliases?: string[];
      phone?: string;
      provisional?: boolean;
    },
  ): LedgerEvent {
    if (input.name === '') throw new LedgerError('Party name must not be empty (PT-1)');
    if (input.partyId === '') throw new LedgerError('partyId must not be empty');
    if (this.current.parties[input.partyId] !== undefined) {
      throw new LedgerError(`Party "${input.partyId}" already exists`);
    }
    for (const role of input.roles ?? []) {
      if (!PARTY_ROLES.includes(role)) throw new LedgerError(`Unknown party role "${role}" (PT-1)`);
    }
    return this.append({
      id: meta.id,
      kind: 'create',
      entityType: 'party',
      entityId: input.partyId,
      at: meta.at,
      source: meta.source ?? 'manual',
      payload: input,
    });
  }

  editParty(
    meta: OpMeta,
    partyId: string,
    patch: { name?: string; roles?: PartyRole[]; aliases?: string[]; phone?: string },
  ): LedgerEvent {
    const party = this.current.parties[partyId];
    if (party === undefined) throw new LedgerError(`Unknown party "${partyId}"`);
    if (patch.name !== undefined && patch.name === '')
      throw new LedgerError('Party name must not be empty (PT-1)');
    for (const role of patch.roles ?? []) {
      if (!PARTY_ROLES.includes(role)) throw new LedgerError(`Unknown party role "${role}" (PT-1)`);
    }
    return this.appendPartyAmendment(meta, { partyId, action: 'edit', ...patch });
  }

  /** PT-5: hidden from pickers, retained in history. (True hard-delete has no precedent
   * anywhere in this event-sourced engine — every other entity is archive-only too; see
   * this WP's CHANGELOG entry for why "delete when zero links" is deliberately out of
   * scope rather than silently implemented as a real event-log deletion.) */
  archiveParty(meta: OpMeta, partyId: string): LedgerEvent {
    const party = this.current.parties[partyId];
    if (party === undefined) throw new LedgerError(`Unknown party "${partyId}"`);
    if (party.archived) throw new LedgerError(`Party "${partyId}" is already archived`);
    return this.appendPartyAmendment(meta, { partyId, action: 'archive' });
  }

  unarchiveParty(meta: OpMeta, partyId: string): LedgerEvent {
    const party = this.current.parties[partyId];
    if (party === undefined) throw new LedgerError(`Unknown party "${partyId}"`);
    if (!party.archived) throw new LedgerError(`Party "${partyId}" is not archived`);
    if (party.mergedIntoId !== undefined) {
      throw new LedgerError(`Party "${partyId}" was merged away and cannot be unarchived`);
    }
    return this.appendPartyAmendment(meta, { partyId, action: 'unarchive' });
  }

  /** PT-6: an AI-created provisional party becomes a normal one once the owner confirms it. */
  confirmParty(meta: OpMeta, partyId: string): LedgerEvent {
    const party = this.current.parties[partyId];
    if (party === undefined) throw new LedgerError(`Unknown party "${partyId}"`);
    if (!party.provisional) throw new LedgerError(`Party "${partyId}" is not provisional`);
    return this.appendPartyAmendment(meta, { partyId, action: 'confirm' });
  }

  /**
   * PT-4: merges `mergeId` into `keepId` — every transaction/RP/TPF-case link pointing at
   * `mergeId` via `partyId` is re-pointed via its OWN per-record amendment event (LG-3:
   * individual history, not one opaque bulk event — the same precedent `reassignCategory`
   * already established for bulk re-categorize). The merged-away party is archived with
   * `mergedIntoId` set — never deleted, so its own audit trail (and anything that only ever
   * carried its free-text `party` name, never linked via `partyId`) stays fully intact.
   * `name`/`roles`/`aliases`/`phone` are the keep-picker's chosen values for the SURVIVING
   * party (S-19's "field-by-field keep-picker").
   */
  mergeParties(
    meta: OpMeta,
    input: {
      keepId: string;
      mergeId: string;
      name?: string;
      roles?: PartyRole[];
      aliases?: string[];
      phone?: string;
    },
  ): LedgerEvent[] {
    if (input.keepId === input.mergeId) throw new LedgerError('Cannot merge a party into itself');
    const keep = this.current.parties[input.keepId];
    const merge = this.current.parties[input.mergeId];
    if (keep === undefined) throw new LedgerError(`Unknown party "${input.keepId}"`);
    if (merge === undefined) throw new LedgerError(`Unknown party "${input.mergeId}"`);
    if (keep.mergedIntoId !== undefined)
      throw new LedgerError(`"${input.keepId}" has already been merged away`);
    if (merge.mergedIntoId !== undefined)
      throw new LedgerError(`"${input.mergeId}" has already been merged away`);
    if (input.name !== undefined && input.name === '')
      throw new LedgerError('Party name must not be empty (PT-1)');
    for (const role of input.roles ?? []) {
      if (!PARTY_ROLES.includes(role)) throw new LedgerError(`Unknown party role "${role}" (PT-1)`);
    }

    const events: LedgerEvent[] = [];
    let n = 0;
    const nextMeta = (): OpMeta => ({
      id: `${meta.id}:${String(n++)}`,
      at: meta.at,
      ...(meta.source !== undefined ? { source: meta.source } : {}),
    });

    for (const tx of Object.values(this.current.transactions)) {
      if (tx.partyId !== input.mergeId) continue;
      const createEvent = this.store
        .readEntity('transaction', tx.id)
        .find((e) => e.kind === 'create');
      if (createEvent === undefined) continue; // shouldn't happen; replay stays total
      const relinkMeta = nextMeta();
      events.push(
        this.append({
          id: relinkMeta.id,
          kind: 'correct',
          entityType: 'transaction',
          entityId: tx.id,
          at: relinkMeta.at,
          source: relinkMeta.source ?? 'manual',
          amendsEventId: createEvent.id,
          payload: {
            transactionId: tx.id,
            partyId: input.keepId,
          } satisfies TransactionPartyPatchPayload,
        }),
      );
    }
    for (const rp of Object.values(this.current.rps)) {
      if (rp.partyId !== input.mergeId) continue;
      events.push(
        this.appendRpAmendment(nextMeta(), {
          rpId: rp.id,
          action: 'relinkParty',
          partyId: input.keepId,
        }),
      );
    }
    for (const tpfCase of Object.values(this.current.tpfCases)) {
      if (tpfCase.partyId !== input.mergeId) continue;
      events.push(
        this.appendTpfAmendment(nextMeta(), {
          caseId: tpfCase.id,
          action: 'relinkParty',
          partyId: input.keepId,
        }),
      );
    }

    if (
      input.name !== undefined ||
      input.roles !== undefined ||
      input.aliases !== undefined ||
      input.phone !== undefined
    ) {
      events.push(
        this.appendPartyAmendment(nextMeta(), {
          partyId: input.keepId,
          action: 'edit',
          ...(input.name !== undefined ? { name: input.name } : {}),
          ...(input.roles !== undefined ? { roles: input.roles } : {}),
          ...(input.aliases !== undefined ? { aliases: input.aliases } : {}),
          ...(input.phone !== undefined ? { phone: input.phone } : {}),
        }),
      );
    }

    events.push(
      this.appendPartyAmendment(nextMeta(), {
        partyId: input.mergeId,
        action: 'mergedAway',
        mergedIntoId: input.keepId,
      }),
    );

    return events;
  }

  /** S-19: search-first list — archived and merged-away parties are filtered by the caller
   * (pickers exclude both; the list screen can still show archived ones on request). */
  queryParties(): PartyRecord[] {
    return Object.values(this.current.parties).sort((a, b) => a.name.localeCompare(b.name));
  }

  /** PT-2: the S-20 profile stat strip + tab data. */
  queryPartyProfile(partyId: string): PartyProfileSnapshot {
    const party = this.current.parties[partyId];
    if (party === undefined) throw new LedgerError(`Unknown party "${partyId}"`);

    const receivedTotal = new Map<CurrencyCode, number>();
    const paidTotal = new Map<CurrencyCode, number>();
    const transactionIds: string[] = [];
    for (const tx of Object.values(this.current.transactions)) {
      if (tx.partyId !== partyId || tx.reversed || tx.adjustment === true) continue;
      transactionIds.push(tx.id);
      const bucket = tx.type === 'income' ? receivedTotal : paidTotal;
      bucket.set(tx.currency, (bucket.get(tx.currency) ?? 0) + tx.amountMinor);
    }

    const receivableMinor = new Map<CurrencyCode, number>();
    const payableMinor = new Map<CurrencyCode, number>();
    const rpIds: string[] = [];
    for (const rp of Object.values(this.current.rps)) {
      if (rp.partyId !== partyId) continue;
      rpIds.push(rp.id);
      if (rp.status === 'closed' || rp.status === 'settled') continue;
      const remaining = rp.amountMinor - rp.settledMinor;
      const bucket = rp.kind === 'receivable' ? receivableMinor : payableMinor;
      bucket.set(rp.currency, (bucket.get(rp.currency) ?? 0) + remaining);
    }

    const tpfHeldMinor = new Map<CurrencyCode, number>();
    const tpfCaseIds: string[] = [];
    for (const tpfCase of Object.values(this.current.tpfCases)) {
      if (tpfCase.partyId !== partyId) continue;
      tpfCaseIds.push(tpfCase.id);
      if (tpfCase.status !== 'open') continue;
      tpfHeldMinor.set(
        tpfCase.currency,
        (tpfHeldMinor.get(tpfCase.currency) ?? 0) + tpfCase.owedBackMinor,
      );
    }

    const quotationIds = Object.values(this.current.quotations)
      .filter((q) => q.partyId === partyId)
      .map((q) => q.id);

    const projectIds = Object.values(this.current.projects)
      .filter((p) => p.partyId === partyId)
      .map((p) => p.id);

    const scheduleItemIds = Object.values(this.current.scheduleItems)
      .filter((i) => i.partyId === partyId)
      .map((i) => i.id);

    const toArray = (m: Map<CurrencyCode, number>): CurrencyAmount[] =>
      Array.from(m.entries()).map(([currency, amountMinor]) => ({ currency, amountMinor }));

    return {
      party,
      receivedTotal: toArray(receivedTotal),
      paidTotal: toArray(paidTotal),
      receivableMinor: toArray(receivableMinor),
      payableMinor: toArray(payableMinor),
      tpfHeldMinor: toArray(tpfHeldMinor),
      transactionIds,
      rpIds,
      tpfCaseIds,
      quotationIds,
      projectIds,
      scheduleItemIds,
    };
  }

  private appendPartyAmendment(meta: OpMeta, payload: PartyPatchPayload): LedgerEvent {
    const createEvent = this.store
      .readEntity('party', payload.partyId)
      .find((e) => e.kind === 'create');
    if (createEvent === undefined) throw new LedgerError('Create event missing from log');
    return this.append({
      id: meta.id,
      kind: 'correct',
      entityType: 'party',
      entityId: payload.partyId,
      at: meta.at,
      source: meta.source ?? 'manual',
      amendsEventId: createEvent.id,
      payload,
    });
  }

  // WP-3.2 (M2 QT-1..6, S-21/22): quotations.
  private validateQuotationPricing(pricing: QuotationPricing): void {
    if (pricing.kind === 'fixed') {
      if (!(pricing.amountMinor > 0)) throw new LedgerError('Amount must be positive (§18.5)');
      return;
    }
    if (!(pricing.minMinor > 0) || !(pricing.maxMinor > 0)) {
      throw new LedgerError('Amount must be positive (§18.5)');
    }
    if (!(pricing.minMinor < pricing.maxMinor)) {
      throw new LedgerError('Range minimum must be less than maximum (QT-2)');
    }
  }

  createQuotation(
    meta: OpMeta,
    input: {
      quotationId: string;
      party: string;
      partyId?: string;
      title: string;
      description?: string;
      pricing: QuotationPricing;
      currency: CurrencyCode;
      expiryDate?: string;
      notes?: string;
      paymentTerms?: string;
      expectedStartDate?: string;
    },
  ): LedgerEvent {
    if (input.party === '') throw new LedgerError('Party is required (QT-2)');
    if (input.title === '') throw new LedgerError('Title is required (QT-2)');
    currencyInfo(input.currency); // throws on unknown currency
    this.validateQuotationPricing(input.pricing);
    if (input.quotationId === '') throw new LedgerError('quotationId must not be empty');
    if (this.current.quotations[input.quotationId] !== undefined) {
      throw new LedgerError(`"${input.quotationId}" already exists`);
    }
    if (input.partyId !== undefined && this.current.parties[input.partyId] === undefined) {
      throw new LedgerError(`Unknown party "${input.partyId}"`);
    }
    return this.append({
      id: meta.id,
      kind: 'create',
      entityType: 'quotation',
      entityId: input.quotationId,
      at: meta.at,
      source: meta.source ?? 'manual',
      payload: input,
    });
  }

  /** QT-1: the only legal transitions — read from S-21's own flow arrow
   * ("Draft→Sent→Accepted→Rejected/Expired→Converted"), since the spec never spells out a
   * transition table explicitly. A genuine interpretation, surfaced rather than assumed. */
  private transitionQuotation(
    meta: OpMeta,
    quotationId: string,
    from: readonly QuotationState[],
    to: QuotationState,
  ): LedgerEvent {
    const quotation = this.current.quotations[quotationId];
    if (quotation === undefined) throw new LedgerError(`Unknown quotation "${quotationId}"`);
    if (!from.includes(quotation.status)) {
      throw new LedgerError(
        `Cannot move "${quotationId}" from ${quotation.status} to ${to} (QT-1)`,
      );
    }
    return this.appendQuotationAmendment(meta, { quotationId, action: 'transition', status: to });
  }

  sendQuotation(meta: OpMeta, quotationId: string): LedgerEvent {
    return this.transitionQuotation(meta, quotationId, ['draft'], 'sent');
  }

  acceptQuotation(meta: OpMeta, quotationId: string): LedgerEvent {
    return this.transitionQuotation(meta, quotationId, ['sent'], 'accepted');
  }

  rejectQuotation(meta: OpMeta, quotationId: string): LedgerEvent {
    return this.transitionQuotation(meta, quotationId, ['sent'], 'rejected');
  }

  expireQuotation(meta: OpMeta, quotationId: string): LedgerEvent {
    return this.transitionQuotation(meta, quotationId, ['sent'], 'expired');
  }

  /** QT-5: a revision before acceptance is kept as a version, not an overwrite. */
  reviseQuotationPricing(
    meta: OpMeta,
    quotationId: string,
    pricing: QuotationPricing,
  ): LedgerEvent {
    const quotation = this.current.quotations[quotationId];
    if (quotation === undefined) throw new LedgerError(`Unknown quotation "${quotationId}"`);
    if (quotation.status !== 'draft' && quotation.status !== 'sent') {
      throw new LedgerError(`Cannot revise pricing once a quotation is ${quotation.status} (QT-5)`);
    }
    this.validateQuotationPricing(pricing);
    return this.appendQuotationAmendment(meta, {
      quotationId,
      action: 'revisePricing',
      pricing,
      supersededAt: meta.at,
    });
  }

  /** Non-price fields only — pricing revisions go through `reviseQuotationPricing` (QT-5) so
   * they're always versioned; a converted quotation is frozen (QT-3's "verbatim forever"
   * principle extended to the whole record once it's done). */
  editQuotation(
    meta: OpMeta,
    quotationId: string,
    patch: {
      party?: string;
      partyId?: string;
      title?: string;
      description?: string;
      expiryDate?: string;
      notes?: string;
      paymentTerms?: string;
      expectedStartDate?: string;
    },
  ): LedgerEvent {
    const quotation = this.current.quotations[quotationId];
    if (quotation === undefined) throw new LedgerError(`Unknown quotation "${quotationId}"`);
    if (quotation.status === 'converted')
      throw new LedgerError('Cannot edit a converted quotation (QT-3)');
    if (patch.title !== undefined && patch.title === '')
      throw new LedgerError('Title is required (QT-2)');
    if (patch.party !== undefined && patch.party === '')
      throw new LedgerError('Party is required (QT-2)');
    if (patch.partyId !== undefined && this.current.parties[patch.partyId] === undefined) {
      throw new LedgerError(`Unknown party "${patch.partyId}"`);
    }
    return this.appendQuotationAmendment(meta, { quotationId, action: 'edit', ...patch });
  }

  /**
   * QT-4 "zero re-entry": converting to a receivable is fully real — creates an actual
   * `RpRecord` carrying the quotation's party/currency/title over untouched. Converting to a
   * project or contract only records the INTENT (`convertedToType`) — P3.3's Project entity
   * doesn't exist yet (WP-3.3 is the very next WP); nothing is fabricated to stand in for it.
   * A range-priced quotation has no single "the amount" (QT-3: the range stays verbatim, it's
   * never silently collapsed to a number) — `amountMinorOverride` is REQUIRED for those; a
   * fixed-priced one defaults to its own amount if not overridden.
   */
  convertQuotation(
    meta: OpMeta,
    quotationId: string,
    target: QuotationConvertTarget,
    amountMinorOverride?: number,
  ): LedgerEvent[] {
    const quotation = this.current.quotations[quotationId];
    if (quotation === undefined) throw new LedgerError(`Unknown quotation "${quotationId}"`);
    if (quotation.status !== 'accepted') {
      throw new LedgerError(`Only an accepted quotation can be converted (QT-1)`);
    }
    if (!QUOTATION_CONVERT_TARGETS.includes(target)) {
      throw new LedgerError(`Unknown convert target "${target}"`);
    }

    if (target !== 'receivable') {
      return [
        this.appendQuotationAmendment(meta, {
          quotationId,
          action: 'convert',
          convertedToType: target,
        }),
      ];
    }

    let amountMinor: number;
    if (quotation.pricing.kind === 'fixed') {
      amountMinor = amountMinorOverride ?? quotation.pricing.amountMinor;
    } else {
      if (amountMinorOverride === undefined) {
        throw new LedgerError(
          'A range-priced quotation needs an explicit amount to convert to a receivable (QT-3)',
        );
      }
      amountMinor = amountMinorOverride;
    }
    if (!(amountMinor > 0)) throw new LedgerError('Amount must be positive (§18.5)');

    const rpId = `${meta.id}:rp`;
    const rpMeta: OpMeta = { id: rpId, at: meta.at, source: meta.source ?? 'manual' };
    const rpEvent = this.createRp(rpMeta, {
      rpId,
      kind: 'receivable',
      party: quotation.party,
      ...(quotation.partyId !== undefined ? { partyId: quotation.partyId } : {}),
      amountMinor,
      currency: quotation.currency,
      reason: quotation.title,
    });
    const convertEvent = this.appendQuotationAmendment(meta, {
      quotationId,
      action: 'convert',
      convertedToType: 'receivable',
      convertedToId: rpId,
    });
    return [rpEvent, convertEvent];
  }

  /** S-21 kanban: every quotation, optionally scoped to one column/state. */
  queryQuotations(status?: QuotationState): QuotationRecord[] {
    return Object.values(this.current.quotations)
      .filter((q) => status === undefined || q.status === status)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }

  private appendQuotationAmendment(meta: OpMeta, payload: QuotationPatchPayload): LedgerEvent {
    const createEvent = this.store
      .readEntity('quotation', payload.quotationId)
      .find((e) => e.kind === 'create');
    if (createEvent === undefined) throw new LedgerError('Create event missing from log');
    return this.append({
      id: meta.id,
      kind: 'correct',
      entityType: 'quotation',
      entityId: payload.quotationId,
      at: meta.at,
      source: meta.source ?? 'manual',
      amendsEventId: createEvent.id,
      payload,
    });
  }

  // WP-3.3 (M3 PJ-1..7, S-23/24): projects.
  /**
   * `fromQuotationId` closes QT-4's "zero re-entry" loop: WP-3.2's `convertQuotation` already
   * marked the quotation `status: 'converted'`/`convertedToType: 'project'|'contract'` without
   * an id (Projects didn't exist yet); creating the real project here backfills
   * `convertedToId` on that SAME quotation event chain, so the two-way link is complete.
   */
  createProject(
    meta: OpMeta,
    input: {
      projectId: string;
      party: string;
      partyId?: string;
      title: string;
      serviceCategory?: string;
      productCategory?: string;
      currency: CurrencyCode;
      earningModel: ProjectEarningModel;
      originalQuotedRange?: { minMinor: number; maxMinor: number };
      agreedMinor: number;
      startDate?: string;
      endDate?: string;
      notes?: string;
      fromQuotationId?: string;
    },
  ): LedgerEvent[] {
    if (input.party === '') throw new LedgerError('Party is required (PJ-4)');
    if (input.title === '') throw new LedgerError('Title is required (PJ-4)');
    currencyInfo(input.currency); // throws on unknown currency
    if (!PROJECT_EARNING_MODELS.includes(input.earningModel)) {
      throw new LedgerError(`Unknown earning model "${input.earningModel}" (PJ-2)`);
    }
    if (!(input.agreedMinor > 0)) throw new LedgerError('Amount must be positive (§18.5)');
    if (input.projectId === '') throw new LedgerError('projectId must not be empty');
    if (this.current.projects[input.projectId] !== undefined) {
      throw new LedgerError(`"${input.projectId}" already exists`);
    }
    if (input.partyId !== undefined && this.current.parties[input.partyId] === undefined) {
      throw new LedgerError(`Unknown party "${input.partyId}"`);
    }

    const events: LedgerEvent[] = [
      this.append({
        id: meta.id,
        kind: 'create',
        entityType: 'project',
        entityId: input.projectId,
        at: meta.at,
        source: meta.source ?? 'manual',
        payload: input,
      }),
    ];

    if (input.fromQuotationId !== undefined) {
      const quotation = this.current.quotations[input.fromQuotationId];
      if (quotation === undefined)
        throw new LedgerError(`Unknown quotation "${input.fromQuotationId}"`);
      if (quotation.convertedToType !== 'project' && quotation.convertedToType !== 'contract') {
        throw new LedgerError(
          `Quotation "${input.fromQuotationId}" was not converted to a project/contract (QT-4)`,
        );
      }
      if (quotation.convertedToId === undefined) {
        events.push(
          this.appendQuotationAmendment(
            {
              id: `${meta.id}:link`,
              at: meta.at,
              ...(meta.source !== undefined ? { source: meta.source } : {}),
            },
            {
              quotationId: input.fromQuotationId,
              action: 'convert',
              convertedToType: quotation.convertedToType,
              convertedToId: input.projectId,
            },
          ),
        );
      }
    }
    return events;
  }

  editProject(
    meta: OpMeta,
    projectId: string,
    patch: {
      title?: string;
      serviceCategory?: string;
      productCategory?: string;
      status?: ProjectStatus;
      startDate?: string;
      endDate?: string;
      notes?: string;
    },
  ): LedgerEvent {
    const project = this.current.projects[projectId];
    if (project === undefined) throw new LedgerError(`Unknown project "${projectId}"`);
    if (patch.title !== undefined && patch.title === '')
      throw new LedgerError('Title is required (PJ-4)');
    if (patch.status !== undefined && !PROJECT_STATUSES.includes(patch.status)) {
      throw new LedgerError(`Unknown project status "${patch.status}" (PJ-4)`);
    }
    return this.appendProjectAmendment(meta, { projectId, action: 'edit', ...patch });
  }

  /** PJ-3 (LOCKED): the original agreement (`agreedMinor`) is never touched by this — only
   * ever added onto. A `note` is required for `discount`/`compensation` (S-24's own form
   * spec), everything else is optional. */
  addProjectAdjustment(
    meta: OpMeta,
    projectId: string,
    input: { type: ProjectAdjustmentType; amountMinor: number; date: string; note?: string },
  ): LedgerEvent {
    const project = this.current.projects[projectId];
    if (project === undefined) throw new LedgerError(`Unknown project "${projectId}"`);
    if (!PROJECT_ADJUSTMENT_TYPES.includes(input.type)) {
      throw new LedgerError(`Unknown adjustment type "${input.type}" (PJ-3)`);
    }
    if (input.amountMinor === 0)
      throw new LedgerError('Adjustment amount must not be zero (§18.5)');
    if (
      (input.type === 'discount' || input.type === 'compensation') &&
      (input.note === undefined || input.note === '')
    ) {
      throw new LedgerError(`A note is required for a "${input.type}" adjustment (PJ-3)`);
    }
    const adjustment: ProjectAdjustment = {
      id: meta.id,
      type: input.type,
      amountMinor: input.amountMinor,
      date: input.date,
      ...(input.note !== undefined ? { note: input.note } : {}),
    };
    return this.appendProjectAmendment(meta, { projectId, action: 'addAdjustment', adjustment });
  }

  /** PJ-5: hourly projects only — a manual hours entry generates its own `hourly_accrual`
   * adjustment (rate × hours) in the SAME event, so the two can never drift apart. No live
   * timer (D-7) — this is always a backdated, manually-typed entry. */
  logProjectHours(
    meta: OpMeta,
    projectId: string,
    input: { date: string; hours: number; rateMinor: number; note?: string },
  ): LedgerEvent {
    const project = this.current.projects[projectId];
    if (project === undefined) throw new LedgerError(`Unknown project "${projectId}"`);
    if (project.earningModel !== 'hourly') {
      throw new LedgerError(`Hours can only be logged on an hourly-earning-model project (PJ-5)`);
    }
    if (!(input.hours > 0)) throw new LedgerError('Hours must be positive (§18.5)');
    if (!(input.rateMinor > 0)) throw new LedgerError('Rate must be positive (§18.5)');
    const amountMinor = Math.round(input.hours * input.rateMinor);
    const entry: ProjectHoursEntry = {
      id: `${meta.id}:hours`,
      date: input.date,
      hours: input.hours,
      rateMinor: input.rateMinor,
      ...(input.note !== undefined ? { note: input.note } : {}),
      adjustmentId: meta.id,
    };
    const adjustment: ProjectAdjustment = {
      id: meta.id,
      type: 'hourly_accrual',
      amountMinor,
      date: input.date,
      ...(input.note !== undefined ? { note: input.note } : {}),
    };
    return this.appendProjectAmendment(meta, { projectId, action: 'logHours', entry, adjustment });
  }

  /** PJ-7: a payment recorded before any agreement existed (`projectId` left unset) can be
   * re-linked once the project is known — a real amendment event, history preserved. */
  relinkTransactionToProject(meta: OpMeta, transactionId: string, projectId: string): LedgerEvent {
    const tx = this.current.transactions[transactionId];
    if (tx === undefined) throw new LedgerError(`Unknown transaction "${transactionId}"`);
    if (this.current.projects[projectId] === undefined)
      throw new LedgerError(`Unknown project "${projectId}"`);
    const createEvent = this.store
      .readEntity('transaction', transactionId)
      .find((e) => e.kind === 'create');
    if (createEvent === undefined) throw new LedgerError('Create event missing from log');
    return this.append({
      id: meta.id,
      kind: 'correct',
      entityType: 'transaction',
      entityId: transactionId,
      at: meta.at,
      source: meta.source ?? 'manual',
      amendsEventId: createEvent.id,
      payload: { transactionId, projectId } satisfies TransactionProjectPatchPayload,
    });
  }

  // WP-4.2 "Corrections" (AU-7/AM-3/TX-4/AR-4/LG-2/LG-3): conversational/typed corrections —
  // create/correct/link/split/reverse/reschedule per DEV_PLAN's own WP-4.2 bullet. Every
  // method below is a typed, narrow amendment (LG-2/LG-3: never edit in place, history
  // always preserved), same discriminated-by-payload-key precedent as `reassignCategory`/
  // `relinkTransactionToProject`/`mergeParties` above. Three of AU-7's named verbs are
  // deliberately NOT here, each a documented trim (not a silent gap):
  //   - MERGE ("I entered that twice") is TX-6 duplicate detection — WP-4.3's own named
  //     scope (DEV_PLAN: "WP-4.3 AI-path dedupe... Duplicate-suspect items with twin
  //     preview"), not built here.
  //   - RESCHEDULE is already satisfied by the existing RM-3 reminder-snooze mechanism
  //     (WP-C1.6, `snoozeReminder`) — schedule items aren't created via AI capture, so
  //     there's no natural "reschedule the thing I just captured" anchor for a NEW method.
  //   - The AM-3 high-impact confirm gate (party/account relink always confirm; amount
  //     corrections confirm above `modeACapMinor`) is an ORCHESTRATION decision made by the
  //     caller BEFORE it ever calls one of these methods — the engine (AR-3: the only write
  //     path) has no "pending/unconfirmed" state of its own; every method below applies its
  //     correction immediately and unconditionally once called, exactly like every other
  //     write in this file.

  /** AU-7/AM-3: "actually 550" — corrects a transaction's own amount in place (a NEW
   * `correct` event on the SAME transactionId, never a new transaction). Reuses the exact
   * `fromMinor(...).minorUnits <= 0` positive-amount guard `recordTransaction` already uses. */
  correctTransactionAmount(meta: OpMeta, transactionId: string, amountMinor: number): LedgerEvent {
    const tx = this.current.transactions[transactionId];
    if (tx === undefined) throw new LedgerError(`Unknown transaction "${transactionId}"`);
    if (tx.reversed) {
      throw new LedgerError(
        `Transaction "${transactionId}" has been reversed and cannot be corrected (LG-2)`,
      );
    }
    const amount = fromMinor(tx.currency, amountMinor); // FX-5 integer guard
    if (amount.minorUnits <= 0) {
      throw new LedgerError('Amount must be positive (§18.5); direction comes from type');
    }
    const createEvent = this.store
      .readEntity('transaction', transactionId)
      .find((e) => e.kind === 'create');
    if (createEvent === undefined) throw new LedgerError('Create event missing from log');
    return this.append({
      id: meta.id,
      kind: 'correct',
      entityType: 'transaction',
      entityId: transactionId,
      at: meta.at,
      source: meta.source ?? 'manual',
      amendsEventId: createEvent.id,
      payload: { transactionId, amountMinor } satisfies TransactionAmountPatchPayload,
    });
  }

  /** AU-7: "that was for Ahmad"/"that was Project B"'s party-equivalent — corrects who a
   * transaction was with. Reuses (rather than re-inventing) `mergeParties`' own
   * `TransactionPartyPatchPayload` shape, extended to carry the free-text `party` name
   * alongside the linked `partyId` since a spoken correction may only ever resolve a name.
   * At least one of `party`/`partyId` must be given. */
  correctTransactionParty(
    meta: OpMeta,
    transactionId: string,
    party: string | undefined,
    partyId: string | undefined,
  ): LedgerEvent {
    if (party === undefined && partyId === undefined) {
      throw new LedgerError('Give at least one of party or partyId to correct (AU-7)');
    }
    const tx = this.current.transactions[transactionId];
    if (tx === undefined) throw new LedgerError(`Unknown transaction "${transactionId}"`);
    if (tx.reversed) {
      throw new LedgerError(
        `Transaction "${transactionId}" has been reversed and cannot be corrected (LG-2)`,
      );
    }
    if (partyId !== undefined && this.current.parties[partyId] === undefined) {
      throw new LedgerError(`Unknown party "${partyId}"`);
    }
    const createEvent = this.store
      .readEntity('transaction', transactionId)
      .find((e) => e.kind === 'create');
    if (createEvent === undefined) throw new LedgerError('Create event missing from log');
    return this.append({
      id: meta.id,
      kind: 'correct',
      entityType: 'transaction',
      entityId: transactionId,
      at: meta.at,
      source: meta.source ?? 'manual',
      amendsEventId: createEvent.id,
      payload: {
        transactionId,
        ...(party !== undefined ? { party } : {}),
        ...(partyId !== undefined ? { partyId } : {}),
      } satisfies TransactionPartyPatchPayload,
    });
  }

  /** AM-3 "changes to financial obligations"/"uncertain relinking": moves a transaction onto
   * a different account. Mirrors `relinkTransactionToProject`'s own validation shape (transaction
   * exists, new account exists) PLUS one check that precedent doesn't need: `accountId` — unlike
   * `projectId` — is balance-bearing (LG-4; the projection below moves the transaction's own
   * balance effect from the old account onto the new one), so relinking across a currency
   * boundary with no conversion would silently corrupt the destination account's balanceMinor
   * (a raw integer with no currency tag of its own — FX-5 never mixes currencies within one
   * sum). The new account's currency MUST match the transaction's own frozen `currency`; a
   * genuine cross-currency move is a transfer-and-relink the owner does explicitly, not this
   * method's job. Split/reschedule intents are typed/web-only (S-10) in v1, not spoken — see
   * this section's own header comment; this method has no NL entry point yet either, by the
   * same reasoning. */
  relinkTransactionAccount(meta: OpMeta, transactionId: string, accountId: string): LedgerEvent {
    const tx = this.current.transactions[transactionId];
    if (tx === undefined) throw new LedgerError(`Unknown transaction "${transactionId}"`);
    if (tx.reversed) {
      throw new LedgerError(
        `Transaction "${transactionId}" has been reversed and cannot be corrected (LG-2)`,
      );
    }
    const newAccount = this.current.accounts[accountId];
    if (newAccount === undefined) throw new LedgerError(`Unknown account "${accountId}"`);
    if (newAccount.currency !== tx.currency) {
      throw new LedgerError(
        `Cannot relink across a currency boundary: account "${accountId}" is ${newAccount.currency}, the transaction is ${tx.currency} (FX-5/LG-4)`,
      );
    }
    const createEvent = this.store
      .readEntity('transaction', transactionId)
      .find((e) => e.kind === 'create');
    if (createEvent === undefined) throw new LedgerError('Create event missing from log');
    return this.append({
      id: meta.id,
      kind: 'correct',
      entityType: 'transaction',
      entityId: transactionId,
      at: meta.at,
      source: meta.source ?? 'manual',
      amendsEventId: createEvent.id,
      payload: { transactionId, accountId } satisfies TransactionAccountPatchPayload,
    });
  }

  /** TX-4: S-10's action-menu "split into lines" correction — WEB-ONLY in v1 (not a spoken/
   * WhatsApp NL intent): parsing "split that into rent and utilities, 300 and 200" from free
   * text is a real NLP problem with no existing precedent anywhere in this codebase
   * (Tier-0/Tier-1 both only ever produce ONE amount per segment); attempting it now would be
   * inventing capability with no grounding (mirrors this codebase's own scope-discipline
   * precedents, e.g. WP-3.2's "pricing-revision UI is fixed-amount only").
   *
   * TX-4: sum(lines) MUST equal the transaction's OWN CURRENT amountMinor — a split never
   * changes the total, only how it's divided. Every line's linkType/linkId existence/kind/
   * status is validated the same way `recordTransaction` already does (do not weaken it) —
   * EXCEPT the RP-4/SC-5 "remaining balance" arithmetic, which is deliberately NOT
   * re-checked here: correctly recomputing it requires netting out THIS transaction's own
   * prior settlement/receipt entries before re-validating against the new split, a real
   * reconciliation problem of its own — out of scope for WP-4.2 (documented trim, not a
   * silent gap). The projection (below) REPLACES the lines array wholesale and leaves any
   * RP/schedule-item totals derived from the OLD lines untouched for the same reason. */
  splitTransactionLines(
    meta: OpMeta,
    transactionId: string,
    lines: TransactionLineRecord[],
  ): LedgerEvent {
    const tx = this.current.transactions[transactionId];
    if (tx === undefined) throw new LedgerError(`Unknown transaction "${transactionId}"`);
    if (tx.reversed) {
      throw new LedgerError(
        `Transaction "${transactionId}" has been reversed and cannot be corrected (LG-2)`,
      );
    }
    for (const line of lines) {
      if (line.amountMinor <= 0) {
        throw new LedgerError('Split line amounts must be positive (TX-4)');
      }
      if (line.linkType === 'project' && line.linkId !== undefined) {
        if (this.current.projects[line.linkId] === undefined) {
          throw new LedgerError(`Unknown project "${line.linkId}"`);
        }
      }
      if (
        (line.linkType === 'receivable' || line.linkType === 'payable') &&
        line.linkId !== undefined
      ) {
        const rp = this.current.rps[line.linkId];
        if (rp === undefined) throw new LedgerError(`Unknown ${line.linkType} "${line.linkId}"`);
        if (rp.kind !== line.linkType) {
          throw new LedgerError(
            `Line linkType "${line.linkType}" does not match "${line.linkId}"'s kind "${rp.kind}" (RP-4)`,
          );
        }
        if (rp.status !== 'open')
          throw new LedgerError(`Cannot settle a ${rp.status} ${rp.kind} (RP-2)`);
      }
      if (line.linkType === 'scheduleItem' && line.linkId !== undefined) {
        const item = this.current.scheduleItems[line.linkId];
        if (item === undefined) throw new LedgerError(`Unknown schedule item "${line.linkId}"`);
        if (item.status === 'cancelled') {
          throw new LedgerError(
            `Cannot receive against a cancelled schedule item "${line.linkId}" (SC-2)`,
          );
        }
        if (tx.type !== 'income') {
          throw new LedgerError('Only income can be received against a schedule item (SC-1)');
        }
      }
    }
    // TX-4: sum(lines) === parent's own CURRENT amount — enforced at write time AND
    // re-checked by LG-4, the exact same message shape `recordTransaction` uses.
    const lineSum = sum(
      tx.currency,
      lines.map((l) => fromMinor(tx.currency, l.amountMinor)),
    );
    if (lineSum.minorUnits !== tx.amountMinor) {
      throw new LedgerError(
        `Split lines must sum to the parent amount (TX-4): ` +
          `${String(lineSum.minorUnits)} !== ${String(tx.amountMinor)}`,
      );
    }
    const createEvent = this.store
      .readEntity('transaction', transactionId)
      .find((e) => e.kind === 'create');
    if (createEvent === undefined) throw new LedgerError('Create event missing from log');
    return this.append({
      id: meta.id,
      kind: 'correct',
      entityType: 'transaction',
      entityId: transactionId,
      at: meta.at,
      source: meta.source ?? 'manual',
      amendsEventId: createEvent.id,
      payload: { transactionId, lines } satisfies TransactionLinesPatchPayload,
    });
  }

  /** S-23: the project list, optionally scoped by status/party. */
  queryProjects(filters: { status?: ProjectStatus; partyId?: string } = {}): ProjectRecord[] {
    return Object.values(this.current.projects)
      .filter((p) => filters.status === undefined || p.status === filters.status)
      .filter((p) => filters.partyId === undefined || p.partyId === filters.partyId)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }

  /** MI-3: the four lenses, always reconstructable (§14-3) — nothing here is stored, all of
   * it is derived fresh from the project's own adjustments plus whatever transactions
   * currently link to it (directly via `projectId`, or via a `'project'` split line). */
  queryProjectValueLenses(projectId: string): ProjectValueLenses {
    const project = this.current.projects[projectId];
    if (project === undefined) throw new LedgerError(`Unknown project "${projectId}"`);

    const finalValueMinor =
      project.agreedMinor + project.adjustments.reduce((sum, a) => sum + a.amountMinor, 0);

    let grossReceivedMinor = 0;
    let netUsableMinor = 0;
    for (const tx of Object.values(this.current.transactions)) {
      if (tx.reversed || tx.type !== 'income' || tx.adjustment === true) continue;
      const feesTotal = (tx.feeLegs ?? []).reduce((sum, f) => sum + f.amountMinor, 0);
      if (tx.projectId === projectId) {
        grossReceivedMinor += tx.amountMinor;
        netUsableMinor += tx.amountMinor - feesTotal;
        continue;
      }
      for (const line of tx.lines ?? []) {
        if (line.linkType !== 'project' || line.linkId !== projectId) continue;
        grossReceivedMinor += line.amountMinor;
        netUsableMinor += line.amountMinor; // fees apply to the whole payment, not a share — WP-3.5's own scope
      }
    }

    return {
      ...(project.originalQuotedRange !== undefined
        ? { originalQuotedRange: project.originalQuotedRange }
        : {}),
      agreedMinor: project.agreedMinor,
      finalValueMinor,
      grossReceivedMinor,
      netUsableMinor,
    };
  }

  /** PP-1/PP-2: revenue under a caller-chosen basis (Agreed Value is excluded — PP-2's own
   * three-basis list — a pre-adjustment estimate is never a real profit basis), direct costs
   * (PP-1's own "expenses link to projects" — via the same `projectId`/`'project'`-split-line
   * mechanism revenue already uses), profit, and margin. Never stored, always reconstructable. */
  queryProjectProfitability(
    projectId: string,
    basis: RevenueBasis = 'final',
  ): ProjectProfitability {
    const project = this.current.projects[projectId];
    if (project === undefined) throw new LedgerError(`Unknown project "${projectId}"`);
    if (!REVENUE_BASES.includes(basis))
      throw new LedgerError(`Unknown revenue basis "${basis}" (PP-2)`);

    const lenses = this.queryProjectValueLenses(projectId);
    const revenueMinor =
      basis === 'final'
        ? lenses.finalValueMinor
        : basis === 'gross'
          ? lenses.grossReceivedMinor
          : lenses.netUsableMinor;

    const costs: ProjectCostEntry[] = [];
    for (const tx of Object.values(this.current.transactions)) {
      if (tx.reversed || tx.type !== 'expense' || tx.adjustment === true) continue;
      if (tx.projectId === projectId) {
        costs.push({
          transactionId: tx.id,
          amountMinor: tx.amountMinor,
          at: tx.at,
          ...(tx.category !== undefined ? { category: tx.category } : {}),
        });
        continue;
      }
      for (const line of tx.lines ?? []) {
        if (line.linkType !== 'project' || line.linkId !== projectId) continue;
        costs.push({
          transactionId: tx.id,
          amountMinor: line.amountMinor,
          at: tx.at,
          ...(line.category !== undefined ? { category: line.category } : {}),
        });
      }
    }
    const directCostsMinor = costs.reduce((sum, c) => sum + c.amountMinor, 0);
    const profitMinor = revenueMinor - directCostsMinor;
    return {
      basis,
      revenueMinor,
      directCostsMinor,
      profitMinor,
      marginPercent: revenueMinor > 0 ? profitMinor / revenueMinor : null,
      costs,
    };
  }

  /** PP-3: "which clients/services are actually profitable" — one currency at a time (the
   * same discipline every other multi-entity total in this file uses). Margin is derived
   * from SUMMED revenue/costs per bucket, never averaged per-project percentages (averaging
   * would let a tiny loss-making project outweigh a large profitable one). */
  queryProfitabilityRollups(
    currency: CurrencyCode,
    basis: RevenueBasis = 'final',
  ): ProfitabilityRollups {
    if (!REVENUE_BASES.includes(basis))
      throw new LedgerError(`Unknown revenue basis "${basis}" (PP-2)`);

    interface Bucket {
      label: string;
      revenueMinor: number;
      directCostsMinor: number;
      projectCount: number;
    }
    const byClient = new Map<string, Bucket>();
    const byServiceCategory = new Map<string, Bucket>();
    const byProductCategory = new Map<string, Bucket>();
    const bump = (
      map: Map<string, Bucket>,
      key: string,
      label: string,
      revenueMinor: number,
      directCostsMinor: number,
    ): void => {
      const existing = map.get(key) ?? {
        label,
        revenueMinor: 0,
        directCostsMinor: 0,
        projectCount: 0,
      };
      existing.revenueMinor += revenueMinor;
      existing.directCostsMinor += directCostsMinor;
      existing.projectCount += 1;
      map.set(key, existing);
    };

    for (const project of Object.values(this.current.projects)) {
      if (project.currency !== currency) continue;
      const profitability = this.queryProjectProfitability(project.id, basis);
      bump(
        byClient,
        project.partyId ?? `name:${project.party}`,
        project.party,
        profitability.revenueMinor,
        profitability.directCostsMinor,
      );
      bump(
        byServiceCategory,
        project.serviceCategory ?? 'uncategorized',
        project.serviceCategory ?? 'Uncategorized',
        profitability.revenueMinor,
        profitability.directCostsMinor,
      );
      bump(
        byProductCategory,
        project.productCategory ?? 'uncategorized',
        project.productCategory ?? 'Uncategorized',
        profitability.revenueMinor,
        profitability.directCostsMinor,
      );
    }

    const toRows = (map: Map<string, Bucket>): ProfitabilityRollupRow[] =>
      Array.from(map.entries())
        .map(([key, b]) => {
          const profitMinor = b.revenueMinor - b.directCostsMinor;
          return {
            key,
            label: b.label,
            revenueMinor: b.revenueMinor,
            directCostsMinor: b.directCostsMinor,
            profitMinor,
            marginPercent: b.revenueMinor > 0 ? profitMinor / b.revenueMinor : null,
            projectCount: b.projectCount,
          };
        })
        .sort((a, b) => b.revenueMinor - a.revenueMinor);

    return {
      currency,
      basis,
      byClient: toRows(byClient),
      byServiceCategory: toRows(byServiceCategory),
      byProductCategory: toRows(byProductCategory),
    };
  }

  private miIncomeDimensionKey(
    tx: TransactionRecord,
    dimension: MiIncomeDimension,
  ): { key: string; label: string } {
    if (dimension === 'account') {
      const account = this.current.accounts[tx.accountId];
      return { key: tx.accountId, label: account?.name ?? 'Unknown account' };
    }
    if (dimension === 'client') {
      return tx.party !== undefined && tx.party !== ''
        ? { key: tx.party, label: tx.party }
        : { key: '__none__', label: 'No client' };
    }
    // project, serviceCategory, productCategory, earningModel all resolve via the project link.
    const project = tx.projectId !== undefined ? this.current.projects[tx.projectId] : undefined;
    if (project === undefined) return { key: '__none__', label: 'No project' };
    if (dimension === 'project') return { key: project.id, label: project.title };
    if (dimension === 'serviceCategory') {
      return project.serviceCategory !== undefined
        ? { key: project.serviceCategory, label: project.serviceCategory }
        : { key: '__none__', label: 'Uncategorized' };
    }
    if (dimension === 'productCategory') {
      return project.productCategory !== undefined
        ? { key: project.productCategory, label: project.productCategory }
        : { key: '__none__', label: 'Uncategorized' };
    }
    return { key: project.earningModel, label: project.earningModel }; // 'earningModel'
  }

  private miExpenseDimensionKey(
    tx: TransactionRecord,
    dimension: MiExpenseDimension,
  ): { key: string; label: string } {
    if (dimension === 'category') {
      const category = tx.category ?? 'Uncategorized';
      return { key: category, label: category };
    }
    if (dimension === 'account') {
      const account = this.current.accounts[tx.accountId];
      return { key: tx.accountId, label: account?.name ?? 'Unknown account' };
    }
    // 'project'
    const project = tx.projectId !== undefined ? this.current.projects[tx.projectId] : undefined;
    return project !== undefined
      ? { key: project.id, label: project.title }
      : { key: '__none__', label: 'No project' };
  }

  /** WP-5.2 (M17 MI-1/2, S-25): the dimension engine — one currency, one period, one
   * direction+dimension pair, always sorted richest-first. Split-line income/expense (a
   * single transaction spread across multiple projects, WP-1.3) attributes to the
   * transaction's own PRIMARY `projectId` only, not proportionally per line — a documented
   * simplification, not silently wrong (the whole transaction amount, not a per-line share). */
  queryMiBreakdown(
    direction: MiDirection,
    dimension: MiDimension,
    currency: CurrencyCode,
    period: { from: string; to: string },
    tagId?: string, // WP-C1.8 (SR-2): "tag filter available in analytics"
  ): MiBreakdown {
    const validDimensions: readonly string[] =
      direction === 'income' ? MI_INCOME_DIMENSIONS : MI_EXPENSE_DIMENSIONS;
    if (!validDimensions.includes(dimension)) {
      throw new LedgerError(`Unknown ${direction} dimension "${dimension}" (MI-1/MI-2)`);
    }
    if (tagId !== undefined && this.current.tags[tagId] === undefined) {
      throw new LedgerError(`Unknown tag "${tagId}"`);
    }

    const byKey = new Map<string, MiRow>();
    let totalMinor = 0;
    for (const tx of Object.values(this.current.transactions)) {
      if (tx.reversed || tx.currency !== currency || tx.adjustment === true) continue;
      if (tx.type !== direction) continue;
      if (!this.inPeriod(tx.at, period)) continue;
      if (tagId !== undefined && tx.tagIds?.includes(tagId) !== true) continue;

      const resolved =
        direction === 'income'
          ? this.miIncomeDimensionKey(tx, dimension as MiIncomeDimension)
          : this.miExpenseDimensionKey(tx, dimension as MiExpenseDimension);

      const existing = byKey.get(resolved.key);
      if (existing !== undefined) existing.amountMinor += tx.amountMinor;
      else
        byKey.set(resolved.key, {
          key: resolved.key,
          label: resolved.label,
          amountMinor: tx.amountMinor,
        });
      totalMinor += tx.amountMinor;
    }

    const rows = [...byKey.values()].sort((a, b) => b.amountMinor - a.amountMinor);
    return {
      direction,
      dimension,
      currency,
      period,
      rows,
      totalMinor,
      ...(tagId !== undefined ? { tagId } : {}),
    };
  }

  // Shared with WP-5.4's `reportFeesByChannel` — the SAME "Payment Channel" mapping, not
  // re-derived, so the Flow Explorer and the Fee-%-by-channel report never disagree on labels.
  private static readonly FLOW_CHANNEL_LABELS: Record<AccountType, string> = {
    bank: 'Bank',
    psp_wallet: 'PSP wallet',
    cash: 'Cash',
    mobile_wallet: 'Mobile wallet',
    custom: 'Custom',
  };

  /** WP-5.3 (M18 MF-1/2, S-26): reconstructs the fixed-level money flow for one currency and
   * period. See `FLOW_LEVELS`' own doc comment for the interpretation behind each bucket. */
  queryMoneyFlow(currency: CurrencyCode, period: { from: string; to: string }): MoneyFlow {
    const nodes = new Map<string, FlowNode>();
    const links = new Map<string, FlowLink>();
    const recordsByNode = new Map<string, FlowRecordRef[]>();
    const accountNetInMinor = new Map<string, number>();
    const accountOutMinor = new Map<string, number>();

    const bumpNode = (id: string, level: number, label: string, amountMinor: number): void => {
      if (amountMinor <= 0) return;
      const existing = nodes.get(id);
      if (existing !== undefined) existing.valueMinor += amountMinor;
      else nodes.set(id, { id, level, label, valueMinor: amountMinor });
    };
    const bumpLink = (source: string, target: string, amountMinor: number): void => {
      if (amountMinor <= 0) return;
      const id = `${source}->${target}`;
      const existing = links.get(id);
      if (existing !== undefined) existing.valueMinor += amountMinor;
      else links.set(id, { source, target, valueMinor: amountMinor });
    };
    const attach = (id: string, ref: FlowRecordRef): void => {
      const list = recordsByNode.get(id);
      if (list !== undefined) list.push(ref);
      else recordsByNode.set(id, [ref]);
    };
    const ensureAccountNode = (accountId: string): string => {
      const id = `account:${accountId}`;
      if (!nodes.has(id)) {
        const account = this.current.accounts[accountId];
        nodes.set(id, { id, level: 4, label: account?.name ?? 'Unknown account', valueMinor: 0 });
      }
      return id;
    };

    for (const tx of Object.values(this.current.transactions)) {
      if (tx.reversed || tx.currency !== currency || tx.adjustment === true) continue;
      if (!this.inPeriod(tx.at, period)) continue;
      const ref: FlowRecordRef = { kind: 'transaction', record: tx };

      if (tx.type === 'income') {
        const client = this.miIncomeDimensionKey(tx, 'client');
        const service = this.miIncomeDimensionKey(tx, 'serviceCategory');
        const account = this.current.accounts[tx.accountId];
        const channelType = account?.type ?? 'custom';
        const channelLabel = Ledger.FLOW_CHANNEL_LABELS[channelType];
        const feesMinor = (tx.feeLegs ?? []).reduce((total, fee) => total + fee.amountMinor, 0);
        const netMinor = Math.max(0, tx.amountMinor - feesMinor);

        const clientId = `client:${client.key}`;
        const serviceId = `service:${service.key}`;
        const channelId = `channel:${channelType}`;
        const accountId = ensureAccountNode(tx.accountId);

        bumpNode(clientId, 0, client.label, tx.amountMinor);
        bumpNode(serviceId, 1, service.label, tx.amountMinor);
        bumpNode(channelId, 2, channelLabel, tx.amountMinor);
        bumpLink(clientId, serviceId, tx.amountMinor);
        bumpLink(serviceId, channelId, tx.amountMinor);
        attach(clientId, ref);
        attach(serviceId, ref);
        attach(channelId, ref);

        if (feesMinor > 0) {
          bumpNode('fees', 3, 'Fees', feesMinor);
          bumpLink(channelId, 'fees', feesMinor);
          attach('fees', ref);
        }
        if (netMinor > 0) {
          const accountNode = nodes.get(accountId);
          if (accountNode !== undefined) accountNode.valueMinor += netMinor;
          bumpLink(channelId, accountId, netMinor);
          attach(accountId, ref);
          accountNetInMinor.set(
            tx.accountId,
            (accountNetInMinor.get(tx.accountId) ?? 0) + netMinor,
          );
        }
        continue;
      }

      // expense
      const accountId = ensureAccountNode(tx.accountId);
      const isPeoplePaid = tx.party !== undefined && tx.party !== '';
      const bucket = isPeoplePaid
        ? { id: `people:${tx.party ?? ''}`, label: tx.party ?? '' }
        : (() => {
            const category = this.miExpenseDimensionKey(tx, 'category');
            return { id: `expense:${category.key}`, label: category.label };
          })();

      bumpNode(bucket.id, 6, bucket.label, tx.amountMinor);
      bumpLink(accountId, bucket.id, tx.amountMinor);
      attach(accountId, ref);
      attach(bucket.id, ref);
      accountOutMinor.set(tx.accountId, (accountOutMinor.get(tx.accountId) ?? 0) + tx.amountMinor);
    }

    for (const transfer of Object.values(this.current.transfers)) {
      if (transfer.sentCurrency !== currency) continue;
      if (!this.inPeriod(transfer.initiatedAt, period)) continue;
      const feesMinor = (transfer.feeLegs ?? []).reduce((total, fee) => total + fee.amountMinor, 0);
      const outMinor = transfer.sentMinor + feesMinor;
      if (outMinor <= 0) continue;

      const fromId = ensureAccountNode(transfer.fromAccountId);
      const toAccount = this.current.accounts[transfer.toAccountId];
      const isSavings = toAccount?.spendable === false;
      const bucketId = isSavings ? 'savings' : 'transfers';
      const bucketLabel = isSavings ? 'Savings' : 'Transfers';
      const ref: FlowRecordRef = { kind: 'transfer', record: transfer };

      bumpNode(bucketId, 5, bucketLabel, outMinor);
      bumpLink(fromId, bucketId, outMinor);
      attach(fromId, ref);
      attach(bucketId, ref);
      accountOutMinor.set(
        transfer.fromAccountId,
        (accountOutMinor.get(transfer.fromAccountId) ?? 0) + outMinor,
      );
    }

    for (const [accountId, netInMinor] of accountNetInMinor.entries()) {
      const outMinor = accountOutMinor.get(accountId) ?? 0;
      const remainingMinor = Math.max(0, netInMinor - outMinor);
      if (remainingMinor <= 0) continue;
      const fromId = ensureAccountNode(accountId);
      bumpNode('remaining', 6, 'Remaining', remainingMinor);
      bumpLink(fromId, 'remaining', remainingMinor);
    }

    return {
      currency,
      period,
      nodes: [...nodes.values()],
      links: [...links.values()],
      recordsByNode: Object.fromEntries(recordsByNode),
    };
  }

  // WP-3.7 (M19 TL-1/2, S-29): the timeline itself.

  /** TL-1's three scopes (global/party/project) + TL-2's "filter by event type" — the whole
   * event log, filtered and rendered, never a second write path. */
  queryTimeline(
    filters: { partyId?: string; projectId?: string; entityTypes?: TimelineEntityType[] } = {},
  ): TimelineEntry[] {
    // WP-H2: naming no types means the DEFAULT feed (config-ish excluded), not everything —
    // otherwise completing coverage would have made the owner's main view unreadable.
    const wantedTypes = filters.entityTypes ?? TIMELINE_DEFAULT_ENTITY_TYPES;
    const entries: TimelineEntry[] = [];
    for (const event of this.store.readAll()) {
      if (!(TIMELINE_ENTITY_TYPES as readonly string[]).includes(event.entityType)) continue;
      const entityType = event.entityType as TimelineEntityType;
      if (!wantedTypes.includes(entityType)) continue;
      if (filters.partyId !== undefined && !this.timelineEventTouchesParty(event, filters.partyId))
        continue;
      if (
        filters.projectId !== undefined &&
        !this.timelineEventTouchesProject(event, filters.projectId)
      )
        continue;
      const described = this.describeTimelineEvent(event, entityType);
      if (described === undefined) continue;
      entries.push({
        id: event.id,
        seq: event.seq,
        at: event.at,
        entityType,
        entityId: event.entityId,
        sentence: described.sentence,
        source: event.source,
        ...(described.amountMinor !== undefined ? { amountMinor: described.amountMinor } : {}),
        ...(described.currency !== undefined ? { currency: described.currency } : {}),
      });
    }
    // Business time (`at`), newest first — a backdated CSV-imported entry surfaces on its
    // OWN date, not "today" (`seq`, insertion order, is only the tiebreaker for determinism).
    return entries.sort((a, b) => b.at.localeCompare(a.at) || b.seq - a.seq);
  }

  private timelineEventTouchesParty(event: LedgerEvent, partyId: string): boolean {
    if (event.entityType === 'party') return event.entityId === partyId;
    if (event.entityType === 'transaction')
      return this.current.transactions[event.entityId]?.partyId === partyId;
    if (event.entityType === 'rp') return this.current.rps[event.entityId]?.partyId === partyId;
    if (event.entityType === 'tpfCase')
      return this.current.tpfCases[event.entityId]?.partyId === partyId;
    if (event.entityType === 'quotation')
      return this.current.quotations[event.entityId]?.partyId === partyId;
    if (event.entityType === 'project')
      return this.current.projects[event.entityId]?.partyId === partyId;
    if (event.entityType === 'scheduleItem')
      return this.current.scheduleItems[event.entityId]?.partyId === partyId;
    return false;
  }

  private timelineEventTouchesProject(event: LedgerEvent, projectId: string): boolean {
    if (event.entityType === 'project') return event.entityId === projectId;
    if (event.entityType === 'scheduleItem')
      return this.current.scheduleItems[event.entityId]?.projectId === projectId;
    if (event.entityType === 'transaction') {
      const tx = this.current.transactions[event.entityId];
      if (tx === undefined) return false;
      if (tx.projectId === projectId) return true;
      return (tx.lines ?? []).some((l) => l.linkType === 'project' && l.linkId === projectId);
    }
    return false;
  }

  /** Undefined = skip — not every amendment is feed-worthy (e.g. a bare `relinkParty` after
   * a merge is plumbing, not a business event). */
  private describeTimelineEvent(
    event: LedgerEvent,
    entityType: TimelineEntityType,
  ): TimelineDescription | undefined {
    if (entityType === 'transaction') return this.describeTransactionEvent(event);
    if (entityType === 'transfer') return this.describeTransferEvent(event);
    if (entityType === 'rp') return this.describeRpEvent(event);
    if (entityType === 'tpfCase') return this.describeTpfCaseEvent(event);
    if (entityType === 'quotation') return this.describeQuotationEvent(event);
    if (entityType === 'project') return this.describeProjectEvent(event);
    if (entityType === 'scheduleItem') return this.describeScheduleItemEvent(event);
    return describeSimpleEntityEvent(event, entityType); // WP-H2: the eleven added types
  }

  private describeTransactionEvent(event: LedgerEvent): TimelineDescription | undefined {
    if (event.kind === 'create') {
      const p = event.payload as TransactionPayload;
      const currency = this.current.accounts[p.accountId]?.currency;
      if (p.adjustment === true)
        return {
          sentence: 'Reconciliation adjustment recorded',
          amountMinor: p.amountMinor,
          currency,
        };
      const kindLabel = p.type === 'income' ? 'Income' : 'Expense';
      const suffix =
        p.category !== undefined
          ? ` — ${p.category}`
          : p.party !== undefined
            ? ` — ${p.party}`
            : '';
      return { sentence: `${kindLabel} recorded${suffix}`, amountMinor: p.amountMinor, currency };
    }
    if (event.kind === 'reverse') {
      return {
        sentence: `Transaction reversed${event.reason !== undefined ? `: ${event.reason}` : ''}`,
      };
    }
    // 'correct' — discriminated by which key the patch carries (no `action` field, EX-2's own precedent).
    const p = event.payload as
      | TransactionCategoryPatchPayload
      | TransactionPartyPatchPayload
      | TransactionProjectPatchPayload
      | TransactionAmountPatchPayload
      | TransactionAccountPatchPayload
      | TransactionLinesPatchPayload;
    if ('category' in p) return { sentence: `Transaction re-categorized to "${p.category}"` };
    if ('amountMinor' in p) {
      // WP-4.2 (AU-7): "actually 550" — feed this correction with its own amount/currency so
      // the Timeline doesn't silently omit it (matches the create event's own shape).
      const tx = this.current.transactions[p.transactionId];
      return {
        sentence: 'Transaction amount corrected',
        amountMinor: p.amountMinor,
        currency: tx?.currency,
      };
    }
    if ('accountId' in p) {
      const account = this.current.accounts[p.accountId];
      return { sentence: `Transaction relinked to account "${account?.name ?? p.accountId}"` };
    }
    if ('lines' in p) return { sentence: 'Transaction split into multiple lines' };
    if ('projectId' in p) {
      const project = this.current.projects[p.projectId];
      return { sentence: `Transaction relinked to project "${project?.title ?? p.projectId}"` };
    }
    return { sentence: 'Transaction relinked to a party' }; // party/partyId — WP-3.1's own precedent
  }

  private describeTransferEvent(event: LedgerEvent): TimelineDescription | undefined {
    if (event.kind === 'create') {
      const p = event.payload as TransferPayload;
      const currency = this.current.accounts[p.fromAccountId]?.currency;
      return { sentence: 'Transfer initiated (in transit)', amountMinor: p.sentMinor, currency };
    }
    // 'correct' — the only amendment a transfer has is settling.
    const p = event.payload as TransferSettlePayload;
    const transfer = this.current.transfers[p.transferId];
    const currency =
      transfer !== undefined ? this.current.accounts[transfer.toAccountId]?.currency : undefined;
    return { sentence: 'Transfer settled', amountMinor: p.receivedMinor, currency };
  }

  private describeRpEvent(event: LedgerEvent): TimelineDescription | undefined {
    if (event.kind === 'create') {
      const p = event.payload as RpPayload;
      const kindLabel = p.kind === 'receivable' ? 'Receivable' : 'Payable';
      return {
        sentence: `${kindLabel} opened: ${p.reason}`,
        amountMinor: p.amountMinor,
        currency: p.currency,
      };
    }
    const p = event.payload as RpPatchPayload;
    const rp = this.current.rps[p.rpId];
    const kindLabel = rp?.kind === 'payable' ? 'Payable' : 'Receivable';
    if (p.action === 'settle')
      return {
        sentence: `${kindLabel} settled`,
        amountMinor: p.amountMinor,
        currency: rp?.currency,
      };
    if (p.action === 'close')
      return { sentence: `${kindLabel} closed${p.reason !== '' ? `: ${p.reason}` : ''}` };
    if (p.action === 'reopen') return { sentence: `${kindLabel} reopened` };
    return undefined; // relinkParty — plumbing, not a business event
  }

  private describeTpfCaseEvent(event: LedgerEvent): TimelineDescription | undefined {
    if (event.kind === 'create') {
      const p = event.payload as TpfCasePayload;
      return { sentence: `Third-party fund case opened for ${p.party}`, currency: p.currency };
    }
    const p = event.payload as TpfCasePatchPayload;
    const tpfCase = this.current.tpfCases[p.caseId];
    if (p.action === 'receive')
      return {
        sentence: 'TPF funds received',
        amountMinor: p.caseAmountMinor,
        currency: tpfCase?.currency,
      };
    if (p.action === 'return')
      return {
        sentence: 'TPF funds returned',
        amountMinor: p.caseAmountMinor,
        currency: tpfCase?.currency,
      };
    if (p.action === 'convert')
      return {
        sentence: `TPF funds converted: ${p.reason}`,
        amountMinor: p.caseAmountMinor,
        currency: tpfCase?.currency,
      };
    if (p.action === 'close') return { sentence: 'TPF case closed' };
    return undefined; // relinkParty
  }

  private describeQuotationEvent(event: LedgerEvent): TimelineDescription | undefined {
    if (event.kind === 'create') {
      const p = event.payload as QuotationPayload;
      const amountMinor = p.pricing.kind === 'fixed' ? p.pricing.amountMinor : undefined;
      return { sentence: `Quotation "${p.title}" created`, amountMinor, currency: p.currency };
    }
    const p = event.payload as QuotationPatchPayload;
    const quotation = this.current.quotations[p.quotationId];
    const title = quotation?.title ?? p.quotationId;
    if (p.action === 'transition') return { sentence: `Quotation "${title}" ${p.status}` };
    if (p.action === 'convert')
      return { sentence: `Quotation "${title}" converted to ${p.convertedToType}` };
    if (p.action === 'revisePricing') return { sentence: `Quotation "${title}" pricing revised` };
    return { sentence: `Quotation "${title}" updated` }; // 'edit'
  }

  private describeProjectEvent(event: LedgerEvent): TimelineDescription | undefined {
    if (event.kind === 'create') {
      const p = event.payload as ProjectPayload;
      return {
        sentence: `Project "${p.title}" created`,
        amountMinor: p.agreedMinor,
        currency: p.currency,
      };
    }
    const p = event.payload as ProjectPatchPayload;
    const project = this.current.projects[p.projectId];
    const title = project?.title ?? p.projectId;
    if (p.action === 'addAdjustment') {
      return {
        sentence: `Project "${title}" adjustment: ${p.adjustment.type.replace('_', ' ')}`,
        amountMinor: p.adjustment.amountMinor,
        currency: project?.currency,
      };
    }
    if (p.action === 'logHours') {
      return {
        sentence: `${String(p.entry.hours)}h logged on "${title}"`,
        amountMinor: p.adjustment.amountMinor,
        currency: project?.currency,
      };
    }
    if (p.status !== undefined) return { sentence: `Project "${title}" marked ${p.status}` };
    return { sentence: `Project "${title}" updated` };
  }

  private describeScheduleItemEvent(event: LedgerEvent): TimelineDescription | undefined {
    if (event.kind === 'create') {
      const p = event.payload as ScheduleItemPayload;
      return {
        sentence: `Schedule item created for ${p.party}`,
        amountMinor: p.expectedAmountMinor,
        currency: p.currency,
      };
    }
    const p = event.payload as ScheduleItemPatchPayload;
    const item = this.current.scheduleItems[p.scheduleItemId];
    if (p.action === 'reschedule') {
      return {
        sentence: `Schedule item rescheduled${p.newDate !== undefined ? '' : ' to undated'}`,
        currency: item?.currency,
      };
    }
    if (p.action === 'cancel')
      return { sentence: `Schedule item cancelled: ${p.reason}`, currency: item?.currency };
    return { sentence: 'Schedule item updated', currency: item?.currency }; // 'edit'
  }

  // WP-3.8 (M20 RE-1/3/4, S-27/28): reports.

  /** RE-3/CX-6/D-8 (LOCKED): Calendar (Jan-Dec) vs PK Fiscal (Jul-Jun), the [from, to)
   * instant containing `asOf`. The one place in this engine a year basis is ever resolved. */
  private resolveYearRange(yearBasis: YearBasis, asOfMs: number): { from: string; to: string } {
    const d = new Date(asOfMs);
    const year = d.getUTCFullYear();
    if (yearBasis === 'calendar') {
      return {
        from: new Date(Date.UTC(year, 0, 1)).toISOString(),
        to: new Date(Date.UTC(year + 1, 0, 1)).toISOString(),
      };
    }
    const fiscalStartYear = d.getUTCMonth() >= 6 ? year : year - 1; // Jul (index 6) starts the PK fiscal year
    return {
      from: new Date(Date.UTC(fiscalStartYear, 6, 1)).toISOString(),
      to: new Date(Date.UTC(fiscalStartYear + 1, 6, 1)).toISOString(),
    };
  }

  /** RE-1's seven report families, each a raw `ReportRow[]` — never formatted (FX-5), the
   * caller (web) maps rows to columns/labels/export, the same split `exportTable.ts`'s own
   * `TabularData` already documents. Every family accepts the same params for one uniform
   * parameter bar (S-27/28) even though not every family is genuinely time-scoped (RE-3's
   * own "every TIME-SCOPED report" — Outstanding/Third-party are point-in-time snapshots AS
   * OF the period's end, documented per family below, not silently pretending to filter by it). */
  queryReport(
    family: ReportFamily,
    params: { currency: CurrencyCode; yearBasis: YearBasis; asOf: string },
  ): ReportResult {
    if (!REPORT_FAMILIES.includes(family))
      throw new LedgerError(`Unknown report family "${family}" (RE-1)`);
    if (!YEAR_BASES.includes(params.yearBasis))
      throw new LedgerError(`Unknown year basis "${params.yearBasis}" (RE-3)`);
    const period = this.resolveYearRange(params.yearBasis, Date.parse(params.asOf));
    const rows = this.buildReportRows(family, params.currency, period);
    return { family, currency: params.currency, yearBasis: params.yearBasis, period, rows };
  }

  /** WP-4.5 (QA-1): the SAME per-category aggregation the Expenses report uses
   * (`reportExpenses`), exposed with an arbitrary period rather than RE-3's own whole-year
   * scope — a spoken question like "how much did I spend on food in June" needs a tighter
   * window than any report family offers. */
  queryExpensesInPeriod(currency: CurrencyCode, period: { from: string; to: string }): ReportRow[] {
    return this.reportExpenses(currency, period);
  }

  /** WP-4.5 (QA-1): the fee sibling of queryExpensesInPeriod — the SAME per-transaction fee
   * scan `queryFees`/`reportFees` already do, but period- AND account-scoped together (FE-3's
   * own "fees by platform/channel", over an arbitrary window rather than queryFees's all-time
   * scope or reportFees's whole-year one). Total only — no per-type breakdown, since "total
   * Payoneer fees in 2025" asks for one number, not the Fees report's own row shape. */
  queryFeesInPeriod(
    currency: CurrencyCode,
    period: { from: string; to: string },
    filter?: { accountId?: string },
  ): number {
    let totalMinor = 0;
    for (const tx of Object.values(this.current.transactions)) {
      if (tx.reversed || tx.currency !== currency) continue;
      if (!this.inPeriod(tx.at, period)) continue;
      if (filter?.accountId !== undefined && tx.accountId !== filter.accountId) continue;
      for (const fee of tx.feeLegs ?? []) totalMinor += fee.amountMinor;
    }
    return totalMinor;
  }

  private buildReportRows(
    family: ReportFamily,
    currency: CurrencyCode,
    period: { from: string; to: string },
  ): ReportRow[] {
    if (family === 'income') return this.reportIncome(currency, period);
    if (family === 'expenses') return this.reportExpenses(currency, period);
    if (family === 'fees') return this.reportFees(currency, period);
    if (family === 'cashflow') return this.reportCashflow(currency, period);
    if (family === 'outstanding') return this.reportOutstanding(currency, period);
    if (family === 'currency') return this.reportCurrency(period); // spans ALL currencies by design
    if (family === 'thirdParty') return this.reportThirdParty(currency); // all-time, not period-scoped
    if (family === 'aging') return this.reportAging(currency, period);
    return this.reportFeesByChannel(currency, period); // 'feesByChannel'
  }

  private inPeriod(at: string, period: { from: string; to: string }): boolean {
    return at >= period.from && at < period.to;
  }

  /** "Earned"/"Outstanding" are AS-OF-NOW totals — MI-3's own lenses are never period-scoped
   * either, and a project's agreement/adjustments aren't inherently dated to one report year.
   * Only "Received" is period-filtered, the one column receipt timing genuinely bounds. */
  private reportIncome(currency: CurrencyCode, period: { from: string; to: string }): ReportRow[] {
    return Object.values(this.current.projects)
      .filter((p) => p.currency === currency)
      .map((p) => {
        const lenses = this.queryProjectValueLenses(p.id);
        let receivedMinor = 0;
        for (const tx of Object.values(this.current.transactions)) {
          if (
            tx.reversed ||
            tx.type !== 'income' ||
            tx.adjustment === true ||
            !this.inPeriod(tx.at, period)
          )
            continue;
          if (tx.projectId === p.id) receivedMinor += tx.amountMinor;
          for (const line of tx.lines ?? []) {
            if (line.linkType === 'project' && line.linkId === p.id)
              receivedMinor += line.amountMinor;
          }
        }
        return {
          project: p.title,
          client: p.party,
          earnedMinor: lenses.finalValueMinor,
          receivedMinor,
          outstandingMinor: lenses.finalValueMinor - lenses.grossReceivedMinor,
        };
      });
  }

  /** By category (PJ-6/EX-1's own "a project link is optional") — period-filtered. */
  private reportExpenses(
    currency: CurrencyCode,
    period: { from: string; to: string },
  ): ReportRow[] {
    const byCategory = new Map<string, number>();
    for (const tx of Object.values(this.current.transactions)) {
      if (
        tx.reversed ||
        tx.type !== 'expense' ||
        tx.adjustment === true ||
        tx.currency !== currency
      )
        continue;
      if (!this.inPeriod(tx.at, period)) continue;
      const category = tx.category ?? 'Uncategorized';
      byCategory.set(category, (byCategory.get(category) ?? 0) + tx.amountMinor);
    }
    return Array.from(byCategory.entries())
      .map(([category, amountMinor]) => ({ category, amountMinor }))
      .sort((a, b) => b.amountMinor - a.amountMinor);
  }

  /** FE-3: total fees, by type/channel, conversion losses — period-filtered. */
  private reportFees(currency: CurrencyCode, period: { from: string; to: string }): ReportRow[] {
    const byType = new Map<string, number>();
    for (const tx of Object.values(this.current.transactions)) {
      if (tx.reversed || tx.currency !== currency || !this.inPeriod(tx.at, period)) continue;
      for (const fee of tx.feeLegs ?? []) {
        byType.set(fee.type, (byType.get(fee.type) ?? 0) + fee.amountMinor);
      }
    }
    const rows: ReportRow[] = Array.from(byType.entries()).map(([type, amountMinor]) => ({
      type,
      amountMinor,
    }));
    let conversionLossMinor = 0;
    for (const t of Object.values(this.current.transfers)) {
      if (t.conversionLossMinor === undefined || t.settledAt === undefined) continue;
      if (!this.inPeriod(t.settledAt, period)) continue;
      if (this.current.accounts[t.toAccountId]?.currency !== currency) continue;
      conversionLossMinor += t.conversionLossMinor;
    }
    if (conversionLossMinor !== 0)
      rows.push({ type: 'conversion_loss', amountMinor: conversionLossMinor });
    return rows;
  }

  /** In/out/transfers/net, one row per calendar month spanning the period (12 rows for a
   * full year, whichever basis). Transfers never count toward "net" (TR-1). */
  private reportCashflow(
    currency: CurrencyCode,
    period: { from: string; to: string },
  ): ReportRow[] {
    const rows: ReportRow[] = [];
    let cursor = new Date(period.from);
    const end = new Date(period.to);
    while (cursor < end) {
      const monthStart = cursor.toISOString();
      const nextMonth = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, 1));
      const monthEnd = (nextMonth < end ? nextMonth : end).toISOString();
      let incomeMinor = 0;
      let expensesMinor = 0;
      for (const tx of Object.values(this.current.transactions)) {
        if (tx.reversed || tx.adjustment === true || tx.currency !== currency) continue;
        if (tx.at < monthStart || tx.at >= monthEnd) continue;
        if (tx.type === 'income') incomeMinor += tx.amountMinor;
        else expensesMinor += tx.amountMinor;
      }
      let transfersMinor = 0;
      for (const t of Object.values(this.current.transfers)) {
        if (t.sentCurrency !== currency) continue;
        if (t.initiatedAt < monthStart || t.initiatedAt >= monthEnd) continue;
        transfersMinor += t.sentMinor;
      }
      rows.push({
        month: monthStart.slice(0, 7),
        incomeMinor,
        expensesMinor,
        transfersMinor,
        netMinor: incomeMinor - expensesMinor,
      });
      cursor = nextMonth;
    }
    return rows;
  }

  /** Owed to/by, overdue — a point-in-time snapshot AS OF the period's end, not filtered to
   * "opened during the period" (SC-8's own "owed to me" figure is never period-scoped either). */
  private reportOutstanding(
    currency: CurrencyCode,
    period: { from: string; to: string },
  ): ReportRow[] {
    const asOfMs = Date.parse(period.to);
    return Object.values(this.current.rps)
      .filter((rp) => rp.status === 'open' && rp.currency === currency)
      .map((rp) => ({
        party: rp.party,
        kind: rp.kind,
        remainingMinor: rp.amountMinor - rp.settledMinor,
        overdue: rp.dueDate !== undefined && Date.parse(rp.dueDate) < asOfMs ? 'yes' : 'no',
      }));
  }

  /** The one family that spans every currency by design (RE-1's own "revenue by currency"). */
  private reportCurrency(period: { from: string; to: string }): ReportRow[] {
    const byCurrency = new Map<string, number>();
    for (const tx of Object.values(this.current.transactions)) {
      if (tx.reversed || tx.type !== 'income' || tx.adjustment === true) continue;
      if (!this.inPeriod(tx.at, period)) continue;
      byCurrency.set(tx.currency, (byCurrency.get(tx.currency) ?? 0) + tx.amountMinor);
    }
    return Array.from(byCurrency.entries()).map(([reportCurrency, grossReceivedMinor]) => ({
      currency: reportCurrency,
      grossReceivedMinor,
    }));
  }

  /** Received/returned/outstanding per case — all-time (a TPF case's own lifetime, not one
   * report year; PartyProfileSnapshot's own tpfHeldMinor uses the same all-time discipline). */
  private reportThirdParty(currency: CurrencyCode): ReportRow[] {
    return Object.values(this.current.tpfCases)
      .filter((c) => c.currency === currency)
      .map((c) => ({
        party: c.party,
        receivedMinor: c.receivedMinor,
        returnedMinor: c.returnedMinor,
        outstandingMinor: c.owedBackMinor,
      }));
  }

  /** WP-5.4 (SC-7/RE-2): the SAME `queryAgingBuckets` rollup Expected's own Aging view (WP-3.4)
   * already computes, flattened into report rows — a point-in-time snapshot AS OF the period's
   * end, the SAME "Outstanding" precedent above (`reportOutstanding`), not filtered to "opened
   * during the period". */
  private reportAging(currency: CurrencyCode, period: { from: string; to: string }): ReportRow[] {
    const buckets = this.queryAgingBuckets(currency, period.to);
    return [
      {
        bucket: '1-30 days',
        amountMinor: buckets.days1to30.amountMinor,
        count: buckets.days1to30.count,
      },
      {
        bucket: '31-60 days',
        amountMinor: buckets.days31to60.amountMinor,
        count: buckets.days31to60.count,
      },
      {
        bucket: '61-90 days',
        amountMinor: buckets.days61to90.amountMinor,
        count: buckets.days61to90.count,
      },
      {
        bucket: '90+ days',
        amountMinor: buckets.days90plus.amountMinor,
        count: buckets.days90plus.count,
      },
    ];
  }

  /** WP-5.4 (FE-3, RE-2 "fee-%-of-gross"): gross income received and fees deducted, grouped by
   * the receiving account's `type` (AC-1) — "Payment Channel", the SAME mapping the Flow
   * Explorer (WP-5.3) uses for its own channel level — period-filtered, richest-first by gross.
   * `feePercent` is a fraction (0.024 = 2.4%), the SAME "ratio, not money" convention
   * `ProfitabilityRollupRow.marginPercent` (WP-3.6) already uses; 0 rather than a
   * `ReportRow`-illegal `null` when a channel had no gross activity in the period — a real
   * answer (no fees on no income), not a missing one. */
  private reportFeesByChannel(
    currency: CurrencyCode,
    period: { from: string; to: string },
  ): ReportRow[] {
    const byChannel = new Map<AccountType, { grossMinor: number; feesMinor: number }>();
    for (const tx of Object.values(this.current.transactions)) {
      if (tx.reversed || tx.type !== 'income' || tx.adjustment === true || tx.currency !== currency)
        continue;
      if (!this.inPeriod(tx.at, period)) continue;
      const channelType = this.current.accounts[tx.accountId]?.type ?? 'custom';
      const feesMinor = (tx.feeLegs ?? []).reduce((sum, fee) => sum + fee.amountMinor, 0);
      const existing = byChannel.get(channelType);
      if (existing !== undefined) {
        existing.grossMinor += tx.amountMinor;
        existing.feesMinor += feesMinor;
      } else {
        byChannel.set(channelType, { grossMinor: tx.amountMinor, feesMinor });
      }
    }
    return Array.from(byChannel.entries())
      .map(([channelType, totals]) => ({
        channel: Ledger.FLOW_CHANNEL_LABELS[channelType],
        grossMinor: totals.grossMinor,
        feesMinor: totals.feesMinor,
        feePercent: totals.grossMinor > 0 ? totals.feesMinor / totals.grossMinor : 0,
      }))
      .sort((a, b) => b.grossMinor - a.grossMinor);
  }

  private appendProjectAmendment(meta: OpMeta, payload: ProjectPatchPayload): LedgerEvent {
    const createEvent = this.store
      .readEntity('project', payload.projectId)
      .find((e) => e.kind === 'create');
    if (createEvent === undefined) throw new LedgerError('Create event missing from log');
    return this.append({
      id: meta.id,
      kind: 'correct',
      entityType: 'project',
      entityId: payload.projectId,
      at: meta.at,
      source: meta.source ?? 'manual',
      amendsEventId: createEvent.id,
      payload,
    });
  }

  // WP-3.4 (M4 SC-1..8, S-14): schedule items.
  private static readonly SCHEDULE_DUE_WINDOW_MS = 3 * 24 * 60 * 60 * 1000; // SC-2 "Due" — the
  // spec never defines this window explicitly; §16.11's own notification-matrix row ("schedule
  // item due -> WhatsApp, 3 days before + on day") is the nearest grounded hint, adopted here.

  /** SC-1: one expected payment, within a project or standalone. */
  createScheduleItem(
    meta: OpMeta,
    input: {
      scheduleItemId: string;
      party: string;
      partyId?: string;
      projectId?: string;
      currency: CurrencyCode;
      expectedAmountMinor: number;
      expectedDate?: string;
      milestone?: string;
      confidence: ConfidenceLevel;
    },
  ): LedgerEvent {
    if (input.party === '') throw new LedgerError('Party is required (SC-1)');
    currencyInfo(input.currency); // throws on unknown currency
    if (!(input.expectedAmountMinor > 0)) throw new LedgerError('Amount must be positive (§18.5)');
    if (!CONFIDENCE_LEVELS.includes(input.confidence)) {
      throw new LedgerError(`Unknown confidence "${input.confidence}" (SC-3)`);
    }
    if (input.scheduleItemId === '') throw new LedgerError('scheduleItemId must not be empty');
    if (this.current.scheduleItems[input.scheduleItemId] !== undefined) {
      throw new LedgerError(`"${input.scheduleItemId}" already exists`);
    }
    if (input.partyId !== undefined && this.current.parties[input.partyId] === undefined) {
      throw new LedgerError(`Unknown party "${input.partyId}"`);
    }
    if (input.projectId !== undefined && this.current.projects[input.projectId] === undefined) {
      throw new LedgerError(`Unknown project "${input.projectId}"`);
    }
    return this.append({
      id: meta.id,
      kind: 'create',
      entityType: 'scheduleItem',
      entityId: input.scheduleItemId,
      at: meta.at,
      source: meta.source ?? 'manual',
      payload: input,
    });
  }

  editScheduleItem(
    meta: OpMeta,
    scheduleItemId: string,
    patch: {
      party?: string;
      partyId?: string;
      expectedAmountMinor?: number;
      milestone?: string;
      confidence?: ConfidenceLevel;
    },
  ): LedgerEvent {
    const item = this.current.scheduleItems[scheduleItemId];
    if (item === undefined) throw new LedgerError(`Unknown schedule item "${scheduleItemId}"`);
    if (patch.party !== undefined && patch.party === '')
      throw new LedgerError('Party is required (SC-1)');
    if (patch.expectedAmountMinor !== undefined && !(patch.expectedAmountMinor > 0)) {
      throw new LedgerError('Amount must be positive (§18.5)');
    }
    if (patch.confidence !== undefined && !CONFIDENCE_LEVELS.includes(patch.confidence)) {
      throw new LedgerError(`Unknown confidence "${patch.confidence}" (SC-3)`);
    }
    return this.appendScheduleItemAmendment(meta, { scheduleItemId, action: 'edit', ...patch });
  }

  /** SC-6: preserves the ORIGINAL expected date in history — pass `newDate: undefined` to move
   * back to undated. */
  rescheduleScheduleItem(
    meta: OpMeta,
    scheduleItemId: string,
    newDate: string | undefined,
  ): LedgerEvent {
    const item = this.current.scheduleItems[scheduleItemId];
    if (item === undefined) throw new LedgerError(`Unknown schedule item "${scheduleItemId}"`);
    if (item.status === 'cancelled')
      throw new LedgerError('Cannot reschedule a cancelled schedule item (SC-2)');
    return this.appendScheduleItemAmendment(meta, {
      scheduleItemId,
      action: 'reschedule',
      ...(item.expectedDate !== undefined ? { previousDate: item.expectedDate } : {}),
      ...(newDate !== undefined ? { newDate } : {}),
    });
  }

  /** S-14's own form spec: "Cancel requires reason." */
  cancelScheduleItem(meta: OpMeta, scheduleItemId: string, reason: string): LedgerEvent {
    const item = this.current.scheduleItems[scheduleItemId];
    if (item === undefined) throw new LedgerError(`Unknown schedule item "${scheduleItemId}"`);
    if (item.status === 'paid')
      throw new LedgerError('Cannot cancel a fully paid schedule item (SC-2)');
    if (item.status === 'cancelled') throw new LedgerError('Already cancelled');
    if (reason === '') throw new LedgerError('A cancellation reason is required (§18.5)');
    return this.appendScheduleItemAmendment(meta, { scheduleItemId, action: 'cancel', reason });
  }

  /** SC-2: the full six-value status, computed fresh — Overdue/Due are time-relative and
   * never stored (AR-7: the engine never reads the clock). Paid/cancelled always win (a
   * fully-paid item that's also past its date is "Paid", not "Overdue"). */
  private deriveScheduleItemStatus(item: ScheduleItemRecord, asOfMs: number): ScheduleItemStatus {
    if (item.status === 'cancelled' || item.status === 'paid') return item.status;
    if (item.expectedDate !== undefined) {
      const dueMs = Date.parse(item.expectedDate);
      if (dueMs < asOfMs) return 'overdue';
      if (dueMs - asOfMs <= Ledger.SCHEDULE_DUE_WINDOW_MS) return 'due';
    }
    return item.status; // 'upcoming' | 'partially_paid'
  }

  /** A single item's view — same effective-status computation as `queryScheduleItems`, an
   * O(1) lookup for detail routes. */
  queryScheduleItem(scheduleItemId: string, asOf: string): ScheduleItemView | undefined {
    const item = this.current.scheduleItems[scheduleItemId];
    if (item === undefined) return undefined;
    return {
      ...item,
      effectiveStatus: this.deriveScheduleItemStatus(item, Date.parse(asOf)),
      remainingMinor: item.expectedAmountMinor - item.receivedMinor,
    };
  }

  /** S-14: the schedule item list, with SC-2's full effective status computed at query time. */
  queryScheduleItems(
    filters: { projectId?: string; partyId?: string; confidence?: ConfidenceLevel } = {},
    asOf: string,
  ): ScheduleItemView[] {
    const asOfMs = Date.parse(asOf);
    return Object.values(this.current.scheduleItems)
      .filter((i) => filters.projectId === undefined || i.projectId === filters.projectId)
      .filter((i) => filters.partyId === undefined || i.partyId === filters.partyId)
      .filter((i) => filters.confidence === undefined || i.confidence === filters.confidence)
      .map((i) => ({
        ...i,
        effectiveStatus: this.deriveScheduleItemStatus(i, asOfMs),
        remainingMinor: i.expectedAmountMinor - i.receivedMinor,
      }))
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }

  /** SC-4: 7d/30d/month/year/overdue/undated, each split by confidence — §14-6 "Tentative
   * never inflates Confirmed" is enforced structurally, by keeping the three confidence totals
   * in every bucket separate, never pre-summed. Scoped to one currency, the same "caller
   * picks, the engine never auto-converts" discipline `queryDashboard` already uses. Calendar
   * month/year, UTC (D-8's fiscal-year toggle is a real, documented gap — see CHANGELOG). */
  queryScheduleRollups(currency: CurrencyCode, asOf: string): ScheduleRollups {
    const asOfMs = Date.parse(asOf);
    const next7Ms = asOfMs + 7 * Ledger.ONE_DAY_MS;
    const next30Ms = asOfMs + 30 * Ledger.ONE_DAY_MS;
    const asOfDate = new Date(asOf);
    const monthEndMs = Date.UTC(asOfDate.getUTCFullYear(), asOfDate.getUTCMonth() + 1, 1);
    const yearEndMs = Date.UTC(asOfDate.getUTCFullYear() + 1, 0, 1);

    const empty = (): ConfidenceSplitAmount => ({
      confirmedMinor: 0,
      likelyMinor: 0,
      tentativeMinor: 0,
    });
    const rollups: ScheduleRollups = {
      currency,
      next7Days: empty(),
      next30Days: empty(),
      thisMonth: empty(),
      thisYear: empty(),
      overdue: empty(),
      undated: empty(),
    };
    const add = (
      bucket: ConfidenceSplitAmount,
      confidence: ConfidenceLevel,
      amountMinor: number,
    ): void => {
      if (confidence === 'confirmed') bucket.confirmedMinor += amountMinor;
      else if (confidence === 'likely') bucket.likelyMinor += amountMinor;
      else bucket.tentativeMinor += amountMinor;
    };

    for (const item of Object.values(this.current.scheduleItems)) {
      if (item.currency !== currency || item.status === 'cancelled' || item.status === 'paid')
        continue;
      const remaining = item.expectedAmountMinor - item.receivedMinor;
      if (remaining <= 0) continue;
      if (item.expectedDate === undefined) {
        add(rollups.undated, item.confidence, remaining);
        continue;
      }
      const dueMs = Date.parse(item.expectedDate);
      if (dueMs < asOfMs) {
        add(rollups.overdue, item.confidence, remaining);
        continue;
      }
      if (dueMs <= next7Ms) add(rollups.next7Days, item.confidence, remaining);
      if (dueMs <= next30Ms) add(rollups.next30Days, item.confidence, remaining);
      if (dueMs < monthEndMs) add(rollups.thisMonth, item.confidence, remaining);
      if (dueMs < yearEndMs) add(rollups.thisYear, item.confidence, remaining);
    }
    return rollups;
  }

  /** SC-7: 1-30/31-60/61-90/90+ days overdue, unifying schedule items AND standalone
   * receivables (D-5: the two stay separate entities, unified only at this rollup layer). */
  queryAgingBuckets(currency: CurrencyCode, asOf: string): AgingBuckets {
    const asOfMs = Date.parse(asOf);
    const empty = (): AgingBucket => ({ amountMinor: 0, count: 0 });
    const buckets: AgingBuckets = {
      currency,
      days1to30: empty(),
      days31to60: empty(),
      days61to90: empty(),
      days90plus: empty(),
    };
    const add = (ageDays: number, amountMinor: number): void => {
      if (amountMinor <= 0) return;
      const bucket =
        ageDays <= 30
          ? buckets.days1to30
          : ageDays <= 60
            ? buckets.days31to60
            : ageDays <= 90
              ? buckets.days61to90
              : buckets.days90plus;
      bucket.amountMinor += amountMinor;
      bucket.count += 1;
    };

    for (const rp of Object.values(this.current.rps)) {
      if (rp.kind !== 'receivable' || rp.currency !== currency || rp.status !== 'open') continue;
      if (rp.dueDate === undefined) continue;
      const ageDays = (asOfMs - Date.parse(rp.dueDate)) / Ledger.ONE_DAY_MS;
      if (ageDays <= 0) continue;
      add(ageDays, rp.amountMinor - rp.settledMinor);
    }
    for (const item of Object.values(this.current.scheduleItems)) {
      if (item.currency !== currency || item.status === 'cancelled' || item.status === 'paid')
        continue;
      if (item.expectedDate === undefined) continue;
      const ageDays = (asOfMs - Date.parse(item.expectedDate)) / Ledger.ONE_DAY_MS;
      if (ageDays <= 0) continue;
      add(ageDays, item.expectedAmountMinor - item.receivedMinor);
    }
    return buckets;
  }

  private appendScheduleItemAmendment(
    meta: OpMeta,
    payload: ScheduleItemPatchPayload,
  ): LedgerEvent {
    const createEvent = this.store
      .readEntity('scheduleItem', payload.scheduleItemId)
      .find((e) => e.kind === 'create');
    if (createEvent === undefined) throw new LedgerError('Create event missing from log');
    return this.append({
      id: meta.id,
      kind: 'correct',
      entityType: 'scheduleItem',
      entityId: payload.scheduleItemId,
      at: meta.at,
      source: meta.source ?? 'manual',
      amendsEventId: createEvent.id,
      payload,
    });
  }

  /** M16 (DB-1..5): the whole dashboard in one call — pure aggregation, appends nothing. */
  queryDashboard(
    currency: CurrencyCode,
    asOf: string,
    period: { from: string; to: string },
  ): DashboardSnapshot {
    const accounts = Object.values(this.current.accounts).filter(
      (a) => !a.archived && a.currency === currency,
    );
    const availableMinor = accounts
      .filter((a) => a.spendable)
      .reduce((sum, a) => sum + a.balanceMinor, 0);
    const totalBalanceMinor = accounts.reduce((sum, a) => sum + a.balanceMinor, 0);
    const inTransitMinor = this.inTransitTotal(currency);
    const holdingForOthersMinor = this.queryHoldingForOthers(currency);
    const reservedMinor = this.queryReservedTotal(currency); // DB-1 final (WP-6.1) — was a documented 0 placeholder

    const dueSoonMs = Date.parse(asOf) + 7 * 24 * 60 * 60 * 1000;
    let payableTotalMinor = 0;
    let payableDueSoonMinor = 0;
    let receivableTotalMinor = 0;
    let receivableOverdueMinor = 0;
    for (const rp of Object.values(this.current.rps)) {
      if (rp.status === 'closed' || rp.currency !== currency) continue;
      const remaining = rp.amountMinor - rp.settledMinor;
      if (rp.kind === 'payable') {
        payableTotalMinor += remaining;
        if (rp.dueDate !== undefined && Date.parse(rp.dueDate) <= dueSoonMs)
          payableDueSoonMinor += remaining;
      } else {
        receivableTotalMinor += remaining;
        if (rp.dueDate !== undefined && Date.parse(rp.dueDate) < Date.parse(asOf)) {
          receivableOverdueMinor += remaining;
        }
      }
    }
    // SC-8: "Owed to me" = outstanding standalone receivables (above) + unpaid CONFIRMED
    // schedule items — Likely/Tentative never inflate this figure (§14-6). ScheduleItem and
    // RP stay separate entities (D-5); this loop is SC-8's own "explicit link rule" made
    // concrete — each pool is summed once, independently, into the same total.
    for (const item of Object.values(this.current.scheduleItems)) {
      if (item.currency !== currency || item.confidence !== 'confirmed') continue;
      if (item.status === 'cancelled' || item.status === 'paid') continue;
      const remaining = item.expectedAmountMinor - item.receivedMinor;
      if (remaining <= 0) continue;
      receivableTotalMinor += remaining;
      if (item.expectedDate !== undefined && Date.parse(item.expectedDate) < Date.parse(asOf)) {
        receivableOverdueMinor += remaining;
      }
    }

    let upcomingRecurringInflowMinor = 0;
    let upcomingRecurringBillsMinor = 0;
    for (const occurrence of this.queryUpcomingRecurringOccurrences(asOf, 30)) {
      const template = this.current.recurringTemplates[occurrence.templateId];
      if (template === undefined || occurrence.amountMinor === undefined) continue;
      const occurrenceCurrency =
        template.base.kind === 'payable'
          ? template.base.currency
          : this.recurBaseCurrency(template.base);
      if (occurrenceCurrency !== currency) continue;
      if (occurrence.kind === 'income') upcomingRecurringInflowMinor += occurrence.amountMinor;
      else if (occurrence.kind === 'expense') upcomingRecurringBillsMinor += occurrence.amountMinor;
    }

    let grossIncomeMinor = 0;
    let expensesMinor = 0;
    let feesMinor = 0;
    for (const tx of Object.values(this.current.transactions)) {
      if (tx.reversed || tx.currency !== currency) continue;
      if (tx.adjustment === true) continue; // TX-1: adjustments are reconciliation-only, never real income/expense
      if (tx.at < period.from || tx.at > period.to) continue;
      const fees = (tx.feeLegs ?? []).reduce((sum, f) => sum + f.amountMinor, 0);
      feesMinor += fees;
      if (tx.type === 'income') grossIncomeMinor += tx.amountMinor;
      else expensesMinor += tx.amountMinor;
    }

    return {
      currency,
      availableMinor,
      inTransitMinor,
      holdingForOthersMinor,
      reservedMinor,
      spendableNowMinor: availableMinor - reservedMinor - holdingForOthersMinor,
      netPositionMinor: totalBalanceMinor + inTransitMinor - holdingForOthersMinor,
      strictViolation: this.queryStrictReserveViolation(currency),
      upcomingRecurringInflowMinor,
      upcomingRecurringBillsMinor,
      payableTotalMinor,
      payableDueSoonMinor,
      receivableTotalMinor,
      receivableOverdueMinor,
      period,
      grossIncomeMinor,
      expensesMinor,
      feesMinor,
      netIncomeMinor: grossIncomeMinor - feesMinor,
      netCashFlowMinor: grossIncomeMinor - expensesMinor - feesMinor,
    };
  }

  /** AC-5: reuses `queryDashboard`'s own `netPositionMinor` per currency — never a parallel
   * aggregation — converted through the SAME offline FX-3 rate cache TransferForm/Settings
   * already read/write. Deliberately does NOT fetch or guess a rate: no live daily-rate
   * provider has been chosen for this app (CFG-1, an open owner decision since WP-1.6), so a
   * currency with nothing cached simply doesn't contribute to `totalMinor` yet. */
  queryConsolidatedNetWorth(baseCurrency: CurrencyCode, asOf: string): ConsolidatedNetWorth {
    const currencies = new Set(
      Object.values(this.current.accounts)
        .filter((a) => !a.archived)
        .map((a) => a.currency),
    );
    const perCurrency = [...currencies].map((currency) => {
      const netPositionMinor = this.queryDashboard(currency, asOf, {
        from: asOf,
        to: asOf,
      }).netPositionMinor;
      if (currency === baseCurrency) {
        return { currency, netPositionMinor, convertedMinor: netPositionMinor, rate: 1 };
      }
      const cached = this.getFxRate(currency, baseCurrency);
      return {
        currency,
        netPositionMinor,
        convertedMinor: cached !== undefined ? Math.round(netPositionMinor * cached.rate) : null,
        rate: cached?.rate ?? null,
      };
    });
    const totalMinor = perCurrency.reduce((sum, c) => sum + (c.convertedMinor ?? 0), 0);
    return { baseCurrency, totalMinor, perCurrency };
  }

  /** The currency an expense/income/transfer recurring base is denominated in (via its account). */
  private recurBaseCurrency(
    base: Exclude<RecurBase, { kind: 'payable' }>,
  ): CurrencyCode | undefined {
    const accountId = base.kind === 'transfer' ? base.fromAccountId : base.accountId;
    return this.current.accounts[accountId]?.currency;
  }

  private static readonly STALE_IN_TRANSIT_DAYS = 5; // owner-approved default (2026-07-08 defaults pass)
  private static readonly DAY_GAP_THRESHOLD_DAYS = 7; // an internal silent stretch this long is worth a look
  private static readonly ONE_DAY_MS = 24 * 60 * 60 * 1000;

  // OB-4/S-13: "guided hunt" candidate finders + drift, scoped to one account. A pure read —
  // nothing is written until the owner records an Adjustment (recordAdjustment).
  queryReconciliation(
    accountId: string,
    statedBalanceMinor: number,
    asOf: string,
  ): ReconciliationSnapshot {
    const account = this.current.accounts[accountId];
    if (account === undefined) throw new LedgerError(`Unknown account "${accountId}"`);

    const accountTxs = Object.values(this.current.transactions)
      .filter((t) => t.accountId === accountId && !t.reversed)
      .sort((a, b) => a.at.localeCompare(b.at));

    const candidates: ReconcileCandidate[] = [];

    for (const tx of accountTxs) {
      if (tx.adjustment !== true && tx.category === undefined) {
        candidates.push({
          kind: 'uncategorized',
          transactionId: tx.id,
          amountMinor: tx.amountMinor,
          at: tx.at,
        });
      }
    }

    // TX-6, applied within one account's own history: any two same-type, same-amount
    // transactions within ±1 day of each other — each later one flags the earlier.
    for (let i = 0; i < accountTxs.length; i += 1) {
      for (let j = i + 1; j < accountTxs.length; j += 1) {
        const a = accountTxs[i];
        const b = accountTxs[j];
        if (a === undefined || b === undefined) continue;
        // TX-6: an adjustment is reconciliation-only, never itself a duplicate suspect and
        // never the "original" another transaction gets flagged against either — same
        // discipline as findDuplicateTransactionId/indexDuplicateCandidate above.
        if (a.adjustment === true || b.adjustment === true) continue;
        if (a.type !== b.type || a.amountMinor !== b.amountMinor) continue;
        if (Math.abs(Date.parse(b.at) - Date.parse(a.at)) > Ledger.ONE_DAY_MS) continue;
        candidates.push({
          kind: 'duplicate',
          transactionId: b.id,
          duplicateOfTransactionId: a.id,
          amountMinor: b.amountMinor,
          at: b.at,
        });
      }
    }

    const asOfMs = Date.parse(asOf);
    for (const tr of Object.values(this.current.transfers)) {
      if (tr.status !== 'in_transit') continue;
      if (tr.fromAccountId !== accountId && tr.toAccountId !== accountId) continue;
      const ageDays = (asOfMs - Date.parse(tr.initiatedAt)) / Ledger.ONE_DAY_MS;
      if (ageDays < Ledger.STALE_IN_TRANSIT_DAYS) continue;
      candidates.push({
        kind: 'stale_in_transit',
        transferId: tr.id,
        sentMinor: tr.sentMinor,
        initiatedAt: tr.initiatedAt,
        daysStale: Math.floor(ageDays),
      });
    }

    // Internal silent stretches only (never before-the-first or after-the-last transaction —
    // those aren't gaps, they're "not started yet"/"not due yet").
    const distinctDates = Array.from(new Set(accountTxs.map((t) => t.at.slice(0, 10)))).sort();
    for (let i = 1; i < distinctDates.length; i += 1) {
      const prevDate = distinctDates[i - 1];
      const nextDate = distinctDates[i];
      if (prevDate === undefined || nextDate === undefined) continue;
      const gapDays = Math.round((Date.parse(nextDate) - Date.parse(prevDate)) / Ledger.ONE_DAY_MS);
      if (gapDays >= Ledger.DAY_GAP_THRESHOLD_DAYS) {
        candidates.push({ kind: 'day_gap', sinceDate: prevDate, gapDays });
      }
    }

    return {
      accountId,
      currency: account.currency,
      computedBalanceMinor: account.balanceMinor,
      statedBalanceMinor,
      driftMinor: statedBalanceMinor - account.balanceMinor,
      candidates,
    };
  }

  // exactOptionalPropertyTypes: `subtitle?`/`amountMinor?`/`currency?` on SearchResult forbid
  // an explicit `undefined` value — the same `compact()`-style stripping every other WP's
  // optional-field construction needed (e.g. recurring.ts's `compactRecurBase`).
  private toSearchResult(draft: {
    kind: SearchResultKind;
    id: string;
    title: string;
    subtitle: string | undefined;
    amountMinor: number | undefined;
    currency: CurrencyCode | undefined;
    at: string;
    score: number;
  }): SearchResult {
    const { kind, id, title, subtitle, amountMinor, currency, at, score } = draft;
    return {
      kind,
      id,
      title,
      at,
      score,
      ...(subtitle !== undefined ? { subtitle } : {}),
      ...(amountMinor !== undefined ? { amountMinor } : {}),
      ...(currency !== undefined ? { currency } : {}),
    };
  }

  // WP-1.12 (SR-1): a bare/≈-prefixed amount ("50000", "≈500 usd") switches the whole query
  // into amount-matching mode (every result kind scored on its own amount instead of text) —
  // SR-1's own examples are amount-only queries, never mixed with words, so there's no need
  // to split a query into text + amount halves.
  querySearch(query: string, limit = 20): SearchResult[] {
    const trimmed = query.trim();
    if (trimmed === '') return [];
    const amountQuery = parseSearchAmount(trimmed);
    const results: SearchResult[] = [];

    const scoreOf = (
      texts: readonly (string | undefined)[],
      amountMinor?: number,
      currency?: CurrencyCode,
    ): number => {
      if (amountQuery !== undefined) {
        return amountMinor !== undefined && currency !== undefined
          ? amountMatchScore(amountQuery, amountMinor, currency)
          : 0;
      }
      let best = 0;
      for (const t of texts) {
        if (t === undefined) continue;
        const s = textMatchScore(trimmed, t);
        if (s > best) best = s;
      }
      return best;
    };

    for (const tx of Object.values(this.current.transactions)) {
      if (tx.reversed) continue;
      const tagNames = (tx.tagIds ?? []).map((id) => this.current.tags[id]?.name);
      const score = scoreOf(
        [tx.category, tx.party, tx.note, ...tagNames],
        tx.amountMinor,
        tx.currency,
      );
      if (score > 0) {
        results.push(
          this.toSearchResult({
            kind: 'transaction',
            id: tx.id,
            title: tx.category ?? (tx.type === 'income' ? 'Income' : 'Expense'),
            subtitle: tx.note ?? tx.party,
            amountMinor: tx.amountMinor,
            currency: tx.currency,
            at: tx.at,
            score,
          }),
        );
      }
    }

    for (const tr of Object.values(this.current.transfers)) {
      const destCurrency = this.current.accounts[tr.toAccountId]?.currency;
      const score =
        amountQuery !== undefined
          ? Math.max(
              amountMatchScore(amountQuery, tr.sentMinor, tr.sentCurrency),
              tr.receivedMinor !== undefined && destCurrency !== undefined
                ? amountMatchScore(amountQuery, tr.receivedMinor, destCurrency)
                : 0,
            )
          : scoreOf([tr.note]);
      if (score > 0) {
        results.push(
          this.toSearchResult({
            kind: 'transfer',
            id: tr.id,
            title: 'Transfer',
            subtitle: tr.note ?? tr.status,
            amountMinor: tr.sentMinor,
            currency: tr.sentCurrency,
            at: tr.initiatedAt,
            score,
          }),
        );
      }
    }

    for (const rp of Object.values(this.current.rps)) {
      const remaining = rp.amountMinor - rp.settledMinor;
      const score =
        amountQuery !== undefined
          ? Math.max(
              amountMatchScore(amountQuery, rp.amountMinor, rp.currency),
              amountMatchScore(amountQuery, remaining, rp.currency),
            )
          : scoreOf([rp.party, rp.reason, rp.notes]);
      if (score > 0) {
        results.push(
          this.toSearchResult({
            kind: 'rp',
            id: rp.id,
            title: rp.party,
            subtitle: rp.reason,
            amountMinor: remaining,
            currency: rp.currency,
            at: rp.createdAt,
            score,
          }),
        );
      }
    }

    for (const tpf of Object.values(this.current.tpfCases)) {
      const score =
        amountQuery !== undefined
          ? Math.max(
              amountMatchScore(amountQuery, tpf.receivedMinor, tpf.currency),
              amountMatchScore(amountQuery, tpf.owedBackMinor, tpf.currency),
            )
          : scoreOf([tpf.party]);
      if (score > 0) {
        results.push(
          this.toSearchResult({
            kind: 'tpf',
            id: tpf.id,
            title: tpf.party,
            subtitle: `${tpf.status} — owed back ${String(tpf.owedBackMinor)}`,
            amountMinor: tpf.owedBackMinor,
            currency: tpf.currency,
            at: tpf.closedAt ?? tpf.movements[0]?.at ?? '',
            score,
          }),
        );
      }
    }

    for (const account of Object.values(this.current.accounts)) {
      if (account.archived) continue;
      const score = scoreOf([account.name], account.balanceMinor, account.currency);
      if (score > 0) {
        results.push(
          this.toSearchResult({
            kind: 'account',
            id: account.id,
            title: account.name,
            subtitle: account.type,
            amountMinor: account.balanceMinor,
            currency: account.currency,
            at: account.openingAsOf,
            score,
          }),
        );
      }
    }

    for (const template of Object.values(this.current.recurringTemplates)) {
      const currency =
        template.base.kind === 'payable'
          ? template.base.currency
          : this.recurBaseCurrency(template.base);
      const amountMinor = template.base.amountMinor ?? template.lastAmountMinor;
      const texts =
        template.base.kind === 'payable'
          ? [template.name, template.base.party, template.base.reason]
          : [
              template.name,
              template.base.kind === 'transfer' ? undefined : template.base.category,
              template.base.note,
            ];
      const score = scoreOf(texts, amountMinor, currency);
      if (score > 0) {
        results.push(
          this.toSearchResult({
            kind: 'recurring_template',
            id: template.id,
            title: template.name,
            subtitle: template.base.kind,
            amountMinor,
            currency,
            at: template.nextRunAt,
            score,
          }),
        );
      }
    }

    results.sort((a, b) => (b.score !== a.score ? b.score - a.score : b.at.localeCompare(a.at)));
    return results.slice(0, limit);
  }

  private appendRecurringAmendment(
    meta: OpMeta,
    payload: RecurringTemplatePatchPayload,
  ): LedgerEvent {
    const createEvent = this.store
      .readEntity('recurringTemplate', payload.templateId)
      .find((e) => e.kind === 'create');
    if (createEvent === undefined) throw new LedgerError('Create event missing from log');
    return this.append({
      id: meta.id,
      kind: 'correct',
      entityType: 'recurringTemplate',
      entityId: payload.templateId,
      at: meta.at,
      source: meta.source ?? 'manual',
      amendsEventId: createEvent.id,
      payload,
    });
  }

  private appendReminderAmendment(meta: OpMeta, payload: ReminderPatchPayload): LedgerEvent {
    const createEvent = this.store
      .readEntity('reminder', payload.reminderId)
      .find((e) => e.kind === 'create');
    if (createEvent === undefined) throw new LedgerError('Create event missing from log');
    return this.append({
      id: meta.id,
      kind: 'correct',
      entityType: 'reminder',
      entityId: payload.reminderId,
      at: meta.at,
      source: meta.source ?? 'manual',
      amendsEventId: createEvent.id,
      payload,
    });
  }

  private append(event: Parameters<EventStore['append']>[0]): LedgerEvent {
    // WP-F3 (§18.4, SE-6): the label invariant, at the one funnel every write passes through and
    // replay does not. `reason` is sanitised at the EVENT level too — `reverseTransaction` and
    // `recordAdjustment` carry it there rather than in the payload, and it is rendered to the
    // owner just the same.
    const sanitised = {
      ...event,
      ...(event.reason !== undefined ? { reason: safeLabel('reason', event.reason) } : {}),
      payload: sanitiseValue(event.payload),
    } as Parameters<EventStore['append']>[0];
    const stored = this.store.append(sanitised);
    this.current = ledgerProjection.apply(this.current, stored);
    return stored;
  }
}

export { MoneyError };

/** WP-H2 (TL-1/2, LG-3): timeline sentences for the eleven entity types the feed did not cover.
 *
 * These are deliberately ONE function rather than eleven methods: unlike transactions or RPs —
 * whose amendments carry real financial meaning and each need their own phrasing — every type
 * here follows the same create / amend / (sometimes) archive shape, and the useful content is
 * simply the record's own name. Spelling that out eleven times would be duplication pretending
 * to be thoroughness.
 *
 * Every branch returns a real sentence. `undefined` would silently drop the entry from the feed,
 * which is precisely the invisibility this work package exists to fix. */
function describeSimpleEntityEvent(
  event: LedgerEvent,
  entityType: TimelineEntityType,
): TimelineDescription | undefined {
  const payload = event.payload as Record<string, unknown>;
  const nameOf = (): string | undefined => {
    for (const key of ['name', 'purpose', 'text', 'title']) {
      const value = payload[key];
      if (typeof value === 'string' && value !== '') return value;
    }
    return undefined;
  };
  const named = nameOf();
  const suffix = named !== undefined ? ` — ${named}` : '';

  const LABELS: Record<string, string> = {
    party: 'Contact',
    goal: 'Goal',
    reserve: 'Reserve',
    suggestion: 'Allocation suggestion',
    account: 'Account',
    category: 'Category',
    tag: 'Tag',
    fxRate: 'FX rate',
    importSource: 'Import source',
    recurringTemplate: 'Recurring template',
    reminder: 'Reminder',
  };
  const label = LABELS[entityType] ?? entityType;

  // An amount where the type genuinely carries one, so the feed can show it the same way it
  // already does for transactions (never invented — only read from the payload).
  const amountKey = entityType === 'goal' ? 'targetAmountMinor' : 'amountMinor';
  const rawAmount = payload[amountKey];
  const amountMinor = typeof rawAmount === 'number' ? rawAmount : undefined;
  const rawCurrency = payload.currency;
  const currency = typeof rawCurrency === 'string' ? (rawCurrency as CurrencyCode) : undefined;

  if (entityType === 'fxRate') {
    // An FX rate has no name; the pair and the rate ARE the content, and a bare "FX rate updated"
    // would tell the owner nothing about what actually changed.
    const from = typeof payload.from === 'string' ? payload.from : '?';
    const to = typeof payload.to === 'string' ? payload.to : '?';
    const rate = typeof payload.rate === 'number' ? payload.rate : undefined;
    const rateText =
      rate !== undefined ? ` — ${from}/${to} at ${String(rate)}` : ` — ${from}/${to}`;
    return { sentence: `FX rate ${event.kind === 'create' ? 'set' : 'updated'}${rateText}` };
  }

  if (event.kind === 'create') {
    return {
      sentence: `${label} created${suffix}`,
      ...(amountMinor !== undefined ? { amountMinor } : {}),
      ...(currency !== undefined ? { currency } : {}),
    };
  }
  if (event.kind === 'reverse') return { sentence: `${label} reversed${suffix}` };

  // 'correct' / 'adjust' / 'relink'. Several of these payloads carry an `action` or an `archived`
  // flag that says what the amendment actually was — using it beats a generic "updated".
  const action = payload.action;
  if (typeof action === 'string' && action !== '') {
    return { sentence: `${label} ${action}${suffix}` };
  }
  const archived = payload.archived;
  if (archived === true) return { sentence: `${label} archived${suffix}` };
  if (archived === false) return { sentence: `${label} restored${suffix}` };

  return { sentence: `${label} updated${suffix}` };
}
